import React from 'react';
import { csvCell } from '../../lib/csvCell';
import { safePiiText } from '../../lib/piiDisplay';
import SearchableSelect from '../shared/SearchableSelect';
import { useBodyScrollLock } from '../../lib/useBodyScrollLock';
import { getActiveWorkspaceId, workspaceHeader } from '../../lib/workspaces';
import { db, auth, createAuditLog, handleFirestoreError, OperationType } from '../../lib/firebase';
import { collection, getDocs, getDoc, setDoc, updateDoc, doc, deleteDoc, addDoc, query, where, orderBy } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import MasterPipeline, { MASTER_STAGES } from '../MasterPipeline';
import { usePrograms } from '../../hooks/usePrograms';
import { getPipelineStages, stageLabel as getStageLabel } from '../../lib/programPipeline';
import { Application, Batch, BatchFolder, BatchLog, ServiceProvider, Event, FormBuilderField } from '../../types';
import FormBuilder from '../shared/FormBuilder';
import { notifyToast } from '../../lib/toastStore';
import { confirmDialog } from '../../lib/confirmStore';
import { extractBulkJobProgress, buildBulkJobTerminalToast } from '../../lib/bulkJobProgress';
import SenderDropdown from './SenderDropdown';
import DeliveryStatusBadge, { type DeliveryStatus } from './DeliveryStatusBadge';
import {
  X, Plus, Trash2, MapPin, Clock, Calendar, RefreshCw, Search, Filter, UserPlus,
  ChevronLeft, ChevronRight, Eye, Link, Upload, CheckCircle, CheckCircle2, XCircle, Check,
  MessageSquare, FileText, Send, Users, Download, ArrowLeft, Mail, Save,
  CalendarDays, AlertCircle, FileSignature, Loader, ChevronRight as ChevronRightIcon, Layers, Paperclip,
  Bold, Italic, Underline, List, AlignLeft, AlignCenter, Code, Edit3, AlertTriangle, DollarSign,
  GripVertical, ChevronDown, Bookmark, RotateCcw, UserCheck, Tag,
  Folder, FolderPlus, FolderOpen, Move, Sparkles,
} from 'lucide-react';
import TemplatePickerModal from './TemplatePickerModal';
import WorkspaceTeamAssignModal from './WorkspaceTeamAssignModal';
import UnifiedApplicantFilterBar from '../shared/UnifiedApplicantFilterBar';
import { ApplicantFilterState, DEFAULT_FILTER_STATE, filterApplicantRecord } from '../../lib/applicantFilterUtils';
import Modal from '../shared/Modal';
import {
  type BatchTableView,
  DEFAULT_TABLE_COLUMNS,
  loadBatchViews, persistBatchViews, loadActiveViewId, persistActiveViewId,
  makeViewId, moveColumnOrder, reconcileColumns,
} from './batchTableViews';
import { pageSelectionShare, togglePageSelection } from './batchSelection';
import { getPlacementSummary, getActivePrimaryPlacement } from '../../lib/placementUtils';

import MergeFieldPicker, { type MergeFieldCategory } from '../shared/MergeFieldPicker';
import { renderFormattedResponseValue, buildDynamicFormCsvRows } from '../../lib/responseFormatter';
import FormTemplatePickerBar from './FormTemplatePickerBar';
import ChangeCaseDropdown, { applyCaseToSelection } from '../shared/ChangeCaseDropdown';
import { insertTextToTextarea } from './MergeToken';
import { safeFetch, fetchWithRetry } from '../../lib/api';
import { resolveMergeFields } from '../../lib/variables/resolver';
import { AssignAutomationModal } from './workflow/AssignAutomationModal';
import { sendSystemNotification, buildEmailTemplate } from '../../lib/notifications';
import { isApplicationActive } from '../../lib/constants';
import { firstUsablePersonName } from '../../lib/nameUtils';

// PR-16 / D-27: batch surfaces built candidate names ONLY from
// personalDetails parts — records whose name lives in top-level
// firstName/lastName (Add-Candidate shape), or whose personalDetails
// arrive as encrypted PII blocks, rendered "[object Object]" and never
// matched searches. Resolve every batch name through the shared
// normalizer instead.
const batchDisplayName = (app: any): string => firstUsablePersonName([app], '');
// Shared email composer primitives — the single code path for template
// insertion, WYSIWYG/HTML editing, font size/colour and sanitization.
import { useComposerBody } from './email/useComposerBody';
import { ComposerBody } from './email/ComposerBody';
import { MERGE_FIELD_CATEGORIES } from './email/mergeFieldCategories';
import { sanitizeEmailHtml } from './email/emailSanitize';
import { BroadcastJobProgress } from './email/BroadcastJobProgress';
import {
  useEmailDraft,
  formatDraftTime,
  listCampaignDrafts,
  deleteEmailDraft,
  draftAuthorLabel,
  draftLastEditorLabel,
  type EmailDraftRecord,
} from './email/emailDrafts';
import EmailScheduleModal from './EmailScheduleModal';
import {
  broadcastEmail,
  sendIndividualEmail,
  newIdempotencyKey,
  type BroadcastRecipient,
  type AttachmentPayload,
} from './email/emailApi';
import DocumentTemplateMultiSelect from '../shared/DocumentTemplateMultiSelect';

interface BatchCenterProps {
  batches: Batch[];
  batchFolders?: BatchFolder[];
  currentFolderId?: string | null;
  setCurrentFolderId?: (v: string | null) => void;
  onFetchBatchFolders?: () => void;
  onCreateFolder?: (data: { name: string; description?: string; tags?: string[]; parentId?: string | null }) => Promise<any>;
  onUpdateFolder?: (id: string, updates: { name?: string; description?: string; tags?: string[]; parentId?: string | null }) => Promise<void>;
  onDeleteFolder?: (id: string) => Promise<void>;
  applications: Application[];
  /** False when the member has only view access to the Batch module — all
   *  mutation controls (create/delete/edit/bulk) are hidden. */
  canEditBatch?: boolean;
  isCreatingBatch: boolean;
  selectedBatchId: string | null;
  batchName: string;
  batchDescription: string;
  batchProgramTypes: string[];
  batchSearchQuery: string;
  batchSelectedApplicantIds: string[];
  bulkEmailSubject: string;
  bulkEmailContent: string;
  broadcastSubject: string;
  broadcastContent: string;
  broadcastAttachments: { name: string; base64Content: string; mimeType: string }[];
  broadcasting: boolean;
  broadcastProgress: { completed: number; total: number; totalRecipients: number } | null;
  broadcastSummary: { success: boolean; totalSent: number; totalChunks: number; failedChunks: number; failedRecipients: string[] } | null;
  bulkStatusValue: string;
  cohortSearch: string;
  cohortStatusFilter: string;
  cohortProgramFilter: string;
  batchLogs: BatchLog[];
  isLoadingLogs: boolean;
  newBatchLogText: string;
  isAddApplicantOpen: boolean;
  addApplicantSearch: string;
  batchNotes: string;
  isSavingNotes: boolean;
  adminEmail: string;
  programsList?: any[];
  appCtxProviders?: any[];
  setIsCreatingBatch: (v: boolean) => void;
  setSelectedBatchId: (v: string | null) => void;
  setBatchName: (v: string) => void;
  setBatchDescription: (v: string) => void;
  setBatchProgramTypes: (v: string[]) => void;
  setBatchSearchQuery: (v: string) => void;
  setBatchSelectedApplicantIds: (v: string[]) => void;
  setBulkEmailSubject: (v: string) => void;
  setBulkEmailContent: (v: string) => void;
  setBulkStatusValue: (v: string) => void;
  setCohortSearch: (v: string) => void;
  setCohortStatusFilter: (v: string) => void;
  setCohortProgramFilter: (v: string) => void;
  setBatchLogs: (v: BatchLog[]) => void;
  setIsLoadingLogs: (v: boolean) => void;
  setNewBatchLogText: (v: string) => void;
  setIsAddApplicantOpen: (v: boolean) => void;
  setAddApplicantSearch: (v: string) => void;
  setBatchNotes: (v: string) => void;
  setIsSavingNotes: (v: boolean) => void;
  // ADM-13: routes to the one Applicants workspace via AdminDashboard's
  // viewApplicantInWorkspace (origin: the batch surface).
  onViewApplicant: (v: Application) => void;
  onFetchBatches: () => void;
  onCreateBatch: (e: React.FormEvent) => void;
  onDeleteBatch: (id: string, name: string) => void;
  // The real helper (useBatches) takes an optional change detail and
  // resolves true when the update was written, false when it failed —
  // the bulk relocate/remove handlers rely on that result to report
  // honestly instead of claiming success over a failed write.
  onUpdateBatchMembers: (batchId: string, memberIds: string[], changeDetail?: string) => Promise<boolean>;
  onSaveBatchNotes: (batchId: string, notesText: string) => void;
  onUpdateBatchDetail: (batchId: string, updates: { name?: string; description?: string; programTypes?: string[]; status?: string; folderId?: string | null }) => void;
  onUpdateBatchTags: (batchId: string, tags: string[]) => void;
  onBulkStatusUpdate: () => void;
  onBulkPartnerUpdate?: (providerId: string, targetApplicationIds?: string[]) => void;
  onSendBulkEmail: (batchId: string) => void;
  onBroadcastEmail: () => void;
  setBroadcastSubject: (v: string) => void;
  setBroadcastContent: (v: string) => void;
  setBroadcastAttachments: (v: { name: string; base64Content: string; mimeType: string }[]) => void;
  setBroadcastSummary: (v: any) => void;
  onFetchBatchLogs: (batchId: string) => void;
  onAddBatchLog: () => void;
  onCreateAuditLog: (action: string, targetId: string, details: string) => void;
  onNavigateToApplicant?: (app: Application) => void;
  batchLogFile: File | null;
  batchLogFileProgress: number | null;
  isUploadingBatchLogFile: boolean;
  onBatchLogFileChange: (file: File | null) => void;
  selectedApp: Application | null;
  previewDoc?: any;
  setPreviewDoc?: (doc: any) => void;
  setBatchLogFile?: (f: File | null) => void;
  setBatchLogFileProgress?: (n: number | null) => void;
  setIsUploadingBatchLogFile?: (b: boolean) => void;
}

const MEMBERS_PER_PAGE = 25;

export default function BatchCenter(props: BatchCenterProps) {
  const {
    batches, applications, isCreatingBatch, selectedBatchId,
    batchName, batchDescription, batchProgramTypes, batchSearchQuery,
    batchSelectedApplicantIds, bulkEmailSubject, bulkEmailContent,
    broadcastSubject, broadcastContent, broadcastAttachments, broadcasting, broadcastProgress, broadcastSummary,
    broadcastSenderAddress, bulkStatusValue, cohortSearch, cohortStatusFilter, cohortProgramFilter,
    batchLogs, isLoadingLogs, newBatchLogText,
    isAddApplicantOpen, addApplicantSearch, batchNotes, isSavingNotes, adminEmail,
    setIsCreatingBatch, setSelectedBatchId, setBatchName, setBatchDescription,
    setBatchProgramTypes, setBatchSearchQuery, setBatchSelectedApplicantIds,
    setBulkEmailSubject, setBulkEmailContent,
    setBroadcastSubject, setBroadcastContent, setBroadcastAttachments, setBroadcastSummary, setBroadcastSenderAddress,
    setBulkStatusValue, setCohortSearch, setCohortStatusFilter, setCohortProgramFilter,
    setBatchLogs, setIsLoadingLogs, setNewBatchLogText,
    setIsAddApplicantOpen, setAddApplicantSearch, setBatchNotes, setIsSavingNotes,
    onViewApplicant,
    onNavigateToApplicant,
    batchLogFile, batchLogFileProgress, isUploadingBatchLogFile,
    onBatchLogFileChange: handleBatchLogFileChange,
    selectedApp, previewDoc, setPreviewDoc,
    setBatchLogFile, setBatchLogFileProgress, setIsUploadingBatchLogFile,
    onFetchBatches: fetchBatches,
    /** False when the member has only view access to the Batch module —
     *  create/delete/edit/bulk controls are hidden and server-enforced. */
    canEditBatch = true,
    onCreateBatch: handleCreateBatch,
    onDeleteBatch: handleDeleteBatch,
    onUpdateBatchMembers: handleUpdateBatchMembers,
    onSaveBatchNotes: handleSaveBatchNotes,
    onUpdateBatchDetail: handleUpdateBatchDetail,
    onUpdateBatchTags: handleUpdateBatchTags,
    onBulkStatusUpdate: handleBulkStatusUpdate,
    onBulkPartnerUpdate: handleBulkPartnerUpdate,
    onSendBulkEmail: handleSendBulkEmail,
    onBroadcastEmail: handleBroadcastEmail,
    onFetchBatchLogs: fetchBatchLogs,
    onAddBatchLog: handleAddBatchLog,
    onCreateAuditLog,
  } = props;

  // Folder state and navigation
  const batchFolders = props.batchFolders || [];
  const [localFolderId, setLocalFolderId] = React.useState<string | null>(null);
  const currentFolderId = props.currentFolderId !== undefined ? props.currentFolderId : localFolderId;
  const setCurrentFolderId = (id: string | null) => {
    if (props.setCurrentFolderId) props.setCurrentFolderId(id);
    else setLocalFolderId(id);
  };

  const [createBatchFolderInput, setCreateBatchFolderInput] = React.useState<string | null>(null);

  React.useEffect(() => {
    setCreateBatchFolderInput(currentFolderId);
  }, [currentFolderId, isCreatingBatch]);

  const [showFolderModal, setShowFolderModal] = React.useState(false);
  const [folderModalMode, setFolderModalMode] = React.useState<'create' | 'edit'>('create');
  const [editingFolderId, setEditingFolderId] = React.useState<string | null>(null);
  const [folderNameInput, setFolderNameInput] = React.useState('');
  const [folderDescInput, setFolderDescInput] = React.useState('');
  const [folderTagsInput, setFolderTagsInput] = React.useState('');
  const [folderParentInput, setFolderParentInput] = React.useState<string | null>(null);
  const [folderSubmitting, setFolderSubmitting] = React.useState(false);

  const [movingBatch, setMovingBatch] = React.useState<Batch | null>(null);
  const [moveTargetFolderId, setMoveTargetFolderId] = React.useState<string | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = React.useState<string | null>(null);

  // Helper: calculate folder depth (0 for root, 1 for top-level, max 10)
  const getFolderDepth = React.useCallback((fId: string | null | undefined): number => {
    if (!fId) return 0;
    let depth = 0;
    let curr: string | null | undefined = fId;
    const visited = new Set<string>();
    while (curr && depth < 20) {
      if (visited.has(curr)) break;
      visited.add(curr);
      depth++;
      const found = batchFolders.find(f => f.folderId === curr);
      if (!found) break;
      curr = found.parentId;
    }
    return depth;
  }, [batchFolders]);

  const currentFolderDepth = getFolderDepth(currentFolderId);

  // Helper: build breadcrumb trail
  const folderTrail = React.useMemo(() => {
    if (!currentFolderId) return [];
    const trail: BatchFolder[] = [];
    let curr: string | null | undefined = currentFolderId;
    const visited = new Set<string>();
    while (curr && trail.length < 20) {
      if (visited.has(curr)) break;
      visited.add(curr);
      const found = batchFolders.find(f => f.folderId === curr);
      if (!found) break;
      trail.unshift(found);
      curr = found.parentId;
    }
    return trail;
  }, [currentFolderId, batchFolders]);

  // Helper: build full path label for any folder (e.g. Main / Folder A / Folder B)
  const getFolderPathLabel = React.useCallback((fId: string | null | undefined): string => {
    if (!fId) return 'Main';
    const trail: string[] = [];
    let curr: string | null | undefined = fId;
    const visited = new Set<string>();
    while (curr && trail.length < 20) {
      if (visited.has(curr)) break;
      visited.add(curr);
      const found = batchFolders.find(f => f.folderId === curr);
      if (!found) break;
      trail.unshift(found.name);
      curr = found.parentId;
    }
    return ['Main', ...trail].join(' / ');
  }, [batchFolders]);

  // Folder actions
  const handleOpenCreateFolder = () => {
    if (currentFolderDepth >= 10) {
      notifyToast('warning', 'Maximum folder depth of 10 reached. Cannot create further subfolders inside this level.');
      return;
    }
    setFolderModalMode('create');
    setEditingFolderId(null);
    setFolderNameInput('');
    setFolderDescInput('');
    setFolderTagsInput('');
    setFolderParentInput(currentFolderId);
    setShowFolderModal(true);
  };

  const handleOpenEditFolder = (folder: BatchFolder) => {
    setFolderModalMode('edit');
    setEditingFolderId(folder.folderId);
    setFolderNameInput(folder.name);
    setFolderDescInput(folder.description || '');
    setFolderTagsInput((folder.tags || []).join(', '));
    setFolderParentInput(folder.parentId || null);
    setShowFolderModal(true);
  };

  const handleSaveFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!folderNameInput.trim()) {
      notifyToast('warning', 'Folder name is required.');
      return;
    }
    setFolderSubmitting(true);
    const tags = folderTagsInput.split(',').map(t => t.trim()).filter(Boolean);
    try {
      if (folderModalMode === 'create') {
        if (props.onCreateFolder) {
          await props.onCreateFolder({
            name: folderNameInput.trim(),
            description: folderDescInput.trim(),
            tags,
            parentId: folderParentInput || currentFolderId || null,
          });
        }
      } else if (editingFolderId && props.onUpdateFolder) {
        await props.onUpdateFolder(editingFolderId, {
          name: folderNameInput.trim(),
          description: folderDescInput.trim(),
          tags,
          parentId: folderParentInput || null,
        });
      }
      setShowFolderModal(false);
      if (props.onFetchBatchFolders) props.onFetchBatchFolders();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to save folder');
    } finally {
      setFolderSubmitting(false);
    }
  };

  const handleConfirmMoveBatch = async () => {
    if (!movingBatch) return;
    try {
      if (props.onUpdateBatchDetail) {
        await props.onUpdateBatchDetail(movingBatch.batchId, { folderId: moveTargetFolderId });
      }
      setMovingBatch(null);
      if (props.onFetchBatches) props.onFetchBatches();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to move batch');
    }
  };


  const [batchTab, setBatchTab] = React.useState<'applicants' | 'relatedEvents' | 'relatedSignRequests' | 'relatedEmailCampaigns' | 'notes' | 'bulkRequests'>('applicants');
  const [memberPage, setMemberPage] = React.useState(0);
  // Default stays the established 25 rows (MEMBERS_PER_PAGE); the
  // selector Jules added in #1333 raises it to 50/100/150/200 on
  // demand — its initial 50 silently changed the landing view and
  // broke the pinned page-scoped selection behavior.
  const [membersPerPage, setMembersPerPage] = React.useState<number>(MEMBERS_PER_PAGE);
  const [showScheduleModal, setShowScheduleModal] = React.useState(false);

  // ── Excel-like table state (used inside batch detail IIFE) ──
  const [memberSearch, setMemberSearch] = React.useState('');
  const [memberProgramFilter, setMemberProgramFilter] = React.useState('');
  const [batchFilters, setBatchFilters] = React.useState<ApplicantFilterState>(DEFAULT_FILTER_STATE);
  const [bulkFinanceModalOpen, setBulkFinanceModalOpen] = React.useState(false);
  const [bulkFinanceType, setBulkFinanceType] = React.useState<'invoice' | 'quote'>('invoice');
  const [bulkPartnerValue, setBulkPartnerValue] = React.useState('');
  // Column set AND order (Excel-style). The saved-view helpers live in
  // batchTableViews.ts so this component stays about rendering.
  // Views are read on mount BEFORE the column state so a saved layout comes
  // back on reload — otherwise the toolbar would name a view that isn't
  // actually applied.
  const [savedViews, setSavedViews] = React.useState<BatchTableView[]>(() => loadBatchViews());
  const [activeViewId, setActiveViewId] = React.useState<string | null>(() => loadActiveViewId());
  const [visibleColumns, setVisibleColumns] = React.useState<string[]>(() => {
    const view = loadBatchViews().find(v => v.id === loadActiveViewId());
    return view && view.columns.length ? [...view.columns] : [...DEFAULT_TABLE_COLUMNS];
  });
  const [showViewsMenu, setShowViewsMenu] = React.useState(false);
  const [newViewName, setNewViewName] = React.useState('');
  const [draggingColumnId, setDraggingColumnId] = React.useState<string | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = React.useState<string | null>(null);
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>({});
  const resizingColumnRef = React.useRef<{ id: string; startX: number; startWidth: number } | null>(null);

  // Reset custom column widths back to regular defaults when component unmounts or leaving view
  React.useEffect(() => {
    return () => {
      setColumnWidths({});
    };
  }, [batchTab, selectedBatchId]);

  const handleResizeMouseDown = (e: React.MouseEvent, colId: string, currentWidth: number) => {
    e.stopPropagation();
    e.preventDefault();
    resizingColumnRef.current = { id: colId, startX: e.clientX, startWidth: currentWidth || 160 };

    const onMouseMove = (moveEvent: MouseEvent) => {
      if (!resizingColumnRef.current) return;
      const diff = moveEvent.clientX - resizingColumnRef.current.startX;
      const newWidth = Math.max(80, resizingColumnRef.current.startWidth + diff);
      setColumnWidths(prev => ({ ...prev, [colId]: newWidth }));
    };

    const onMouseUp = () => {
      resizingColumnRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };
  const [selectedMemberIds, setSelectedMemberIds] = React.useState<string[]>([]);
  const [showBulkInviteModal, setShowBulkInviteModal] = React.useState(false);
  const [showAssignAdminModal, setShowAssignAdminModal] = React.useState(false);
  const [showBulkTagModal, setShowBulkTagModal] = React.useState(false);
  const [showRelocateModal, setShowRelocateModal] = React.useState(false);
  const [relocateTargetBatchId, setRelocateTargetBatchId] = React.useState('');
  const [isRelocating, setIsRelocating] = React.useState(false);
  const [showBulkRemoveModal, setShowBulkRemoveModal] = React.useState(false);
  const [isBulkRemoving, setIsBulkRemoving] = React.useState(false);
  // PR #1333 review fix (D-1): the relocate/remove modals render at
  // THIS level of the component, but Jules's versions read
  // `selectedBatch` — a const block-scoped inside the batch-detail
  // IIFE further down — so opening either modal threw
  // "ReferenceError: selectedBatch is not defined" and the error
  // boundary took the whole Batch Center down. The modals use this
  // component-level derivation of the open batch instead.
  const openBatch = batches.find(b => b.batchId === selectedBatchId);
  // Public Batch ID first, then the name — the one label format for
  // every batch dropdown (D-5): "BAT-10122 - Hospitality Nurturing".
  const batchOptionLabel = (b: any): string => {
    const displayId = b?.batchId?.startsWith('BAT-')
      ? b.batchId
      : b?.numericId
        ? `BAT-${b.numericId}`
        : b?.batchId || b?.id || '—';
    const rawName = typeof b?.name === 'string' ? b.name : '';
    return rawName && !rawName.startsWith('BAT-') ? `${displayId} - ${rawName}` : displayId;
  };
  const [showAiScreeningModal, setShowAiScreeningModal] = React.useState(false);
  const [showScreeningInviteModal, setShowScreeningInviteModal] = React.useState(false);
  const [showAssignAutomationModal, setShowAssignAutomationModal] = React.useState(false);
  const [showBulkVariableModal, setShowBulkVariableModal] = React.useState(false);
  const [targetBulkVariableId, setTargetBulkVariableId] = React.useState('');
  const [isBulkUpdatingVariable, setIsBulkUpdatingVariable] = React.useState(false);
  const [bulkVariableResult, setBulkVariableResult] = React.useState<any | null>(null);
  const [aiScreeningType, setAiScreeningType] = React.useState<'document_review' | 'program_eligibility' | 'job_matching' | 'partner_suggestions'>('program_eligibility');
  const [bulkTagInput, setBulkTagInput] = React.useState('');
  const [bulkTagMode, setBulkTagMode] = React.useState<'append' | 'replace'>('append');
  const [isBulkTagging, setIsBulkTagging] = React.useState(false);

  const [groupEventsList, setGroupEventsList] = React.useState<any[]>([]);
  const [selectedTargetEventId, setSelectedTargetEventId] = React.useState('');
  const [isBulkInviting, setIsBulkInviting] = React.useState(false);
  const [bulkInviteResult, setBulkInviteResult] = React.useState<any | null>(null);

  const fetchWorkspaceGroupEvents = React.useCallback(async () => {
    try {
      const activeWorkspaceId = getActiveWorkspaceId();
      const snap = await getDocs(query(collection(db, 'events')));
      const list: any[] = [];
      snap.forEach(d => {
        const ev = d.data();
        if (ev.isGroupEvent && ev.workspaceId === activeWorkspaceId) {
          list.push({ eventId: d.id, ...ev });
        }
      });
      setGroupEventsList(list);
    } catch (err) {
      console.error('Failed to load group events:', err);
    }
  }, []);

  React.useEffect(() => {
    if (showBulkInviteModal) {
      fetchWorkspaceGroupEvents();
      setSelectedTargetEventId('');
      setBulkInviteResult(null);
    }
  }, [showBulkInviteModal, fetchWorkspaceGroupEvents]);

  const [showColumnPicker, setShowColumnPicker] = React.useState(false);
  const [columnSearch, setColumnSearch] = React.useState('');
  const [showMemberFilters, setShowMemberFilters] = React.useState(false);
  const [isEditingBatch, setIsEditingBatch] = React.useState(false);
  const [editBatchName, setEditBatchName] = React.useState('');
  const [editBatchDesc, setEditBatchDesc] = React.useState('');
  const [editBatchProgramTypes, setEditBatchProgramTypes] = React.useState<string[]>([]);
  const [editBatchStatus, setEditBatchStatus] = React.useState('Draft');
  const [editBatchFolderId, setEditBatchFolderId] = React.useState<string | null>(null);
  const [batchTagInput, setBatchTagInput] = React.useState('');
  const [cohortTagFilter, setCohortTagFilter] = React.useState('All');
  const allBatchTags = React.useMemo(() => {
    const tags = new Set<string>();
    batches.forEach(b => b.tags?.forEach(t => tags.add(t)));
    return Array.from(tags).sort();
  }, [batches]);
  const BATCH_TABS = [
    { id: 'applicants' as const, label: 'Applicants', icon: Users },
    { id: 'relatedEvents' as const, label: 'Events', icon: Calendar },
    { id: 'relatedSignRequests' as const, label: 'Sign Requests', icon: FileSignature },
    { id: 'bulkRequests' as const, label: 'Bulk Requests', icon: FileText },
    { id: 'relatedEmailCampaigns' as const, label: 'Email Campaigns', icon: Mail },
    { id: 'notes' as const, label: 'Notes & Activity', icon: MessageSquare },
  ];

  return (
    /* VIEW 1.5: BATCH PROCESSING & OPERATIONS (HIGH-END COHORT SYSTEM) */
    <div className="space-y-8 animate-fade-in text-slate-800">
    {/* CENTRALIZED BATCHES HEADER */}
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-200 pb-4">
    <div>
    <h1 className="text-xl font-extrabold font-display tracking-tight text-slate-900">Batch Processing & Operations</h1>
    </div>
    {canEditBatch && (
    <button
    type="button"
    onClick={() => {
    setSelectedBatchId(null);
    setIsCreatingBatch(!isCreatingBatch);
    setBatchName('');
    setBatchDescription('');
    setBatchSelectedApplicantIds([]);
    }}
    className="px-4 py-2.5 text-xs font-bold btn-primary  text-white rounded-xl transition-all shadow-sm flex items-center space-x-1.5 cursor-pointer shrink-0"
    >
    <Plus className="h-4 w-4" />
    <span>{isCreatingBatch ? "View All Batches" : "Create New Batch"}</span>
    </button>
    )}
    </div>

    {isCreatingBatch ? (
    /* CREATE BATCH VIEW */
    <div className="bg-white border border-slate-200/80 p-6 rounded-xl shadow-sm max-w-2xl mx-auto space-y-4">
    <h3 className="text-xs uppercase tracking-widest text-slate-500 font-bold">Create Cohort Batch</h3>
    <form onSubmit={(e) => handleCreateBatch(e, createBatchFolderInput)} className="space-y-4 text-xs">
    <div>
    <label className="text-xs font-bold text-slate-500 block mb-1">Batch Name *</label>
    <input 
    type="search"
    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-brand focus:bg-white"
    placeholder="e.g. Summer Camp Counselors (Group B)"
    value={batchName}
    onChange={(e) => setBatchName(e.target.value)}
    />
    </div>
    <div>
    <label className="text-xs font-bold text-slate-500 block mb-1">Description / Memo</label>
    <textarea 
    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-brand focus:bg-white min-h-[80px]"
    placeholder="Processing notes..."
    value={batchDescription}
    onChange={(e) => setBatchDescription(e.target.value)}
    />
    </div>
    <div>
    <label className="text-xs font-bold text-slate-500 block mb-1">Folder Location</label>
    <SearchableSelect
      value={createBatchFolderInput || ''}
      onChange={v => setCreateBatchFolderInput(v || null)}
      options={batchFolders.filter(f => getFolderDepth(f.folderId) < 10).map(f => ({
        value: f.folderId,
        label: getFolderPathLabel(f.folderId),
      }))}
      placeholder="Search folders…"
      clearLabel="Main (Top Level)"
      emptyLabel="No folders found"
    />
    </div>

    <div>
    <label className="text-xs font-bold text-slate-500 block mb-1">Cohort Program Filter (Multi-Select)</label>
        <div className="flex flex-wrap gap-2 pt-1">
    {(props.programsList || []).filter((p: any) => p.active !== false).map((p: any) => {
    const id = p.id || p.programId || p.name;
    const label = p.name || id;
    const isSelected = batchProgramTypes.includes(id);
    return (
    <button
    type="button"
    key={id}
    onClick={() => {
    if (isSelected) {
    if (batchProgramTypes.length > 1) {
    setBatchProgramTypes(batchProgramTypes.filter(t => t !== id));
    } else {
    notifyToast('warning', "At least one program type must be selected.");
    }
    } else {
    setBatchProgramTypes([...batchProgramTypes, id]);
    }
    }}
    className={`px-3 py-1.5 rounded-xl text-caption font-bold border transition-all cursor-pointer ${
    isSelected
    ? 'btn-primary text-white border-brand shadow-sm'
    : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
    }`}
    >
    {label} {isSelected ? '✓' : '+'}
    </button>
    );
    })}
    </div>
    </div>

    <div className="pt-4 border-t border-slate-100 flex items-center justify-end space-x-2">
    <button
    type="button"
    onClick={() => setIsCreatingBatch(false)}
    className="px-4 py-2 border border-slate-200 rounded-lg text-xs font-bold hover:bg-slate-50 text-slate-700"
    >
    Cancel
    </button>
    <button
    type="submit"
    className="px-4 py-2 btn-primary text-white rounded-lg text-xs font-bold "
    >
    Create Batch
    </button>
    </div>
    </form>
    </div>
            ) : !selectedBatchId ? (
    /* BATCH LIST & FOLDER MANAGEMENT VIEW */
    <div className="space-y-6 max-w-6xl mx-auto animate-fadeIn">
    {/* FOLDER BREADCRUMBS & NAVIGATION BAR */}
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3 text-xs shadow-3xs">
      <div className="flex items-center flex-wrap gap-1.5 font-bold text-slate-700">
        <button
          type="button"
          onClick={() => setCurrentFolderId(null)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors cursor-pointer ${
            currentFolderId === null ? 'bg-brand text-white font-extrabold shadow-2xs' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
          }`}
        >
          <FolderOpen className="h-3.5 w-3.5" />
          <span>Main (All Cohorts)</span>
        </button>

        {folderTrail.map((folder, idx) => {
          const isLast = idx === folderTrail.length - 1;
          return (
            <React.Fragment key={folder.folderId}>
              <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <button
                type="button"
                onClick={() => setCurrentFolderId(folder.folderId)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-colors cursor-pointer ${
                  isLast ? 'bg-brand text-white font-extrabold shadow-2xs' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Folder className="h-3.5 w-3.5" />
                <span>{folder.name}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>

      <div className="flex items-center gap-3">
        <span className="text-[10px] font-bold font-mono text-slate-400 uppercase tracking-wider bg-white border border-slate-200 px-2 py-1 rounded-md">
          Folder Depth: {currentFolderDepth} / 10
        </span>
        {canEditBatch && (
          <button
            type="button"
            onClick={handleOpenCreateFolder}
            disabled={currentFolderDepth >= 10}
            className="px-3.5 py-1.5 bg-brand hover:bg-brand-active disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg font-bold text-caption transition flex items-center gap-1.5 shadow-3xs cursor-pointer"
            title={currentFolderDepth >= 10 ? 'Maximum folder depth of 10 reached' : 'Create a new subfolder in this folder'}
          >
            <FolderPlus className="h-3.5 w-3.5" />
            <span>New Folder</span>
          </button>
        )}
      </div>
    </div>

    {/* ADVANCED COHORT SEARCH & FILTERS */}
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-3xs">
    <div className="relative flex-1 max-w-md">
    <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
    <input
    type="search"
    className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-9 pr-4 py-2 text-xs outline-none focus:border-brand focus:bg-white transition-all text-slate-800 font-medium"
    placeholder="Search cohorts or folders by name, description or tags..."
    value={cohortSearch}
    onChange={(e) => setCohortSearch(e.target.value)}
    />
    {cohortSearch && (
    <button 
    type="button" 
    onClick={() => setCohortSearch('')}
    className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 font-extrabold text-xs"
    >
    ✕
    </button>
    )}
    </div>

    <div className="flex flex-wrap items-center gap-3 text-xs">
    <div className="flex items-center space-x-2">
    <span className="text-caption font-bold text-slate-500 tracking-wider">Status:</span>
    <select
    className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-blue-550 font-semibold text-slate-700"
    value={cohortStatusFilter}
    onChange={(e) => setCohortStatusFilter(e.target.value)}
    >
    <option value="All">All Statuses</option>
    <option value="Draft">Draft</option>
    <option value="Processing">Processing</option>
    <option value="Completed">Completed</option>
    </select>
    </div>

    <div className="flex items-center space-x-2">
    <span className="text-caption font-bold text-slate-500 tracking-wider">Program:</span>
    <div className="w-52">
    <SearchableSelect
    value={cohortProgramFilter === 'All' ? '' : cohortProgramFilter}
    onChange={(v) => setCohortProgramFilter(v || 'All')}
    options={(props.programsList || []).filter((p: any) => p.active !== false).map((p: any) => ({ value: p.name || p.id, label: p.name || p.id }))}
    placeholder="Search programs…"
    clearLabel="All Programs"
    emptyLabel="No programs found"
    />
    </div>
    </div>

    <div className="flex items-center space-x-2">
    <span className="text-caption font-bold text-slate-500 tracking-wider">Tag:</span>
    <select
    className="bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none focus:border-blue-550 font-semibold text-slate-700"
    value={cohortTagFilter}
    onChange={(e) => setCohortTagFilter(e.target.value)}
    >
    <option value="All">All Tags</option>
    {allBatchTags.map(tag => (
      <option key={tag} value={tag}>{tag}</option>
    ))}
    </select>
    </div>
    </div>
    </div>

    {(() => {
      const q = cohortSearch.trim().toLowerCase();
      // Filter folders
      const matchingFolders = batchFolders.filter(f => {
        if (q) {
          const mName = f.name.toLowerCase().includes(q);
          const mDesc = (f.description || '').toLowerCase().includes(q);
          const mTags = f.tags?.some(t => t.toLowerCase().includes(q));
          return mName || mDesc || mTags;
        }
        return (f.parentId || null) === (currentFolderId || null);
      });

      // Filter batches
      const filteredBatches = batches.filter(bat => {
        if (!q && (bat.folderId || null) !== (currentFolderId || null)) return false;
        if (q) {
          const mId = (bat.batchId || `BAT-${bat.numericId || ''}`).toLowerCase().includes(q);
          const mName = bat.name.toLowerCase().includes(q);
          const mDesc = (bat.description || '').toLowerCase().includes(q);
          const mTags = (bat.tags || []).some(t => t.toLowerCase().includes(q));
          if (!mId && !mName && !mDesc && !mTags) return false;
        }
        const matchesStatus = cohortStatusFilter === 'All' || bat.status === cohortStatusFilter;
        let matchesProgram = true;
        if (cohortProgramFilter !== 'All') {
          const lowerFilter = cohortProgramFilter.toLowerCase();
          const programStr = (bat.programType || '').toLowerCase();
          const hasInStr = programStr.includes(lowerFilter);
          const hasInArray = bat.programTypes?.some(t => t.toLowerCase() === lowerFilter);
          matchesProgram = hasInStr || !!hasInArray;
        }
        const matchesTag = cohortTagFilter === 'All' || (bat.tags || []).includes(cohortTagFilter);
        return matchesStatus && matchesProgram && matchesTag;
      });

      const hasNoItems = batches.length === 0 && batchFolders.length === 0;
      const hasNoFilteredItems = matchingFolders.length === 0 && filteredBatches.length === 0;

      if (hasNoItems) {
        return (
          <div className="bg-white border border-slate-200 p-12 rounded-xl text-center text-slate-400 space-y-3">
            <Users className="h-8 w-8 mx-auto text-slate-300" />
            <p className="text-sm font-medium">No Cohort Batches or Folders available.</p>
          </div>
        );
      }

      if (hasNoFilteredItems) {
        return (
          <div className="bg-white border border-slate-200 p-12 rounded-xl text-center text-slate-400 space-y-2">
            <Search className="h-8 w-8 mx-auto text-slate-300" />
            <p className="text-sm font-medium">No matching folders or cohorts found in this location.</p>
            <p className="text-xs text-slate-500">Try revising your text query, program filter, or status filter.</p>
          </div>
        );
      }

      return (
        <div className="space-y-6">
          {/* FOLDERS GRID SECTION */}
          {matchingFolders.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-caption uppercase font-extrabold tracking-wider text-slate-400 flex items-center gap-1.5">
                  <Folder className="h-3.5 w-3.5 text-amber-500 fill-amber-100" />
                  <span>Folders ({matchingFolders.length})</span>
                </h4>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {matchingFolders.map(folder => {
                  const childFoldersCount = batchFolders.filter(f => f.parentId === folder.folderId).length;
                  const childBatchesCount = batches.filter(b => b.folderId === folder.folderId).length;
                  const isDragOver = dragOverFolderId === folder.folderId;

                  return (
                    <div
                      key={folder.folderId}
                      onClick={() => { setCohortSearch(''); setCurrentFolderId(folder.folderId); }}
                      onDragOver={e => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                        if (dragOverFolderId !== folder.folderId) setDragOverFolderId(folder.folderId);
                      }}
                      onDragLeave={e => {
                        e.preventDefault();
                        if (dragOverFolderId === folder.folderId) setDragOverFolderId(null);
                      }}
                      onDrop={async e => {
                        e.preventDefault();
                        setDragOverFolderId(null);
                        const batchId = e.dataTransfer.getData('text/plain');
                        if (!batchId) return;
                        try {
                          if (props.onUpdateBatchDetail) {
                            await props.onUpdateBatchDetail(batchId, { folderId: folder.folderId });
                          }
                          if (props.onFetchBatches) props.onFetchBatches();
                        } catch (err: any) {
                          notifyToast('error', err.message || 'Failed to move batch to folder');
                        }
                      }}
                      className={`bg-white border ${isDragOver ? 'border-brand ring-2 ring-brand/30 bg-blue-50/20' : 'border-slate-200 hover:border-brand'} rounded-xl p-4 cursor-pointer transition-all hover:shadow-xs group flex flex-col justify-between`}
                    >
                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <Folder className="h-4 w-4 text-amber-500 fill-amber-100 shrink-0 group-hover:scale-110 transition-transform" />
                            <h5 className="text-xs font-bold text-slate-900 group-hover:text-brand truncate" title={folder.name}>
                              {folder.name}
                            </h5>
                          </div>
                          {canEditBatch && (
                            <div className="flex items-center gap-1 opacity-80 group-hover:opacity-100 shrink-0" onClick={e => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => handleOpenEditFolder(folder)}
                                className="p-1 hover:bg-slate-100 text-slate-500 rounded text-[10px] font-bold cursor-pointer"
                                title="Edit folder details"
                              >
                                <Edit3 className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteFolder(folder.folderId)}
                                className="p-1 hover:bg-rose-50 text-rose-600 rounded text-[10px] font-bold cursor-pointer"
                                title="Delete folder"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </div>

                        {folder.description && (
                          <p className="text-[11px] text-slate-500 line-clamp-2 leading-relaxed">{folder.description}</p>
                        )}

                        {folder.tags && folder.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {folder.tags.map(t => (
                              <span key={t} className="px-1.5 py-0.5 text-[11px] font-bold bg-amber-50 text-amber-800 border border-amber-200/60 rounded">
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="pt-2.5 border-t border-slate-100 mt-3 flex items-center justify-between text-[11px] text-slate-500 font-medium">
                        <span className="font-mono">{childFoldersCount} subfolder{childFoldersCount === 1 ? '' : 's'} • {childBatchesCount} batch{childBatchesCount === 1 ? '' : 'es'}</span>
                        <span className="text-brand font-bold group-hover:translate-x-0.5 transition-transform flex items-center gap-1">
                          Drill In →
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* BATCHES GRID SECTION */}
          {filteredBatches.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-caption uppercase font-extrabold tracking-wider text-slate-400 flex items-center gap-1.5">
                  <Users className="h-3.5 w-3.5 text-brand" />
                  <span>Cohorts ({filteredBatches.length})</span>
                </h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredBatches.map(bat => {
                  const enrolledCount = applications.filter(a => bat.applicantIds?.includes(a.applicationId) && isApplicationActive(a)).length;
                  const folderPathLabel = getFolderPathLabel(bat.folderId);

                  return (
                    <div
                      key={bat.batchId}
                      draggable={canEditBatch}
                      onDragStart={e => {
                        e.dataTransfer.setData('text/plain', bat.batchId);
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onClick={() => {
                        if (bat.folderId !== undefined) {
                          setCurrentFolderId(bat.folderId || null);
                        }
                        setSelectedBatchId(bat.batchId);
                        setBatchNotes(bat.notes || '');
                        setBulkEmailSubject('');
                        setBulkEmailContent('');
                        setBulkStatusValue('');
                        setIsAddApplicantOpen(false);
                        setAddApplicantSearch('');
                      }}
                      className="bg-white border border-slate-200 hover:border-slate-300 rounded-xl p-5 cursor-pointer transition-all hover:shadow-sm hover:-translate-y-1 flex flex-col justify-between min-h-[190px] group"
                    >
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="space-y-0.5 min-w-0">
                            <span className="text-caption uppercase font-bold text-slate-400 tracking-wider block truncate">
                              {bat.programType || (bat.programTypes?.[0] || '')}
                            </span>
                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded font-mono">
                              <Folder className="h-2.5 w-2.5 text-amber-500 fill-amber-100" />
                              <span className="truncate max-w-[140px]">{folderPathLabel}</span>
                            </span>
                          </div>
                          <span className={`px-2 py-0.5 text-caption uppercase font-bold rounded-lg shrink-0 ${
                            bat.status === 'Completed' ? 'bg-emerald-50 text-emerald-700 border border-emerald-150' :
                            bat.status === 'Processing' ? 'bg-brand-active border border-blue-150' :
                            'bg-slate-100 text-slate-600 border border-slate-200'
                          }`}>
                            {bat.status}
                          </span>
                        </div>

                        {(() => {
                          const displayBatchId = bat.batchId?.startsWith('BAT-') ? bat.batchId : ((bat as any).numericId ? `BAT-${(bat as any).numericId}` : (bat.batchId || '—'));
                          const displayBatchTitle = bat.name?.startsWith('BAT-') ? bat.name : `${displayBatchId} - ${bat.name}`;
                          return (
                            <h4 className="text-xs font-extrabold text-slate-900 group-hover:text-brand transition-colors line-clamp-1">{displayBatchTitle}</h4>
                          );
                        })()}
                        <p className="text-caption text-slate-400 line-clamp-2 leading-relaxed">{bat.description || "No description loaded."}</p>

                        {(bat.tags && bat.tags.length > 0) && (
                          <div className="flex flex-wrap gap-1 pt-1.5">
                            {bat.tags.map(tag => (
                              <span key={tag} className="px-1.5 py-0.5 text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-100 rounded-md">{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t border-slate-100 mt-4">
                        <span className="bg-slate-100 text-slate-700 px-2.5 py-1 rounded-lg font-mono font-extrabold text-caption">
                          {enrolledCount} {enrolledCount === 1 ? 'member' : 'members'}
                        </span>

                        <div className="flex items-center space-x-2">
                          {canEditBatch && (
                            <>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMovingBatch(bat);
                                  setMoveTargetFolderId(bat.folderId || null);
                                }}
                                className="p-1 text-slate-400 hover:text-brand hover:bg-slate-100 rounded-md transition-colors"
                                title="Move batch to folder"
                              >
                                <Move className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (await confirmDialog(`Are you sure you want to delete cohort "${bat.name}"? This cannot be undone.`)) {
                                    handleDeleteBatch(bat.batchId, bat.name);
                                  }
                                }}
                                className="px-2 py-1 text-caption font-bold text-rose-600 hover:bg-rose-50 rounded-lg cursor-pointer transition-colors"
                              >
                                Delete
                              </button>
                            </>
                          )}
                          <span className="text-brand font-bold text-caption group-hover:translate-x-1 transition-transform inline-flex items-center">
                            Open →
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    })()}
    </div>
) : (
    /* ONLY ACTIVE BATCH DETAIL SCREEN */
    (() => {
    const selectedBatch = batches.find(b => b.batchId === selectedBatchId);
    if (!selectedBatch) {
    return (
    <div className="bg-white border border-slate-200 p-6 rounded-xl text-slate-400 text-center max-w-lg mx-auto">
    <p className="text-xs font-bold">Cohorts details are not accessible or have been removed.</p>
    <button 
    type="button" 
    onClick={() => setSelectedBatchId(null)}
    className="mt-3 px-3 py-1.5 bg-brand hover:bg-brand-active text-white rounded-lg text-xs font-bold"
    >
    Return to Directory
    </button>
    </div>
    );
    }

    // Get only currently enrolled members (exclude archived/inactive)
    const batchMembers = applications.filter(app =>
      selectedBatch.applicantIds.includes(app.applicationId) &&
      isApplicationActive(app)
    );

    // Find candidate list that are NOT currently in the batch (exclude archived/inactive)
    const availableApplicants = applications.filter(app => {
      if (!isApplicationActive(app)) return false;
      return !selectedBatch.applicantIds.includes(app.applicationId);
    });

    // ── Excel-like table state ──
    // Cell helpers. Labels are written the way an admissions officer says them
    // out loud ("Passport Number", not "passportNumber"), and every renderer
    // reads the REAL Application shape (personalDetails / passportImmigration /
    // visaDetails / placements) instead of legacy flat aliases — that mismatch
    // is why several columns used to be permanently blank "—".
    const COLUMN_EMPTY = <span className="text-slate-300">—</span>;
    const person = (m: any) => m.personalDetails || {};
    const passportInfo = (m: any) => m.passportImmigration || {};
    const visaInfo = (m: any) => m.visaDetails || {};
    const prefs = (m: any) => m.programPreferences || {};

    const textCell = (value?: string | number | null) => {
      // A still-encrypted PII block must never render as a React child
      // (error #31): coerce through safePiiText so envelopes read empty.
      const text = safePiiText(value, '');
      return text.trim() === '' ? COLUMN_EMPTY : <span>{text}</span>;
    };

    const dateCell = (value?: string | null) => {
      if (!value) return COLUMN_EMPTY;
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return COLUMN_EMPTY;
      return <span className="text-caption text-slate-600">{parsed.toLocaleDateString()}</span>;
    };

    const STATUS_TONES: Record<string, string> = {
      approved: 'bg-green-100 text-green-800',
      placed: 'bg-emerald-100 text-emerald-800',
      paid: 'bg-green-100 text-green-800',
      pending: 'bg-amber-100 text-amber-800',
      'pending approval': 'bg-amber-100 text-amber-800',
      'pending placement': 'bg-amber-100 text-amber-800',
      overdue: 'bg-red-100 text-red-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-slate-100 text-slate-500',
      unplaced: 'bg-slate-100 text-slate-500',
    };

    const pillCell = (value?: string | null) => {
      if (!value) return COLUMN_EMPTY;
      const tone = STATUS_TONES[String(value).toLowerCase()] || 'bg-slate-100 text-slate-700';
      return <span className={`px-2 py-0.5 text-caption font-bold rounded-lg ${tone}`}>{value}</span>;
    };

    const ageFromDob = (dob?: string) => {
      if (!dob) return null;
      const birth = new Date(dob);
      if (Number.isNaN(birth.getTime())) return null;
      const today = new Date();
      let years = today.getFullYear() - birth.getFullYear();
      const monthDelta = today.getMonth() - birth.getMonth();
      if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birth.getDate())) years -= 1;
      return years >= 0 && years < 130 ? years : null;
    };

    const programName = (m: any) => {
      const found = (props.programsList || []).find((x: any) => x.id === m.programId || x.programId === m.programId);
      return found?.name || m.programType || m.programId || '';
    };

    // Flag passports that are expired or inside the 6-month validity window most
    // visa rules require, so the column is actionable rather than just a date.
    const passportExpiryCell = (m: any) => {
      const raw = passportInfo(m).expirationDate;
      if (!raw) return COLUMN_EMPTY;
      const expiry = new Date(raw);
      if (Number.isNaN(expiry.getTime())) return COLUMN_EMPTY;
      const monthsLeft = (expiry.getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 30.44);
      const tone = monthsLeft < 0
        ? 'bg-red-100 text-red-800'
        : monthsLeft < 6
          ? 'bg-amber-100 text-amber-800'
          : 'bg-slate-100 text-slate-700';
      return (
        <span
          className={`px-2 py-0.5 text-caption font-bold rounded-lg ${tone}`}
          title={monthsLeft < 0
            ? 'Passport has expired'
            : monthsLeft < 6
              ? 'Expires within 6 months — most visa rules require 6+ months validity'
              : undefined}
        >
          {expiry.toLocaleDateString()}
        </span>
      );
    };

    const ALL_COLUMNS: { id: string; label: string; render: (app: typeof batchMembers[0]) => React.ReactNode }[] = [
      // ── Identity ──
      { id: 'applicantId', label: 'Application ID', render: (m: any) => (
        <button
          type="button"
          onClick={() => onViewApplicant(m)}
          className="font-mono font-bold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
          title="Click to view profile"
        >
          #{m.applicationId}
        </button>
      ) },
      { id: 'firstName', label: 'First Name', render: (m: any) => (
        person(m).firstName ? (
          <button
            type="button"
            onClick={() => onViewApplicant(m)}
            className="font-semibold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
            title="Click to view profile"
          >
            {person(m).firstName}
          </button>
        ) : COLUMN_EMPTY
      ) },
      { id: 'middleName', label: 'Middle Name', render: (m: any) => textCell(person(m).middleName) },
      { id: 'lastName', label: 'Last Name', render: (m: any) => (
        person(m).lastName ? (
          <button
            type="button"
            onClick={() => onViewApplicant(m)}
            className="font-semibold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left"
            title="Click to view profile"
          >
            {person(m).lastName}
          </button>
        ) : COLUMN_EMPTY
      ) },
      { id: 'fullName', label: 'Full Name', render: (m: any) => {
        const n = `${person(m).firstName || ''} ${person(m).lastName || ''}`.trim() || m.userEmail;
        return (
          <button
            type="button"
            onClick={() => onViewApplicant(m)}
            className="text-left cursor-pointer group"
            title="Click to view profile"
          >
            <div className="font-extrabold text-blue-600 group-hover:text-blue-800 group-hover:underline">{n}</div>
            <div className="text-caption text-slate-400 font-medium">{m.userEmail}</div>
          </button>
        );
      }},
      { id: 'passportName', label: 'Name on Passport', render: (m: any) => textCell(person(m).passportName || person(m).fullNameLegal) },
      { id: 'preferredName', label: 'Preferred Name', render: (m: any) => textCell(person(m).preferredName) },
      { id: 'age', label: 'Age', render: (m: any) => {
        const years = ageFromDob(person(m).dob);
        return years == null ? COLUMN_EMPTY : <span className="font-bold text-slate-700">{years}</span>;
      }},
      { id: 'dob', label: 'Date of Birth', render: (m: any) => dateCell(person(m).dob) },
      { id: 'gender', label: 'Gender', render: (m: any) => textCell(person(m).gender) },
      { id: 'maritalStatus', label: 'Marital Status', render: (m: any) => textCell(person(m).maritalStatus) },
      { id: 'citizenship', label: 'Country of Citizenship', render: (m: any) => textCell(person(m).countryOfCitizenship) },
      { id: 'countryOfBirth', label: 'Country of Birth', render: (m: any) => textCell(person(m).countryOfBirth) },
      { id: 'cityOfBirth', label: 'City of Birth', render: (m: any) => textCell(person(m).cityOfBirth) },

      // ── Contact ──
      { id: 'email', label: 'Email', render: (m: any) => (
        m.userEmail ? (
          <button
            type="button"
            onClick={() => onViewApplicant(m)}
            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left truncate max-w-[180px]"
            title="Click to view profile"
          >
            {m.userEmail}
          </button>
        ) : COLUMN_EMPTY
      ) },
      { id: 'phone', label: 'Phone', render: (m: any) => textCell(person(m).phone || person(m).phoneNumber || m.phoneNumber) },
      { id: 'usPhone', label: 'US Phone', render: (m: any) => textCell(person(m).usPhone) },
      { id: 'address', label: 'Street Address', render: (m: any) => textCell([safePiiText(person(m).address?.street, ''), safePiiText(person(m).address?.apartment, '')].filter(Boolean).join(', ')) },
      { id: 'city', label: 'City', render: (m: any) => textCell(person(m).address?.city) },
      { id: 'stateRegion', label: 'State / Region', render: (m: any) => textCell(person(m).address?.state) },
      { id: 'postalCode', label: 'Postal Code', render: (m: any) => textCell(person(m).address?.zip) },
      { id: 'country', label: 'Country', render: (m: any) => textCell(person(m).address?.country || person(m).countryOfCitizenship) },
      { id: 'emergency', label: 'Emergency Contact', render: (m: any) => {
        const ec = m.emergencyContacts?.[0];
        if (!ec) return COLUMN_EMPTY;
        return (
          <div>
            <div className="font-bold text-slate-700">{safePiiText(ec.fullName, '') || safePiiText(ec.name, '') || '—'}</div>
            {ec.relationship && <div className="text-caption text-slate-400">{ec.relationship}</div>}
          </div>
        );
      }},
      { id: 'emergencyPhone', label: 'Emergency Phone', render: (m: any) => textCell(m.emergencyContacts?.[0]?.phone) },

      // ── Program & pipeline ──
      { id: 'status', label: 'Pipeline Status', render: (m: any) => {
        const matchedProg = (props.programsList || []).find((x: any) => x.id === m.programId || x.programId === m.programId || x.name === m.programType || x.code === m.programType);
        const stages = getPipelineStages(matchedProg);
        const stgLabel = getStageLabel(stages, m.masterStage, m.status || '—');
        return <span className="px-2 py-0.5 text-caption font-bold rounded-lg bg-slate-100 text-slate-800 font-mono">{stgLabel}</span>;
      }},
      { id: 'program', label: 'Program', render: (m: any) => <span>{programName(m) || '—'}</span> },
      { id: 'programTerm', label: 'Program Term', render: (m: any) => textCell(m.programTerm || prefs(m).programTerm) },
      { id: 'preferredState', label: 'Preferred State', render: (m: any) => textCell(prefs(m).preferredState) },
      { id: 'preferredIndustry', label: 'Preferred Industry', render: (m: any) => textCell(prefs(m).preferredIndustry) },
      { id: 'englishProficiency', label: 'English Proficiency', render: (m: any) => textCell(prefs(m).englishProficiency) },
      { id: 'placementStatus', label: 'Placement Status', render: (m: any) => {
        // Shared helper (src/lib/placementUtils.ts) — the same derivation the
        // dashboard placement filter uses, legacy records included.
        const summary = getPlacementSummary(m as any);
        if (!summary?.hasPlacements && !m.placementStatus) return COLUMN_EMPTY;
        return pillCell(summary?.currentStatus || m.placementStatus);
      }},
      // ── Passport & immigration ──
      { id: 'passportNumber', label: 'Passport Number', render: (m: any) => <span className="font-mono font-bold text-slate-700">{passportInfo(m).passportNumber || '—'}</span> },
      { id: 'passportCountry', label: 'Passport Country', render: (m: any) => textCell(passportInfo(m).passportCountry) },
      { id: 'passportIssueDate', label: 'Passport Issue Date', render: (m: any) => dateCell(passportInfo(m).issueDate) },
      { id: 'passportExpiry', label: 'Passport Expiry', render: (m: any) => passportExpiryCell(m) },
      { id: 'visaStatus', label: 'Visa Status', render: (m: any) => pillCell(visaInfo(m).visaApprovalStatus) },
      { id: 'visaType', label: 'Visa Type', render: (m: any) => textCell(visaInfo(m).visaType) },
      { id: 'visaNumber', label: 'Visa Number', render: (m: any) => textCell(visaInfo(m).visaNumber) },
      { id: 'visaApplicationNumber', label: 'Visa Application No.', render: (m: any) => textCell(visaInfo(m).visaApplicationNumber) },
      { id: 'visaExpiry', label: 'Visa Expiry', render: (m: any) => dateCell(visaInfo(m).expiryDate) },
      { id: 'ds160Number', label: 'DS-160 Number', render: (m: any) => textCell(visaInfo(m).ds160ConfirmationNumber || m.ds160Details?.ds160ConfirmationNumber || m.ds160Details?.ds160Number) },
      { id: 'sevisId', label: 'SEVIS ID', render: (m: any) => textCell(m.sevisId || passportInfo(m).sevisId) },
      { id: 'ds2019Number', label: 'DS-2019 Number', render: (m: any) => textCell(m.ds2019Number || passportInfo(m).ds2019Number) },
      { id: 'embassyAppointment', label: 'Embassy Appointment', render: (m: any) => dateCell(m.embassyDetails?.appointmentDate || visaInfo(m).embassyAppointmentDate) },
      { id: 'embassyLocation', label: 'Embassy Location', render: (m: any) => textCell(m.embassyDetails?.embassyLocation || visaInfo(m).embassyLocation) },
      { id: 'intendedArrival', label: 'Intended Arrival', render: (m: any) => dateCell(passportInfo(m).intendedArrival) },
      { id: 'usDestination', label: 'US Destination', render: (m: any) => textCell(passportInfo(m).usDestination) },

      // ── Placement & operations ──
      { id: 'employer', label: 'Employer', render: (m: any) => {
        const primary = getActivePrimaryPlacement(m?.placements);
        return textCell(primary?.companyName || m.assignment?.employerName);
      }},
      { id: 'jobTitle', label: 'Job Title', render: (m: any) => {
        const primary = getActivePrimaryPlacement(m?.placements);
        return textCell(primary?.jobTitle || m.assignment?.roleTitle);
      }},
      { id: 'placementStart', label: 'Placement Start Date', render: (m: any) => dateCell(getActivePrimaryPlacement(m?.placements)?.startDate) },
      { id: 'placementLocation', label: 'Placement Location', render: (m: any) => {
        const primary = getActivePrimaryPlacement(m?.placements);
        return textCell([primary?.city, primary?.state, primary?.country].filter(Boolean).join(', '));
      }},
      { id: 'supervisor', label: 'Supervisor', render: (m: any) => {
        const primary = getActivePrimaryPlacement(m?.placements);
        return textCell(primary?.supervisorName || primary?.contactName);
      }},
      { id: 'partner', label: 'Partner', render: (m: any) => {
        const provider = (props.appCtxProviders || []).find((pr: any) => pr.providerId === (m as any).assignedPartner);
        return <span>{provider?.name || (m as any).assignedPartner || '—'}</span>;
      }},
      { id: 'sponsor', label: 'Sponsor', render: (m: any) => textCell(m.sponsorName || m.serviceProvider?.name) },
      { id: 'financeStatus', label: 'Finance Status', render: (m: any) => pillCell(m.financialStatus) },
      { id: 'verified', label: 'Identity Verified', render: (m: any) => (m.applicantVerifiedAt
        ? <span className="rounded-lg bg-emerald-100 px-2 py-0.5 text-caption font-bold text-emerald-800" title={new Date(m.applicantVerifiedAt).toLocaleString()}>Verified</span>
        : COLUMN_EMPTY) },
      { id: 'tags', label: 'Tags', render: (m: any) => (Array.isArray(m.tags) && m.tags.length > 0
        ? <div className="flex flex-wrap gap-1">{m.tags.map((t: string) => <span key={t} className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold text-slate-600">{t}</span>)}</div>
        : COLUMN_EMPTY) },
      { id: 'dateApplied', label: 'Date Applied', render: (m: any) => dateCell(m.createdAt) },
      { id: 'lastUpdated', label: 'Last Updated', render: (m: any) => dateCell(m.updatedAt) },
    ];

    // Filter members
    const filteredMembers = batchMembers.filter(m => {
      const sq = memberSearch.toLowerCase();
      if (sq) {
        const n = batchDisplayName(m).toLowerCase();
        const e = (m.userEmail || '').toLowerCase();
        const id = (m.applicationId || '').toLowerCase();
        if (!n.includes(sq) && !e.includes(sq) && !id.includes(sq)) return false;
      }
      if (memberProgramFilter) {
        const resolvedName = ((() => { const px = (props.programsList || []).find((x: any) => x.id === m.programId || x.programId === m.programId); return px?.name || m.programType; })() || '').toLowerCase();
        if (!resolvedName.includes(memberProgramFilter.toLowerCase())) return false;
      }
      return filterApplicantRecord(m, batchFilters, {
        includeMasterPhase: true,
        batchesList: batches,
        programsList: props.programsList,
      });
    });

    // Get unique program options from the canonical programs list
    const memberProgramIds = [...new Set(batchMembers.map(m => m.programId || m.programType || '').filter(Boolean))];
    const memberPrograms = memberProgramIds.map(id => {
      const found = (props.programsList || []).find((p: any) => p.id === id || p.programId === id);
      return found ? found.name : id;
    });

    // The bulk-actions column is hidden until rows are selected, so the
    // spreadsheet owns the full width by default.
    const hasSelection = selectedMemberIds.length > 0;

    // Columns in the admin's chosen order (Excel-style). Computed inline rather
    // than memoised: the `render` closures capture current props (programs,
    // providers), so they must not be frozen in a stale memo.
    const orderedVisibleColumns = (() => {
      const byId = new Map(ALL_COLUMNS.map(c => [c.id, c]));
      return visibleColumns.map(id => byId.get(id)).filter(Boolean) as typeof ALL_COLUMNS;
    })();
    const hiddenColumns = ALL_COLUMNS.filter(c => !visibleColumns.includes(c.id));
    // With ~60 available fields the picker needs a filter, not just a scroll.
    const columnQuery = columnSearch.trim().toLowerCase();
    const pickerColumns = [...orderedVisibleColumns, ...hiddenColumns].filter(col =>
      !columnQuery
      || col.label.toLowerCase().includes(columnQuery)
      || col.id.toLowerCase().includes(columnQuery),
    );
    const activeView = savedViews.find(v => v.id === activeViewId) || null;
    const viewIsDirty = !!activeView && activeView.columns.join('|') !== visibleColumns.join('|');

    const toggleColumn = (id: string) => {
      setVisibleColumns(prev => {
        if (prev.includes(id)) {
          // Never let an admin hide every column — an empty grid looks broken.
          return prev.length <= 1 ? prev : prev.filter(c => c !== id);
        }
        return [...prev, id];
      });
    };

    const handleColumnDrop = (targetId: string) => {
      const fromId = draggingColumnId;
      setDraggingColumnId(null);
      setDragOverColumnId(null);
      if (!fromId || fromId === targetId) return;
      setVisibleColumns(prev => moveColumnOrder(prev, fromId, targetId));
    };

    const applyView = (view: BatchTableView) => {
      setVisibleColumns(reconcileColumns(view.columns, ALL_COLUMNS.map(c => c.id)));
      setActiveViewId(view.id);
      persistActiveViewId(view.id);
      setShowViewsMenu(false);
    };

    const saveViewAs = (name: string) => {
      const trimmed = name.trim();
      if (!trimmed || visibleColumns.length === 0) return;
      const now = new Date().toISOString();
      const view: BatchTableView = {
        id: makeViewId(), name: trimmed, columns: [...visibleColumns], createdAt: now, updatedAt: now,
      };
      const next = [...savedViews, view];
      setSavedViews(next);
      persistBatchViews(next);
      setActiveViewId(view.id);
      persistActiveViewId(view.id);
      setNewViewName('');
    };

    const updateActiveView = () => {
      if (!activeView) return;
      const next = savedViews.map(v => v.id === activeView.id
        ? { ...v, columns: [...visibleColumns], updatedAt: new Date().toISOString() }
        : v);
      setSavedViews(next);
      persistBatchViews(next);
    };

    const deleteView = async (id: string) => {
      const view = savedViews.find(v => v.id === id);
      if (!(await confirmDialog(`Delete the saved view "${view?.name || 'this view'}"? This cannot be undone.`, { confirmLabel: 'Delete' }))) return;
      const next = savedViews.filter(v => v.id !== id);
      setSavedViews(next);
      persistBatchViews(next);
      if (activeViewId === id) {
        setActiveViewId(null);
        persistActiveViewId(null);
      }
    };

    const resetColumns = () => {
      setVisibleColumns([...DEFAULT_TABLE_COLUMNS]);
      setActiveViewId(null);
      persistActiveViewId(null);
    };

    // Handler for copying rows (selected rows if any selected, otherwise all visible rows in current view)
    const copySelectedMembers = () => {
      const visibleCols = orderedVisibleColumns;
      const rows = selectedMemberIds.length > 0
        ? filteredMembers.filter(m => selectedMemberIds.includes(m.applicationId))
        : filteredMembers;
      if (rows.length === 0) return;
      const header = visibleCols.map(c => c.label).join('\t');
      const data = rows.map(m => visibleCols.map(c => {
        const el = c.render(m);
        if (typeof el === 'string') return el;
        if (typeof el === 'number' || typeof el === 'boolean') return String(el);
        if (React.isValidElement(el)) {
          const text = (el.props as any)?.children;
          if (typeof text === 'string') return text;
          if (Array.isArray(text)) return text.filter((t: any) => typeof t === 'string').join(' ');
        }
        return '';
      }).join('\t'));
      navigator.clipboard.writeText([header, ...data].join('\n'));
      notifyToast('success', `Copied ${rows.length} row${rows.length === 1 ? '' : 's'} (${visibleCols.length} column${visibleCols.length === 1 ? '' : 's'}) to clipboard`);
    };

    // Compute batch master stage from enrolled members (most common stage)
    const batchMasterStage = batchMembers.length > 0
      ? Math.round(batchMembers.reduce((sum, m) => sum + ((m as any).masterStage ?? 0), 0) / batchMembers.length)
      : (selectedBatch.status === 'Completed' ? 9 : selectedBatch.status === 'Draft' ? 0 : 3);

    // Filter available candidates by query
    const filteredAvailable = availableApplicants.filter(app => {
    const fName = batchDisplayName(app).toLowerCase();
    const uEmail = (app.userEmail || '').toLowerCase();
    const sQuery = addApplicantSearch.toLowerCase();
    const appId = String(app.applicationId || (app as any).id || '').toLowerCase();
    return fName.includes(sQuery) || uEmail.includes(sQuery) || appId.includes(sQuery);
    });

    // Pagination
    const pageSize = membersPerPage || MEMBERS_PER_PAGE;
    const totalPages = Math.ceil(filteredMembers.length / pageSize) || 1;
    const safePage = Math.min(memberPage, totalPages - 1);
    const pageMembers = filteredMembers.slice(safePage * pageSize, (safePage + 1) * pageSize);

    // Page-scoped selection (owner rule): the header checkbox and the
    // toolbar page toggle act ONLY on the rows of the current page.
    // They used to select/clear the ENTIRE filtered dataset
    // (filteredMembers), so one click silently selected records on
    // pages the admin had never seen, and clearing wiped selections
    // built up elsewhere. Selections now accumulate across pages and
    // survive filter/sort/page changes untouched.
    const pageMemberIds = pageMembers.map(m => m.applicationId);
    const memberPageShare = pageSelectionShare(pageMemberIds, selectedMemberIds);
    const toggleMemberPageSelection = () =>
      setSelectedMemberIds(prev => togglePageSelection(pageMemberIds, prev));

    return (
    // The cohort spreadsheet is the one screen where horizontal room is the
    // point, so the detail view expands on the applicants tab (the bulk column
    // is hidden until rows are selected). Other tabs keep the narrower canvas.
    <div className={`space-y-6 mx-auto text-slate-800 ${batchTab === 'applicants' ? 'max-w-[1700px]' : 'max-w-6xl'}`}>

    {/* BATCH DETAIL TOP FOCUS NAV BAR */}
    <div className="bg-white border border-slate-200 p-5 rounded-xl shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
    <div className="flex items-center space-x-3">
    <button
    type="button"
    onClick={() => {
    setSelectedBatchId(null);
    setIsAddApplicantOpen(false);
    setAddApplicantSearch('');
    setIsEditingBatch(false);
    }}
    className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-all cursor-pointer inline-flex items-center font-bold"
    title="Back to Cohorts"
    >
    <ArrowLeft className="h-4 w-4" />
    </button>
    {isEditingBatch ? (
      <div className="flex-1 space-y-2 min-w-0">
        <input type="text" value={editBatchName} onChange={e => setEditBatchName(e.target.value)}
          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-sm font-bold outline-none " />
        <input type="text" value={editBatchDesc} onChange={e => setEditBatchDesc(e.target.value)}
          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs outline-none " placeholder="Description" />
        <div className="flex flex-wrap gap-1.5 pt-1">
          {(props.programsList || []).filter((p: any) => p.active !== false).map((p: any) => {
            const pt = p.name || p.id;
            const isSel = editBatchProgramTypes.includes(pt);
            return (
              <button key={p.id} type="button" onClick={() => {
                setEditBatchProgramTypes(prev => isSel ? prev.filter(t => t !== pt) : [...prev, pt]);
              }}
                className={`px-2 py-0.5 rounded-lg text-[10px] font-bold border cursor-pointer ${isSel ? 'btn-primary text-white border-brand' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                {pt}
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Status</label>
            <select value={editBatchStatus} onChange={e => setEditBatchStatus(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-lg px-2 py-1 text-caption font-bold outline-none">
              <option value="Draft">Draft</option>
              <option value="Processing">Processing</option>
              <option value="Completed">Completed</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Folder Location</label>
            <SearchableSelect
              value={editBatchFolderId || ''}
              onChange={v => setEditBatchFolderId(v || null)}
              options={batchFolders.filter(f => getFolderDepth(f.folderId) < 10).map(f => ({
                value: f.folderId,
                label: getFolderPathLabel(f.folderId),
              }))}
              placeholder="Search folders…"
              clearLabel="Main (Top Level)"
              emptyLabel="No folders found"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 pt-2">
          <button type="button" onClick={async () => {
            await handleUpdateBatchDetail(selectedBatch.batchId, { name: editBatchName, description: editBatchDesc, programTypes: editBatchProgramTypes, status: editBatchStatus, folderId: editBatchFolderId });
            setIsEditingBatch(false);
          }} className="px-3 py-1 btn-primary text-white text-caption font-bold rounded-lg cursor-pointer">Save</button>
          <button type="button" onClick={() => setIsEditingBatch(false)} className="px-3 py-1 border border-slate-200 text-caption font-bold rounded-lg hover:bg-slate-50 cursor-pointer">Cancel</button>
        </div>
      </div>
    ) : (
    <div>
    <div className="flex items-center space-x-2">
    <span className="text-caption uppercase font-extrabold tracking-wider text-brand bg-blue-50 px-2.5 py-0.5 rounded-lg border border-blue-100">
    Active Cohort Explorer
    </span>
    <span className={`px-2 py-0.5 text-caption font-extrabold rounded-lg whitespace-nowrap ${
    selectedBatch.status === 'Completed' ? 'bg-emerald-50 text-emerald-700 border border-emerald-150' :
    'bg-brand-active border border-blue-150'
    }`}>
    {MASTER_STAGES[batchMasterStage]?.label || selectedBatch.status}
    </span>
    </div>
    {(() => {
      const selDisplayId = selectedBatch.batchId?.startsWith('BAT-') ? selectedBatch.batchId : ((selectedBatch as any).numericId ? `BAT-${(selectedBatch as any).numericId}` : (selectedBatch.batchId || '—'));
      const selDisplayTitle = selectedBatch.name?.startsWith('BAT-') ? selectedBatch.name : `${selDisplayId} - ${selectedBatch.name}`;
      return <h3 className="text-sm font-extrabold font-display text-slate-900 mt-1">{selDisplayTitle}</h3>;
    })()}
    <p className="text-caption text-slate-400 mt-0.5">{selectedBatch.description || "No manual summary memo loaded."}</p>
    {/* Tags display + add */}
    <div className="flex flex-wrap items-center gap-1.5 mt-2">
      {(selectedBatch.tags || []).map(tag => (
        <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold bg-purple-50 text-purple-700 border border-purple-100 rounded-md">
          {tag}
          {canEditBatch && (
          <button onClick={() => {
            const updated = (selectedBatch.tags || []).filter(t => t !== tag);
            handleUpdateBatchTags(selectedBatch.batchId, updated);
          }} className="hover:text-red-500 cursor-pointer" title="Remove tag">✕</button>
          )}
        </span>
      ))}
      {!isEditingBatch && canEditBatch && (
        <form onSubmit={e => {
          e.preventDefault();
          const tag = batchTagInput.trim();
          if (!tag) return;
          const current = selectedBatch.tags || [];
          if (current.includes(tag)) { setBatchTagInput(''); return; }
          handleUpdateBatchTags(selectedBatch.batchId, [...current, tag]);
          setBatchTagInput('');
        }} className="inline-flex items-center">
          <input type="text" value={batchTagInput}
            onChange={e => setBatchTagInput(e.target.value)}
            placeholder="+ tag"
            className="w-16 text-[10px] bg-transparent border-b border-dashed border-slate-300 focus:border-purple-400 outline-none text-slate-500 placeholder-slate-300"
          />
        </form>
      )}
    </div>
    </div>
    )}
    </div>

    <div className="flex items-center space-x-2.5 shrink-0 self-end sm:self-center">
    {!isEditingBatch && canEditBatch && (
      <button type="button" onClick={() => {
        setEditBatchName(selectedBatch.name);
        setEditBatchDesc(selectedBatch.description || '');
        setEditBatchProgramTypes(selectedBatch.programTypes || (selectedBatch.programType ? [selectedBatch.programType] : []));
        setEditBatchStatus(selectedBatch.status || 'Draft');
        setEditBatchFolderId(selectedBatch.folderId || null);
        setIsEditingBatch(true);
      }} className="px-2.5 py-1.5 text-caption font-bold text-slate-500 hover:text-brand bg-brand-active rounded-lg border border-slate-200 hover:border-blue-200 transition-all cursor-pointer">
        Edit Batch
      </button>
    )}
    <span className="text-caption font-mono uppercase bg-slate-100 px-3 py-1 rounded-lg text-slate-700 font-bold border border-slate-200">
    {(() => {
    const pt = selectedBatch.programType || (selectedBatch.programTypes?.[0] || '');
    if (!pt) return '—';
    const found = (props.programsList || []).find((p: any) => p.id === pt || p.programId === pt || p.name === pt);
    return found?.name || pt;
    })()} Group
    </span>
    <span className="text-caption font-mono bg-blue-50 text-blue-800 px-3 py-1 rounded-lg font-bold border border-blue-100">
    {batchMembers.length} Enlisted
    </span>
    </div>
    </div>

    {/* ── SUB-NAVIGATION TABS ── */}
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <nav className="flex overflow-x-auto">
        {BATCH_TABS.map(tb => {
          const TabIcon = tb.icon;
          const isActive = batchTab === tb.id;
          return (
            <button
              key={tb.id}
              onClick={() => setBatchTab(tb.id)}
              className={`flex items-center space-x-2 px-5 py-3.5 text-xs font-bold transition-all cursor-pointer whitespace-nowrap relative ${
                isActive
                  ? 'text-brand bg-blue-50/60'
                  : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <TabIcon className={`h-4 w-4 ${isActive ? 'text-brand' : 'text-slate-400'}`} />
              <span>{tb.label}</span>
              {isActive && <span className="absolute bottom-0 left-0 right-0 h-0.5 btn-primary rounded-full" />}
            </button>
          );
        })}
      </nav>
    </div>

    {/* ── TAB CONTENT ── */}

    {batchTab === 'applicants' && (
      <div className="space-y-6">
        {/* INLINE EXPANDABLE ADD APPLICANT MODULE */}
        {isAddApplicantOpen && (
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4 animate-slide-down">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="flex items-center justify-between w-full">
  <h5 className="text-xs font-black text-slate-900">Enroll Candidates Into Cohort</h5>
  <button type="button" onClick={() => setIsAddApplicantOpen(false)} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg">&times;</button>
</div>
                <p className="text-caption text-slate-400 font-semibold">Search for active applicants to manually assign them to this cohort.</p>
              </div>
            </div>

            <div className="relative">
              <Search className="absolute left-3.5 top-3 h-4 w-4 text-slate-400" />
              <input
                type="search"
                placeholder="Search candidates by name, email or applicant ID..."
                className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-xs outline-none text-slate-800 font-semibold focus:ring-2 focus:ring-brand-primary/20 focus:border-blue-400 transition-all"
                value={addApplicantSearch}
                onChange={(e) => setAddApplicantSearch(e.target.value)}
              />
            </div>

            {filteredAvailable.length === 0 ? (
              <p className="text-slate-400 text-xs italic text-center py-6 bg-white border border-dashed border-slate-200 rounded-xl">
                {availableApplicants.length === 0
                  ? `No other applicants under ${selectedBatch.programType || ''} program exist.`
                  : "No candidates match your search."}
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-56 overflow-y-auto pr-1">
                {filteredAvailable.map(app => {
                  const cName = batchDisplayName(app) || app.userEmail;
                  return (
                    <div
                      key={app.applicationId}
                      className="bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between text-xs hover:shadow-xs hover:border-slate-300 transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center font-black text-slate-600">
                          {cName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <span className="font-extrabold text-slate-800 block">{cName}</span>
                          <span className="text-caption text-slate-400 font-mono truncate block max-w-[180px]">{app.userEmail}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const updatedList = [...selectedBatch.applicantIds, app.applicationId];
                          const currentName = batchDisplayName(app) || app.userEmail;
                          handleUpdateBatchMembers(selectedBatch.batchId, updatedList, `Added member: ${currentName} (${app.applicationId})`);
                          onCreateAuditLog('APPLICANT_PLACED_IN_BATCH', app.applicationId, `Applicant placed in batch: ${selectedBatch.name}`);
                        }}
                        className="px-3 py-1.5 btn-primary text-white rounded-lg text-[10px] font-black cursor-pointer"
                      >
                        + Enroll
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Desktop Split SaaS Spreadsheet Layout.
            The bulk-actions column only exists while rows are selected — by
            default the spreadsheet spans the full grid width. */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

          {/* Left Column: Spreadsheet Database Table — full width until a selection opens bulk actions */}
          <div className={`${hasSelection ? 'lg:col-span-8' : 'lg:col-span-12'} space-y-6`}>
            <div className="bg-white border border-slate-200 rounded-3xl shadow-xs overflow-hidden flex flex-col">

              {/* Spreadsheet Control Header Bar */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 border-b border-slate-100 bg-white">
                <div className="space-y-1">
                  <h4 className="text-xs font-black tracking-wider text-slate-950 uppercase flex items-center gap-2">
                    <span className="h-2.5 w-2.5 bg-brand rounded-full animate-pulse" />
                    Cohort Database Rows
                  </h4>
                  <p className="text-[11px] text-slate-500 font-semibold">
                    Viewing {filteredMembers.length} of {batchMembers.length} active records in grid
                    {!hasSelection && ' · tick rows to open bulk actions'}
                  </p>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {/* Search box */}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400" />
                    <input
                      type="search"
                      placeholder="Search spreadsheet..."
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      className="w-40 sm:w-48 bg-slate-50 border border-slate-200 rounded-xl pl-8 pr-3 py-1.5 text-xs outline-none text-slate-700 font-medium focus:bg-white focus:ring-1 focus:ring-brand-primary/10 focus:border-blue-400 transition-all"
                    />
                  </div>

                  {/* Enroll action */}
                  <button
                    type="button"
                    onClick={() => setIsAddApplicantOpen(!isAddApplicantOpen)}
                    className={`px-3 py-1.5 text-xs font-bold rounded-xl border transition-all cursor-pointer flex items-center gap-1.5 ${
                      isAddApplicantOpen
                        ? 'bg-slate-100 text-slate-700 border-slate-200'
                        : 'btn-primary text-white border-brand shadow-xs'
                    }`}
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    <span>{isAddApplicantOpen ? 'Cancel' : 'Enroll'}</span>
                  </button>

                  {/* Filter toggle */}
                  <button
                    type="button"
                    onClick={() => setShowMemberFilters(!showMemberFilters)}
                    className={`p-2 rounded-xl border transition-all cursor-pointer ${
                      showMemberFilters
                        ? 'bg-blue-50 text-brand border-blue-200'
                        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                    }`}
                    title="Filter Program rows"
                  >
                    <Filter className="h-3.5 w-3.5" />
                  </button>

                  {/* Saved Views — name + reuse a column layout */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowViewsMenu(!showViewsMenu)}
                      className={`px-3 py-1.5 rounded-xl border text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5 ${
                        showViewsMenu || activeView
                          ? 'bg-blue-50 text-brand border-blue-200'
                          : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                      title="Saved table views"
                    >
                      <Bookmark className="h-3.5 w-3.5" />
                      <span className="max-w-[110px] truncate">{activeView ? activeView.name : 'Views'}</span>
                      {viewIsDirty && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" title="Layout changed since this view was saved" />}
                      <ChevronDown className="h-3 w-3" />
                    </button>
                    {showViewsMenu && (
                      <div className="absolute right-0 top-10 z-[8500] w-72 rounded-2xl border border-slate-200 bg-white p-4 shadow-xl animate-fade-in">
                        <p className="mb-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">Saved Views</p>
                        {savedViews.length === 0 ? (
                          <p className="mb-3 text-[11px] font-semibold text-slate-400">
                            No saved views yet. Arrange or reorder columns, then name the layout below.
                          </p>
                        ) : (
                          <div className="mb-3 max-h-48 space-y-1 overflow-y-auto pr-1">
                            {savedViews.map(v => (
                              <div key={v.id} className={`flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 ${v.id === activeViewId ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
                                <button type="button" onClick={() => applyView(v)} className="min-w-0 flex-1 text-left cursor-pointer">
                                  <span className="block truncate text-xs font-bold text-slate-700">{v.name}</span>
                                  <span className="block text-[10px] font-semibold text-slate-400">{v.columns.length} columns</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteView(v.id)}
                                  className="shrink-0 rounded-md p-1 text-slate-300 transition-colors hover:text-rose-500 cursor-pointer"
                                  title={`Delete view "${v.name}"`}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {activeView && viewIsDirty && (
                          <button
                            type="button"
                            onClick={updateActiveView}
                            className="mb-2 w-full rounded-xl bg-brand px-3 py-1.5 text-[11px] font-black text-white transition-all hover:bg-brand-active cursor-pointer"
                          >
                            Update “{activeView.name}” with current layout
                          </button>
                        )}
                        <div className="flex items-center gap-1.5 border-t border-slate-100 pt-3">
                          <input
                            type="text"
                            value={newViewName}
                            onChange={(e) => setNewViewName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveViewAs(newViewName); }}
                            placeholder="Name this view..."
                            className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold outline-none focus:border-blue-400"
                          />
                          <button
                            type="button"
                            onClick={() => saveViewAs(newViewName)}
                            disabled={!newViewName.trim() || visibleColumns.length === 0}
                            className="btn-primary shrink-0 rounded-lg px-3 py-1.5 text-[11px] font-black text-white transition-all disabled:bg-slate-300 cursor-pointer disabled:cursor-not-allowed"
                          >
                            Save
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={resetColumns}
                          className="mt-2 flex items-center gap-1 text-[10px] font-bold text-slate-400 transition-colors hover:text-slate-600 cursor-pointer"
                        >
                          <RotateCcw className="h-3 w-3" /> Reset to default columns
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Column Picker */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowColumnPicker(!showColumnPicker)}
                      className={`p-2 rounded-xl border transition-all cursor-pointer ${
                        showColumnPicker
                          ? 'bg-blue-50 text-brand border-blue-200'
                          : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'
                      }`}
                      title="Spreadsheet columns"
                    >
                      <Eye className="h-3.5 w-3.5" />
                    </button>
                    {showColumnPicker && (
                      <div className="absolute right-0 top-10 z-[8500] bg-white border border-slate-200 rounded-2xl shadow-xl p-4 w-64 animate-fade-in" onMouseLeave={() => { if (!draggingColumnId) setShowColumnPicker(false); }}>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">Spreadsheet Fields</p>
                        <p className="mb-2 text-[10px] font-semibold text-slate-400">{visibleColumns.length} of {ALL_COLUMNS.length} shown</p>
                        <input
                          type="text"
                          value={columnSearch}
                          onChange={(e) => setColumnSearch(e.target.value)}
                          placeholder="Find a column..."
                          className="mb-2 w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold outline-none focus:border-blue-400"
                        />
                        <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                          {pickerColumns.length === 0 && (
                            <p className="px-2 py-3 text-center text-[11px] font-semibold text-slate-400">No columns match “{columnSearch}”.</p>
                          )}
                          {pickerColumns.map(col => {
                            const isOn = visibleColumns.includes(col.id);
                            return (
                              <div
                                key={col.id}
                                draggable={isOn}
                                onDragStart={() => setDraggingColumnId(col.id)}
                                onDragOver={(e) => { if (isOn) { e.preventDefault(); if (draggingColumnId !== col.id) setDragOverColumnId(col.id); } }}
                                onDragLeave={() => setDragOverColumnId(prev => (prev === col.id ? null : prev))}
                                onDrop={(e) => { e.preventDefault(); handleColumnDrop(col.id); }}
                                onDragEnd={() => { setDraggingColumnId(null); setDragOverColumnId(null); }}
                                className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-bold text-slate-600 transition font-sans ${
                                  dragOverColumnId === col.id && draggingColumnId !== col.id ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-slate-50'
                                } ${draggingColumnId === col.id ? 'opacity-40' : ''}`}
                              >
                                <span
                                  className={`shrink-0 ${isOn ? 'cursor-grab text-slate-300 hover:text-slate-500' : 'text-slate-200'}`}
                                  title={isOn ? 'Drag to reorder' : undefined}
                                >
                                  <GripVertical className="h-3.5 w-3.5" />
                                </span>
                                <input
                                  type="checkbox"
                                  checked={isOn}
                                  onChange={() => toggleColumn(col.id)}
                                  disabled={isOn && visibleColumns.length <= 1}
                                  className="rounded border-slate-300 text-brand focus:ring-brand-primary/20 disabled:opacity-40"
                                />
                                <span className="truncate">{col.label}</span>
                              </div>
                            );
                          })}
                        </div>
                        <p className="mt-2 border-t border-slate-100 pt-2 text-[10px] font-semibold leading-relaxed text-slate-400">
                          Drag the handles to reorder columns (or drag the table headers), then save the layout as a named view.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Copy option */}
                  <button
                    type="button"
                    onClick={copySelectedMembers}
                    className="px-3 py-1.5 bg-brand-primary hover:bg-brand-primary-dark text-white text-xs font-semibold rounded-xl transition-all cursor-pointer flex items-center gap-1 shadow-xs"
                    title={selectedMemberIds.length > 0 ? "Copy selected rows as TSV" : "Copy current view rows as TSV"}
                  >
                    <Check className="h-3 w-3" />
                    <span>Copy {selectedMemberIds.length > 0 ? `(${selectedMemberIds.length})` : `(${filteredMembers.length})`}</span>
                  </button>

                  {/* Bulk Actions when selected */}
                  {selectedMemberIds.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setRelocateTargetBatchId('');
                          setShowRelocateModal(true);
                        }}
                        className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-black rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shadow-xs"
                        title="Bulk Relocate selected candidates to another Batch"
                      >
                        <Move className="h-3.5 w-3.5" />
                        <span>Relocate ({selectedMemberIds.length})</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowBulkRemoveModal(true)}
                        className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-black rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shadow-xs"
                        title="Remove selected candidates from current Batch"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        <span>Remove ({selectedMemberIds.length})</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowBulkInviteModal(true)}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-black rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shadow-xs"
                        title="Bulk Invite selected candidates to a Group Event"
                      >
                        <Calendar className="h-3.5 w-3.5" />
                        <span>Group Event ({selectedMemberIds.length})</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAssignAdminModal(true)}
                        className="px-3 py-1.5 bg-brand-primary hover:bg-brand-primary-dark text-white text-xs font-black rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shadow-xs"
                        title="Bulk Assign Admin/Team Member to selected candidates"
                      >
                        <UserCheck className="h-3.5 w-3.5" />
                        <span>Assign Admin ({selectedMemberIds.length})</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowBulkTagModal(true)}
                        className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-black rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shadow-xs"
                        title="Bulk Tag selected candidates"
                      >
                        <Tag className="h-3.5 w-3.5" />
                        <span>Tag ({selectedMemberIds.length})</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowScreeningInviteModal(true)}
                        className="px-3 py-1.5 bg-brand-primary hover:bg-brand-primary-dark text-white text-xs font-black rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shadow-xs"
                        title="Send Screening Invitation emails to selected candidates"
                      >
                        <Calendar className="h-3.5 w-3.5" />
                        <span>Send Screening Invite ({selectedMemberIds.length})</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAiScreeningModal(true)}
                        className="px-3 py-1.5 bg-sky-600 hover:bg-sky-700 text-white text-xs font-black rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shadow-xs"
                        title="Batch-Trigger AI Screening for selected candidates"
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                        <span>AI Screening ({selectedMemberIds.length})</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowAssignAutomationModal(true)}
                        className="px-3 py-1.5 bg-brand-primary hover:bg-brand-primary-dark text-white text-xs font-black rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shadow-xs"
                        title="Assign Automation flow to selected candidates"
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                        <span>Assign Automation ({selectedMemberIds.length})</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowBulkVariableModal(true)}
                        className="px-3 py-1.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white text-xs font-black rounded-xl transition-all cursor-pointer flex items-center gap-1.5 shadow-xs"
                        title="Bulk Update Program Pricing Variable for selected candidates"
                      >
                        <DollarSign className="h-3.5 w-3.5" />
                        <span>Bulk Variable ({selectedMemberIds.length})</span>
                      </button>
                    </>
                  )}

                  {/* Page selection toggle — page scope only, never the whole filtered set */}
                  {filteredMembers.length > 0 && (
                    <button
                      type="button"
                      onClick={toggleMemberPageSelection}
                      className="px-2.5 py-1.5 text-xs font-black text-slate-500 hover:text-slate-800 transition rounded-xl hover:bg-slate-50 cursor-pointer"
                    >
                      {memberPageShare === 'all' ? 'Deselect Page' : 'Select Page'}
                    </button>
                  )}
                </div>
              </div>

              {/* Unified Filter Row */}
              {showMemberFilters && (
                <div className="bg-slate-50 border-b border-slate-100 p-3 animate-fade-in">
                  <UnifiedApplicantFilterBar
                    filters={batchFilters}
                    onFilterChange={setBatchFilters}
                    includeMasterPhase={true}
                    programsList={props.programsList}
                    batchesList={batches}
                    applications={props.applications}
                  />
                  <div className="flex items-center justify-between text-caption text-slate-500 font-medium px-2 pt-2 border-t border-slate-200/60 mt-2">
                    <span>Active members matching criteria: <strong className="text-slate-800">{filteredMembers.length}</strong> of {batchMembers.length}</span>
                    {memberProgramFilter && (
                      <button
                        type="button"
                        onClick={() => setMemberProgramFilter('')}
                        className="text-xs text-rose-600 hover:text-rose-800 font-black cursor-pointer"
                      >
                        Clear Quick Program Filter
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Central spreadsheet grid */}
              {batchMembers.length === 0 ? (
                <div className="p-16 text-center">
                  <div className="h-12 w-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4 border border-slate-200/50">
                    <Users className="h-6 w-6 text-slate-400" />
                  </div>
                  <h4 className="text-xs font-black text-slate-800 uppercase tracking-widest">No enrolled records</h4>
                  <p className="text-caption text-slate-400 mt-1 max-w-sm mx-auto">Click "Enroll Applicants" above to seed candidates into this operational cohort.</p>
                </div>
              ) : (
                <div className="overflow-x-auto border-t border-b border-slate-300">
                  <table className="w-full text-left text-xs text-slate-900 border-collapse border-slate-300">
                    <thead>
                      <tr className="bg-slate-100 border-b-2 border-slate-300 text-[11px] uppercase font-black text-slate-800 select-none">
                        <th className="py-3.5 px-4 w-10 border-r border-slate-300 text-center sticky top-0 bg-slate-100 z-10">
                          <input
                            type="checkbox"
                            checked={memberPageShare === 'all'}
                            ref={(el) => {
                              if (el) el.indeterminate = memberPageShare === 'some';
                            }}
                            onChange={toggleMemberPageSelection}
                            className="rounded border-slate-400 text-brand focus:ring-brand-primary/20 cursor-pointer"
                          />
                        </th>
                        {orderedVisibleColumns.map(col => {
                          const colW = columnWidths[col.id] || 160;
                          return (
                            <th
                              key={col.id}
                              draggable
                              onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDraggingColumnId(col.id); }}
                              onDragOver={(e) => { e.preventDefault(); if (col.id !== draggingColumnId) setDragOverColumnId(col.id); }}
                              onDragLeave={() => setDragOverColumnId(prev => (prev === col.id ? null : prev))}
                              onDrop={(e) => { e.preventDefault(); handleColumnDrop(col.id); }}
                              onDragEnd={() => { setDraggingColumnId(null); setDragOverColumnId(null); }}
                              title="Drag to reorder or drag border to resize"
                              style={{ width: `${colW}px`, minWidth: `${colW}px`, maxWidth: `${colW}px` }}
                              className={`group/th sticky top-0 cursor-grab select-none truncate border-r border-slate-300 bg-slate-100 py-3.5 px-4 relative z-10 ${
                                dragOverColumnId === col.id && draggingColumnId !== col.id ? 'bg-blue-100 ring-2 ring-inset ring-blue-300' : ''
                              } ${draggingColumnId === col.id ? 'opacity-40' : ''}`}
                            >
                              <span className="inline-flex items-center gap-1.5 truncate">
                                <GripVertical className="h-3 w-3 shrink-0 text-slate-400 transition-colors group-hover/th:text-slate-600" />
                                <span className="truncate">{col.label}</span>
                              </span>
                              {/* Draggable Resizer Handle */}
                              <div
                                onMouseDown={(e) => handleResizeMouseDown(e, col.id, colW)}
                                className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-brand-primary/50 active:bg-blue-700 z-10"
                                title="Drag to resize column width"
                              />
                            </th>
                          );
                        })}
                        <th className="py-3.5 px-4 text-right sticky top-0 bg-slate-100 border-slate-300 font-black text-slate-800 z-10">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-300">
                      {pageMembers.map((member, rowIndex) => {
                        const memberName = batchDisplayName(member) || member.userEmail;
                        const isSelected = selectedMemberIds.includes(member.applicationId);
                        return (
                          <tr
                            key={member.applicationId}
                            className={`hover:bg-blue-100/60 transition-all border-b border-slate-300 ${
                              isSelected
                                ? 'bg-blue-50/80 font-semibold'
                                : rowIndex % 2 === 1
                                  ? 'bg-slate-50/80'
                                  : 'bg-white'
                            }`}
                          >
                            <td className="py-3 px-4 border-r border-slate-300 text-center">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {
                                  setSelectedMemberIds(prev =>
                                    prev.includes(member.applicationId)
                                      ? prev.filter(id => id !== member.applicationId)
                                      : [...prev, member.applicationId]
                                  );
                                }}
                                className="rounded border-slate-400 text-brand focus:ring-brand-primary/20 cursor-pointer"
                              />
                            </td>
                            {orderedVisibleColumns.map(col => {
                              const colW = columnWidths[col.id] || 160;
                              return (
                                <td
                                  key={col.id}
                                  style={{ width: `${colW}px`, minWidth: `${colW}px`, maxWidth: `${colW}px` }}
                                  className="py-3 px-4 border-r border-slate-300 font-semibold text-slate-900 truncate"
                                  title={typeof col.render(member) === 'string' ? (col.render(member) as string) : undefined}
                                >
                                  {col.render(member)}
                                </td>
                              );
                            })}
                            <td className="py-3 px-4 text-right space-x-1 whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() => onViewApplicant(member)}
                                className="px-2.5 py-1 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-[10px] font-black rounded-lg transition shadow-3xs cursor-pointer"
                                title="View full profile details"
                              >
                                View Profile
                              </button>
                              {canEditBatch && (
                              <button
                                type="button"
                                onClick={async () => {
                                  if (await confirmDialog(`Remove candidate "${memberName}" from cohort "${selectedBatch.name}"?`)) {
                                    const updatedList = selectedBatch.applicantIds.filter(id => id !== member.applicationId);
                                    handleUpdateBatchMembers(selectedBatch.batchId, updatedList, `Removed member: ${memberName} (${member.applicationId})`);
                                  }
                                }}
                                className="px-2.5 py-1 text-red-500 hover:bg-red-50 border border-transparent hover:border-red-100 rounded-lg text-[10px] font-bold transition cursor-pointer"
                              >
                                Remove
                              </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Table pagination controller */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-slate-100 p-5 bg-white shrink-0 rounded-b-3xl">
                <div className="flex items-center gap-3 text-[11px] text-slate-500 font-semibold">
                  <span>
                    Showing {filteredMembers.length === 0 ? 0 : safePage * pageSize + 1}–{Math.min((safePage + 1) * pageSize, filteredMembers.length)} of {filteredMembers.length} active rows
                  </span>
                  <div className="flex items-center gap-1.5 border-l border-slate-200 pl-3">
                    <span className="text-slate-400 font-bold uppercase text-[10px] tracking-wider">Rows per page:</span>
                    <select
                      value={membersPerPage}
                      onChange={(e) => {
                        setMembersPerPage(Number(e.target.value));
                        setMemberPage(0);
                      }}
                      className="bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 text-xs font-extrabold text-slate-800 outline-none focus:border-brand cursor-pointer"
                    >
                      <option value={20}>20</option>
                      {/* 25 stays selectable: it is the established
                          default page size. #1333's option list
                          omitted it, which made the default
                          unrepresentable and silently moved the
                          landing view to 50 rows. */}
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={150}>150</option>
                      <option value={200}>200</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    disabled={safePage === 0}
                    onClick={() => setMemberPage(safePage - 1)}
                    className="px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  >
                    <ChevronLeft className="h-3.5 w-3.5 inline-block -mt-0.5" /> Prev
                  </button>
                  <span className="text-xs font-bold text-slate-600 px-2">
                    Page {safePage + 1} of {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={safePage >= totalPages - 1}
                    onClick={() => setMemberPage(safePage + 1)}
                    className="px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                  >
                    Next <ChevronRight className="h-3.5 w-3.5 inline-block -mt-0.5" />
                  </button>
                </div>
              </div>

            </div>
          </div>

          {/* Right Column: Bulk Operations Control Panel — rendered ONLY when applicants are selected */}
          {hasSelection && (
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white border border-slate-200 rounded-3xl p-5 text-slate-900 shadow-xl space-y-4 animate-fade-in">
              <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                <div className="flex items-center gap-2">
                  <span className="px-2.5 py-1 bg-brand text-white font-black text-[11px] rounded-lg">
                    {selectedMemberIds.length} Selected
                  </span>
                  <span className="text-xs font-bold text-slate-700">Bulk Actions</span>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedMemberIds([])}
                  className="text-slate-500 hover:text-slate-800 text-xs font-semibold cursor-pointer"
                >
                  Clear Selection
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1 pb-2">
                <button
                  type="button"
                  onClick={() => {
                    setRelocateTargetBatchId('');
                    setShowRelocateModal(true);
                  }}
                  className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Move className="w-3.5 h-3.5" /> Bulk Relocate
                </button>
                <button
                  type="button"
                  onClick={() => setShowBulkRemoveModal(true)}
                  className="px-3 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Remove Members
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setBulkFinanceType("invoice");
                    setBulkFinanceModalOpen(true);
                  }}
                  className="px-3 py-2 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <DollarSign className="w-3.5 h-3.5" /> Bulk Charge
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setBulkFinanceType("quote");
                    setBulkFinanceModalOpen(true);
                  }}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <FileText className="w-3.5 h-3.5" /> Send Quote
                </button>
                <button
                  type="button"
                  onClick={() => setShowAssignAdminModal(true)}
                  className="px-3 py-2 bg-brand-primary hover:bg-brand-primary-dark text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <UserCheck className="w-3.5 h-3.5" /> Assign Admin
                </button>
                <button
                  type="button"
                  onClick={() => setShowBulkTagModal(true)}
                  className="px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 cursor-pointer"
                >
                  <Tag className="w-3.5 h-3.5" /> Bulk Tag
                </button>
              </div>
              <ApplicantExportSection
                batchMembers={batchMembers.filter(m => (selectedMemberIds || []).includes(m.applicationId || m.id))}
                selectedBatch={selectedBatch}
              />
            </div>
          </div>
          )}
        </div>
      </div>
    )}

    {/* ==================== RELATED EVENTS TAB ==================== */}
    {batchTab === 'relatedEvents' && (
    <BatchRelatedEventsSection
      batchMembers={batchMembers}
      adminEmail={adminEmail}
      onViewApplicant={onViewApplicant}
    />
    )}

    {/* ==================== RELATED SIGN REQUESTS TAB ==================== */}
    {batchTab === 'relatedSignRequests' && (
    <BatchRelatedSignRequestsSection
      batchMembers={batchMembers}
      adminEmail={adminEmail}
      onViewApplicant={onViewApplicant}
    />
    )}

    {/* ==================== BULK REQUESTS TAB ==================== */}
    {batchTab === 'bulkRequests' && (
    <BatchBulkRequestsSection
      batchMembers={batchMembers}
      batchId={selectedBatch.batchId}
      adminEmail={adminEmail}
      onViewApplicant={onViewApplicant}
    />
    )}

    {/* ==================== RELATED EMAIL CAMPAIGNS TAB ==================== */}
    {batchTab === 'relatedEmailCampaigns' && (
    <BatchRelatedEmailCampaignsSection
      batchMembers={batchMembers}
      batchId={selectedBatch.batchId}
      adminEmail={adminEmail}
      onViewApplicant={onViewApplicant}
    />
    )}
    {/* ==================== NOTES & ACTIVITY TAB ==================== */}
    {batchTab === 'notes' && (
    <div className="max-w-3xl mx-auto space-y-6">

    {/* B. ACTIVE BATCH NOTES FOR ADMINS */}
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
    <h4 className="text-xs font-bold tracking-wider text-slate-900 flex items-center">
    <FileText className="h-4 w-4 mr-1.5 text-blue-500" />
    Internal Cohort Notes
    </h4>
    <span className="text-caption uppercase font-bold text-slate-500">Internal Admin Access Only</span>
    </div>

    <p className="text-caption text-slate-400 leading-normal">
    Maintain processing minutes, embassy constraints, interviewer feedback, or custom schedules specific to this candidate drive. Notes are instantly saved to cloud storage on dispatch.
    </p>

    <div className="space-y-3">
    <textarea
    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs outline-none  focus:bg-white min-h-[140px] font-mono leading-relaxed text-slate-700"
    placeholder="Type administrative cohort details, visa drive guidelines, or sponsor requirements here..."
    value={batchNotes}
    onChange={(e) => setBatchNotes(e.target.value)}
    disabled={!canEditBatch}
    />
    {canEditBatch && (
    <div className="flex justify-end pt-1">
    <button
    type="button"
    disabled={isSavingNotes}
    onClick={() => handleSaveBatchNotes(selectedBatch.batchId, batchNotes)}
    className="px-4 py-2 bg-brand hover:bg-brand-active text-white font-bold text-xs rounded-xl flex items-center space-x-1.5 transition-all shadow-sm cursor-pointer disabled:opacity-50"
    >
    {isSavingNotes ? (
    <>
    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
    <span>Saving Notes...</span>
    </>
    ) : (
    <>
    <Save className="h-3.5 w-3.5" />
    <span>Save Notes Log</span>
    </>
    )}
    </button>
    </div>
    )}
    </div>
    </div>

    {/* C. BATCH COMMENTS & ACTIVITY LOG FEED */}
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
    <div className="flex items-center justify-between border-b border-slate-100 pb-3">
    <div>
    <h4 className="text-xs font-bold tracking-wider text-slate-900 flex items-center">
    <MessageSquare className="h-4 w-4 mr-1.5 text-brand" />
    Cohort Activity Logs & Comments ({batchLogs.length})
    </h4>
    <p className="text-caption text-slate-400 mt-0.5">Admin-only real-time log, milestone track, or custom file attachments.</p>
    </div>
    <span className="text-caption font-mono bg-brand-active px-2.5 py-1 rounded-lg font-bold border border-blue-100">
    Secure Feed
    </span>
    </div>

    {/* Log Posting Form */}
    <div className="space-y-3 bg-slate-50 border border-slate-200/60 p-3.5 rounded-xl">
    <div>
    <textarea
    className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs outline-none  focus:bg-white min-h-[80px] font-sans leading-relaxed"
    placeholder={batchLogFileProgress !== null ? "Preparing log attachment... Please wait." : "Log a milestone, add admin notes, or record file attachments..."}
    value={newBatchLogText}
    onChange={(e) => setNewBatchLogText(e.target.value)}
    disabled={batchLogFileProgress !== null}
    rows={2}
    />
    </div>

    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1">
    <div className="flex items-center space-x-2">
    <label className="cursor-pointer inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 font-bold text-caption text-slate-700 transition-all">
    {batchLogFileProgress !== null ? (
    <RefreshCw className="h-3.5 w-3.5 text-blue-500 animate-spin" />
    ) : (
    <Upload className="h-3.5 w-3.5 text-slate-400" />
    )}
    <span className="truncate max-w-[150px]">
    {batchLogFileProgress !== null
    ? `Uploading (${batchLogFileProgress}%)`
    : batchLogFile
    ? batchLogFile.name
    : 'Attach Document'
    }
    </span>
    <input
    type="file"
    className="hidden"
    onChange={(e) => {
    if (e.target.files && e.target.files[0]) {
    handleBatchLogFileChange(e.target.files[0]);
    }
    }}
    />
    </label>
    {batchLogFile && (
    <button
    type="button"
    onClick={() => handleBatchLogFileChange(null)}
    className="p-1 hover:bg-slate-200 rounded-md text-slate-500 transition-colors"
    >
    <X className="h-3.5 w-3.5" />
    </button>
    )}
    </div>

    <button
    type="button"
    disabled={isUploadingBatchLogFile || batchLogFileProgress !== null || (!newBatchLogText.trim() && !batchLogFile)}
    onClick={handleAddBatchLog}
    className="px-3.5 py-1.5 btn-primary  disabled:opacity-55 font-bold text-white rounded-lg text-caption flex items-center space-x-1 transition-all cursor-pointer shadow-sm"
    >
    {isUploadingBatchLogFile ? (
    <>
    <RefreshCw className="h-3 w-3 animate-spin mr-1" />
    <span>Syncing...</span>
    </>
    ) : (
    <span>Post Log Entry</span>
    )}
    </button>
    </div>

    {batchLogFileProgress !== null && (
    <div className="w-full bg-slate-200 rounded-full h-1 overflow-hidden shadow-inner mt-1">
    <div
    className="btn-primary h-1 rounded-full transition-all duration-300"
    style={{ width: `${batchLogFileProgress}%` }}
    />
    </div>
    )}
    </div>

    {/* Logs Feed display */}
    {isLoadingLogs ? (
    <div className="py-6 text-center text-slate-400 text-caption font-semibold flex items-center justify-center space-x-1.5">
    <RefreshCw className="h-3.5 w-3.5 animate-spin text-slate-400" />
    <span>Decrypting Activity feed history...</span>
    </div>
    ) : batchLogs.length === 0 ? (
    <div className="py-8 bg-slate-50/40 rounded-xl border border-dashed border-slate-200/80 text-center text-slate-400">
    <MessageSquare className="h-6 w-6 mx-auto text-slate-300 mb-1" />
    <p className="text-caption font-bold">No cohort activity logger history recorded.</p>
    </div>
    ) : (
    <div className="space-y-3.5 max-h-[480px] overflow-y-auto pr-1">
    {batchLogs.map((log) => (
    <div key={log.logId} className="bg-slate-50/50 border border-slate-200 p-3 rounded-xl hover:bg-slate-50 transition-colors">
    <div className="flex items-center justify-between mb-1.5">
    <div className="flex items-center space-x-2">
    <span className="font-extrabold text-caption text-slate-800">{log.adminName}</span>
    <span className="text-[11px] tracking-wider text-slate-400 font-bold bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">Admin</span>
    </div>
    <span className="text-caption font-mono font-bold text-slate-400">
    {new Date(log.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
    </span>
    </div>
    <p className="text-caption text-slate-700 font-medium leading-relaxed break-words whitespace-pre-wrap">{log.text}</p>
    {log.fileUrl && (
    <div className="mt-2.5 bg-white border border-slate-200 p-2 rounded-lg flex items-center justify-between text-caption">
    <div className="flex items-center space-x-1.5 min-w-0">
    <FileText className="h-3.5 w-3.5 text-blue-500 shrink-0" />
    <span className="font-semibold text-slate-700 truncate">{log.fileName || 'Attached document'}</span>
    </div>
    <button
    onClick={() => {
    setPreviewDoc({
    docId: '',
    applicationId: selectedApp?.applicationId || '',
    userId: auth.currentUser?.uid || 'unauthenticated',
    name: log.fileName || 'Attached document',
    type: 'Batch Log Document',
    uploadDate: log.createdAt,
    status: 'Approved',
    storagePath: log.fileUrl
    });
    }}
    className="text-brand hover:text-blue-800 font-bold flex items-center shrink-0 space-x-0.5 px-2 py-0.5 bg-brand-active rounded-lg transition-all"
    >
    <Eye className="h-3 w-3" />
    <span>Inspect</span>
    </button>
    </div>
    )}
    </div>
    ))}
    </div>
    )}

    {/* Refresh button */}
    <div className="flex justify-center pt-1">
    <button
    type="button"
    onClick={() => fetchBatchLogs(selectedBatch.batchId)}
    className="px-4 py-2 text-caption font-bold text-brand bg-brand-active border border-blue-200 rounded-xl transition-all cursor-pointer flex items-center space-x-1.5"
    >
    <RefreshCw className="h-3.5 w-3.5" />
    <span>Refresh Activity Feed</span>
    </button>
    </div>
    </div>

    </div>
    )}



    </div>
    );
    })()
    )}

      {/* FOLDER CREATE / EDIT MODAL (Top-Level) */}
      {showFolderModal && (
        <Modal onClose={() => setShowFolderModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md space-y-4 animate-fade-in text-xs">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2 text-brand">
                <FolderPlus className="h-5 w-5" />
                <h3 className="font-extrabold text-slate-900 text-sm font-display">
                  {folderModalMode === 'create' ? 'Create Folder' : 'Edit Folder'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowFolderModal(false)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleSaveFolder} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">Folder Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. 2025 Summer Cohorts"
                  value={folderNameInput}
                  onChange={e => setFolderNameInput(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs outline-none focus:border-brand focus:bg-white font-medium text-slate-800"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">Description / Memo</label>
                <textarea
                  placeholder="Folder description or processing notes..."
                  value={folderDescInput}
                  onChange={e => setFolderDescInput(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs outline-none focus:border-brand focus:bg-white min-h-[70px] text-slate-800 font-medium"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">Folder Tags (comma-separated)</label>
                <input
                  type="text"
                  placeholder="e.g. USA, Urgent, Priority"
                  value={folderTagsInput}
                  onChange={e => setFolderTagsInput(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs outline-none focus:border-brand focus:bg-white font-medium text-slate-800"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">Parent Location</label>
                <SearchableSelect
                  value={folderParentInput || ''}
                  onChange={v => setFolderParentInput(v || null)}
                  options={batchFolders
                    .filter(f => f.folderId !== editingFolderId && getFolderDepth(f.folderId) < 10)
                    .map(f => ({
                      value: f.folderId,
                      label: getFolderPathLabel(f.folderId),
                    }))}
                  placeholder="Search folders…"
                  clearLabel="Main (Top Level)"
                  emptyLabel="No folders found"
                />
              </div>

              <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowFolderModal(false)}
                  className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold hover:bg-slate-50 text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={folderSubmitting}
                  className="px-5 py-2 btn-primary text-white rounded-xl text-xs font-bold shadow-sm disabled:opacity-50"
                >
                  {folderSubmitting ? 'Saving...' : folderModalMode === 'create' ? 'Create Folder' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* MOVE BATCH MODAL (Top-Level) */}
      {movingBatch && (
        <Modal onClose={() => setMovingBatch(null)}>
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md space-y-4 animate-fade-in text-xs">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2 text-brand">
                <Move className="h-5 w-5" />
                <h3 className="font-extrabold text-slate-900 text-sm font-display">
                  Move Cohort Batch
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setMovingBatch(null)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              <p className="text-slate-600 font-medium leading-relaxed">
                Select a target folder to move cohort <span className="font-extrabold text-slate-900">{movingBatch.name}</span>:
              </p>

              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">Target Folder Location</label>
                <SearchableSelect
                  value={moveTargetFolderId || ''}
                  onChange={v => setMoveTargetFolderId(v || null)}
                  options={batchFolders
                    .filter(f => getFolderDepth(f.folderId) < 10)
                    .map(f => ({
                      value: f.folderId,
                      label: getFolderPathLabel(f.folderId),
                    }))}
                  placeholder="Search folders…"
                  clearLabel="Main (Top Level)"
                  emptyLabel="No folders found"
                />
              </div>
            </div>

            <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setMovingBatch(null)}
                className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold hover:bg-slate-50 text-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmMoveBatch}
                className="px-5 py-2 btn-primary text-white rounded-xl text-xs font-bold shadow-sm"
              >
                Confirm Move
              </button>
            </div>
          </div>
        </Modal>
      )}

    {showBulkInviteModal && (
      <Modal onClose={() => { if (!isBulkInviting) setShowBulkInviteModal(false); }} title="Bulk Invite to Group Event">
        <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden animate-slideUp text-slate-800 text-xs" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 shrink-0 flex items-center justify-between text-white">
            <div className="flex items-center space-x-2">
              <Calendar className="h-5 w-5 text-white" />
              <div>
                <h3 className="text-sm font-bold font-display">Bulk Invite to Group Event</h3>
                <p className="text-[11px] text-blue-100">Invite {selectedMemberIds.length} candidate(s) to a group event</p>
              </div>
            </div>
            <button
              onClick={() => setShowBulkInviteModal(false)}
              disabled={isBulkInviting}
              className="p-1.5 text-white/70 hover:text-white rounded-lg hover:bg-white/10 transition cursor-pointer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 overflow-y-auto space-y-4 flex-1">
            {bulkInviteResult ? (
              <div className="space-y-4 text-center py-6">
                <div className="w-12 h-12 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-500 mx-auto">
                  <CheckCircle className="h-8 w-8" />
                </div>
                <div className="space-y-1">
                  <h4 className="text-base font-bold text-slate-900">Invitations Queued</h4>
                  <p className="text-xs text-slate-500">
                    Queued <strong>{bulkInviteResult.invitedCount}</strong> invitation(s) out of {selectedMemberIds.length} selected. The background job sends the invitation emails; track progress in Task Tracker.
                  </p>
                  {bulkInviteResult.jobId && (
                    <p className="text-[11px] text-slate-500">Job ID: {bulkInviteResult.jobId}</p>
                  )}
                </div>

                {bulkInviteResult.errors.length > 0 && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-left space-y-1.5 max-h-48 overflow-y-auto">
                    <span className="text-[10px] uppercase font-bold text-rose-500 tracking-wider">Ineligibility Explanations ({bulkInviteResult.errors.length})</span>
                    <div className="space-y-1 divide-y divide-slate-100">
                      {bulkInviteResult.errors.map((err: any, i: number) => (
                        <div key={i} className="pt-1 text-[11px] flex justify-between font-medium">
                          <span className="text-slate-700 font-bold">{err.name || err.applicationId}</span>
                          <span className="text-rose-600 font-semibold">{err.error}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Select Group Event */}
                <div className="space-y-1.5">
                  <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block">Select Target Group Event *</label>
                  <SearchableSelect
                    value={selectedTargetEventId}
                    onChange={(v) => {
                      setSelectedTargetEventId(v);
                    }}
                    options={groupEventsList.map(ev => ({
                      value: ev.eventId,
                      label: `${ev.title} (${new Date(ev.dateTime).toLocaleDateString()})`,
                    }))}
                    placeholder="Search group events…"
                    clearLabel="-- Select a Group Event --"
                    emptyLabel="No group events found"
                  />
                </div>

                {selectedTargetEventId && (() => {
                  const targetEvent = groupEventsList.find(e => e.eventId === selectedTargetEventId);
                  if (!targetEvent) return null;

                  const eligibleApps: any[] = [];
                  const ineligibleApps: any[] = [];
                  const selectedApps = applications.filter(a => selectedMemberIds.includes(a.applicationId) || selectedMemberIds.includes(a.id) || selectedMemberIds.includes(a.applicantId));

                  selectedApps.forEach(app => {
                    const cfg = targetEvent.groupEventConfig || {};
                    let eligible = true;
                    let reason = '';

                    if (cfg.eligibleProgramId && cfg.eligibleProgramId !== 'all' && cfg.eligibleProgramId !== '') {
                      if (app.programId !== cfg.eligibleProgramId) {
                        eligible = false;
                        reason = 'Program mismatch';
                      }
                    }
                    if (eligible && cfg.eligibleTerm && cfg.eligibleTerm !== 'all' && cfg.eligibleTerm !== '') {
                      if (app.programTerm !== cfg.eligibleTerm) {
                        eligible = false;
                        reason = 'Term mismatch';
                      }
                    }
                    if (eligible && cfg.eligibleStage !== undefined && cfg.eligibleStage !== null && cfg.eligibleStage !== 'all' && cfg.eligibleStage !== '') {
                      if (String(app.masterStage) !== String(cfg.eligibleStage)) {
                        eligible = false;
                        reason = 'Pipeline stage mismatch';
                      }
                    }
                    if (eligible && cfg.eligibleStatus && cfg.eligibleStatus !== 'all' && cfg.eligibleStatus !== '') {
                      if (app.submissionStatus !== cfg.eligibleStatus) {
                        eligible = false;
                        reason = 'Application status mismatch';
                      }
                    }

                    const name = batchDisplayName(app) || app.userEmail || 'Applicant';
                    if (eligible) {
                      eligibleApps.push({ app, name });
                    } else {
                      ineligibleApps.push({ app, name, reason });
                    }
                  });

                  return (
                    <div className="space-y-3 pt-2 text-[11px]">
                      {/* Event restriction details */}
                      <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-3 space-y-1">
                        <span className="text-[10px] uppercase font-bold text-blue-800 tracking-wider">Event Restrictions</span>
                        <div className="grid grid-cols-2 gap-2 text-slate-600 font-semibold">
                          <p><strong>Program:</strong> {targetEvent.groupEventConfig?.eligibleProgramId || 'All programs'}</p>
                          <p><strong>Term/Season:</strong> {targetEvent.groupEventConfig?.eligibleTerm || 'All terms'}</p>
                          <p><strong>Stage:</strong> {targetEvent.groupEventConfig?.eligibleStage ? `Stage ${targetEvent.groupEventConfig.eligibleStage}` : 'All stages'}</p>
                          <p><strong>Status:</strong> {targetEvent.groupEventConfig?.eligibleStatus || 'All statuses'}</p>
                        </div>
                      </div>

                      {/* Eligible List */}
                      <div className="space-y-1">
                        <span className="font-bold text-emerald-700 flex items-center gap-1">
                          <CheckCircle className="h-4 w-4" /> Eligible for Invitation ({eligibleApps.length})
                        </span>
                        {eligibleApps.length === 0 ? (
                          <p className="text-slate-500 italic">No selected candidates are eligible.</p>
                        ) : (
                          <div className="max-h-24 overflow-y-auto border border-slate-200 rounded-lg p-2 bg-slate-50/30 space-y-1">
                            {eligibleApps.map((x, i) => (
                              <div key={i} className="flex justify-between font-medium text-slate-700">
                                <span>{x.name}</span>
                                <span className="text-slate-400 font-mono">ID: {x.app.applicationId}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Ineligible List with explainer */}
                      <div className="space-y-1">
                        <span className="font-bold text-rose-700 flex items-center gap-1">
                          <AlertCircle className="h-4 w-4" /> Ineligible for Invitation ({ineligibleApps.length})
                        </span>
                        {ineligibleApps.length === 0 ? (
                          <p className="text-slate-500 italic">All selected candidates are eligible.</p>
                        ) : (
                          <div className="max-h-32 overflow-y-auto border border-rose-100 rounded-lg p-2 bg-rose-50/20 space-y-1">
                            {ineligibleApps.map((x, i) => (
                              <div key={i} className="flex justify-between font-medium text-slate-700 pt-0.5">
                                <div>
                                  <span className="font-bold text-slate-800">{x.name}</span>
                                  <span className="text-slate-400 font-mono ml-1.5">ID: {x.app.applicationId}</span>
                                </div>
                                <span className="text-rose-600 font-bold shrink-0">{x.reason}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 shrink-0 flex justify-end gap-2">
            {bulkInviteResult ? (
              <button
                onClick={() => setShowBulkInviteModal(false)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold cursor-pointer transition"
              >
                Close
              </button>
            ) : (
              <>
                <button
                  onClick={() => setShowBulkInviteModal(false)}
                  disabled={isBulkInviting}
                  className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl font-bold cursor-pointer transition disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!selectedTargetEventId) return;
                    setIsBulkInviting(true);
                    setBulkInviteResult(null);

                    const targetEvent = groupEventsList.find(e => e.eventId === selectedTargetEventId);
                    if (!targetEvent) return;

                    const eligibleIds: string[] = [];
                    const selectedApps = applications.filter(a => selectedMemberIds.includes(a.applicationId) || selectedMemberIds.includes(a.id) || selectedMemberIds.includes(a.applicantId));

                    selectedApps.forEach(app => {
                      const cfg = targetEvent.groupEventConfig || {};
                      let eligible = true;
                      if (cfg.eligibleProgramId && cfg.eligibleProgramId !== 'all' && cfg.eligibleProgramId !== '') {
                        if (app.programId !== cfg.eligibleProgramId) eligible = false;
                      }
                      if (eligible && cfg.eligibleTerm && cfg.eligibleTerm !== 'all' && cfg.eligibleTerm !== '') {
                        if (app.programTerm !== cfg.eligibleTerm) eligible = false;
                      }
                      if (eligible && cfg.eligibleStage !== undefined && cfg.eligibleStage !== null && cfg.eligibleStage !== 'all' && cfg.eligibleStage !== '') {
                        if (String(app.masterStage) !== String(cfg.eligibleStage)) eligible = false;
                      }
                      if (eligible && cfg.eligibleStatus && cfg.eligibleStatus !== 'all' && cfg.eligibleStatus !== '') {
                        if (app.submissionStatus !== cfg.eligibleStatus) eligible = false;
                      }
                      if (eligible) {
                        eligibleIds.push(app.applicationId || app.id);
                      }
                    });

                    if (eligibleIds.length === 0) {
                      notifyToast('warning', 'None of the selected candidates are eligible for this event.');
                      setIsBulkInviting(false);
                      return;
                    }

                    try {
                      const data = await safeFetch(`/api/admin/group-events/${selectedTargetEventId}/invitations`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ applicationIds: eligibleIds })
                      });

                      const invitedCount = Array.isArray(data.invited) ? data.invited.length : eligibleIds.length;
                      notifyToast('success', `Queued ${invitedCount} invitation(s). Track progress in Task Tracker.`);
                      window.dispatchEvent(new CustomEvent('bulk-job-created'));
                      setBulkInviteResult({
                        success: true,
                        jobId: data.jobId || null,
                        invitedCount,
                        failedCount: (selectedMemberIds.length - invitedCount),
                        errors: data.errors || []
                      });
                      setSelectedMemberIds([]);
                    } catch (err: any) {
                      notifyToast('error', err.message || 'Failed to send bulk invitations');
                    } finally {
                      setIsBulkInviting(false);
                    }
                  }}
                  disabled={!selectedTargetEventId || isBulkInviting}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white rounded-xl font-bold flex items-center gap-1.5 transition-all shadow-md cursor-pointer"
                >
                  {isBulkInviting ? (
                    <>
                      <Loader className="h-3.5 w-3.5 animate-spin" />
                      <span>Queueing...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="h-3.5 w-3.5" />
                      <span>Queue Invitations</span>
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </Modal>
    )}

    {showRelocateModal && openBatch && (
      <Modal onClose={() => { if (!isRelocating) setShowRelocateModal(false); }} title="Bulk Relocate Applicants">
        <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-md animate-slideUp text-slate-800 text-xs overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-4 shrink-0 flex items-center justify-between text-white">
            <div className="flex items-center space-x-2">
              <Move className="h-5 w-5 text-white" />
              <div>
                <h3 className="text-sm font-bold font-display">Bulk Relocate Applicants</h3>
                <p className="text-[11px] text-amber-100">
                  Relocate {selectedMemberIds.length} candidate(s) to another batch
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowRelocateModal(false)}
              disabled={isRelocating}
              className="p-1.5 text-white/70 hover:text-white rounded-lg hover:bg-white/10 transition cursor-pointer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block mb-1">
                Select Destination Batch *
              </label>
              <SearchableSelect
                value={relocateTargetBatchId}
                onChange={v => setRelocateTargetBatchId(v)}
                options={batches
                  .filter(b => b.batchId !== openBatch.batchId)
                  .map(b => ({ value: b.batchId, label: batchOptionLabel(b) }))}
                placeholder="Search target batch..."
                clearLabel="-- Select Target Batch --"
                emptyLabel="No other batches found"
              />
            </div>

            <p className="text-slate-500 text-[11px] leading-relaxed bg-amber-50/60 border border-amber-100 rounded-xl p-3 font-medium">
              Relocating will remove the {selectedMemberIds.length} selected candidate(s) from batch <strong>{openBatch.name}</strong> and add them to the selected destination batch.
            </p>

            <div className="flex items-center justify-end space-x-2 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowRelocateModal(false)}
                disabled={isRelocating}
                className="px-4 py-2 border border-slate-200 rounded-xl font-bold hover:bg-slate-50 transition text-slate-600 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!relocateTargetBatchId || isRelocating}
                onClick={async () => {
                  if (!relocateTargetBatchId || !openBatch) return;
                  const targetBatch = batches.find(b => b.batchId === relocateTargetBatchId);
                  if (!targetBatch) return;
                  setIsRelocating(true);
                  try {
                    // The two membership writes cannot be one
                    // transaction from here, so each leg's result is
                    // checked and reported for exactly what it is —
                    // the helper resolves false when its write failed
                    // (it never throws), and no success is claimed or
                    // audited for a leg that did not land.
                    // 1. Remove from current batch
                    const updatedCurrentList = openBatch.applicantIds.filter(id => !selectedMemberIds.includes(id));
                    const removedOk = await handleUpdateBatchMembers(openBatch.batchId, updatedCurrentList, `Relocated ${selectedMemberIds.length} member(s) to ${targetBatch.name}`);
                    if (!removedOk) {
                      notifyToast('error', `Could not update "${openBatch.name}" — nothing was relocated. Please try again.`);
                      return;
                    }

                    // 2. Add to target batch (dedupe: a candidate
                    // already in the target is never added twice)
                    const existingTargetIds = targetBatch.applicantIds || [];
                    const mergedTargetIds = Array.from(new Set([...existingTargetIds, ...selectedMemberIds]));
                    const addedOk = await handleUpdateBatchMembers(targetBatch.batchId, mergedTargetIds, `Relocated ${selectedMemberIds.length} member(s) from ${openBatch.name}`);
                    if (!addedOk) {
                      notifyToast('error', `The ${selectedMemberIds.length} candidate(s) were removed from "${openBatch.name}" but could NOT be added to "${targetBatch.name}" — they are currently in no batch. Re-add them from the "${targetBatch.name}" batch view.`);
                      setSelectedMemberIds([]);
                      setShowRelocateModal(false);
                      return;
                    }

                    // Audit logs — only for a relocation that fully landed
                    selectedMemberIds.forEach(appId => {
                      onCreateAuditLog('APPLICANT_RELOCATED_BATCH', appId, `Relocated applicant from ${openBatch.name} to ${targetBatch.name}`);
                    });

                    notifyToast('success', `Relocated ${selectedMemberIds.length} applicant(s) to "${targetBatch.name}".`);
                    setSelectedMemberIds([]);
                    setShowRelocateModal(false);
                  } catch (err: any) {
                    notifyToast('error', err.message || 'Failed to relocate applicants.');
                  } finally {
                    setIsRelocating(false);
                  }
                }}
                className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-bold flex items-center space-x-1.5 transition shadow-sm cursor-pointer disabled:opacity-40"
              >
                {isRelocating ? (
                  <>
                    <Loader className="h-3.5 w-3.5 animate-spin" />
                    <span>Relocating...</span>
                  </>
                ) : (
                  <>
                    <Move className="h-3.5 w-3.5" />
                    <span>Relocate Candidates</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    )}

    {showBulkRemoveModal && openBatch && (
      <Modal onClose={() => { if (!isBulkRemoving) setShowBulkRemoveModal(false); }} title="Remove Candidates From Batch">
        <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-md animate-slideUp text-slate-800 text-xs overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="bg-gradient-to-r from-rose-600 to-red-600 px-6 py-4 shrink-0 flex items-center justify-between text-white">
            <div className="flex items-center space-x-2">
              <Trash2 className="h-5 w-5 text-white" />
              <div>
                <h3 className="text-sm font-bold font-display">Remove Candidates From Batch</h3>
                <p className="text-[11px] text-rose-100">
                  Remove {selectedMemberIds.length} candidate(s) from current batch
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowBulkRemoveModal(false)}
              disabled={isBulkRemoving}
              className="p-1.5 text-white/70 hover:text-white rounded-lg hover:bg-white/10 transition cursor-pointer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <p className="text-slate-700 text-xs leading-relaxed bg-rose-50 border border-rose-100 rounded-xl p-3 font-medium">
              Are you sure you want to remove <strong>{selectedMemberIds.length} selected candidate(s)</strong> from batch <strong>{openBatch.name}</strong>?
            </p>

            <div className="flex items-center justify-end space-x-2 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowBulkRemoveModal(false)}
                disabled={isBulkRemoving}
                className="px-4 py-2 border border-slate-200 rounded-xl font-bold hover:bg-slate-50 transition text-slate-600 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isBulkRemoving}
                onClick={async () => {
                  if (!openBatch) return;
                  setIsBulkRemoving(true);
                  try {
                    const updatedCurrentList = openBatch.applicantIds.filter(id => !selectedMemberIds.includes(id));
                    const removedOk = await handleUpdateBatchMembers(openBatch.batchId, updatedCurrentList, `Bulk removed ${selectedMemberIds.length} member(s)`);
                    if (!removedOk) {
                      // The write failed (the helper resolves false
                      // instead of throwing): no success toast, no
                      // audit entries, selection kept for a retry.
                      notifyToast('error', `Could not update "${openBatch.name}" — no candidates were removed. Please try again.`);
                      return;
                    }

                    selectedMemberIds.forEach(appId => {
                      onCreateAuditLog('APPLICANT_REMOVED_FROM_BATCH', appId, `Removed applicant from batch: ${openBatch.name}`);
                    });

                    notifyToast('success', `Removed ${selectedMemberIds.length} candidate(s) from "${openBatch.name}".`);
                    setSelectedMemberIds([]);
                    setShowBulkRemoveModal(false);
                  } catch (err: any) {
                    notifyToast('error', err.message || 'Failed to remove applicants.');
                  } finally {
                    setIsBulkRemoving(false);
                  }
                }}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold flex items-center space-x-1.5 transition shadow-sm cursor-pointer disabled:opacity-40"
              >
                {isBulkRemoving ? (
                  <>
                    <Loader className="h-3.5 w-3.5 animate-spin" />
                    <span>Removing...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-3.5 w-3.5" />
                    <span>Confirm Removal</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    )}

    {showAssignAdminModal && (
      <WorkspaceTeamAssignModal
        open={showAssignAdminModal}
        applicationIds={selectedMemberIds.length > 0 ? selectedMemberIds : batchMembers.map(m => m.applicationId || m.id)}
        onClose={() => setShowAssignAdminModal(false)}
        onAssigned={() => {
          notifyToast('success', 'Team member(s) assigned successfully.');
          props.appCtxRefresh?.();
        }}
      />
    )}

    {showBulkTagModal && (
      <Modal onClose={() => { if (!isBulkTagging) setShowBulkTagModal(false); }} title="Bulk Tag Candidates">
        <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-md animate-slideUp text-slate-800 text-xs overflow-hidden" onClick={e => e.stopPropagation()}>
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 shrink-0 flex items-center justify-between text-white">
            <div className="flex items-center space-x-2">
              <Tag className="h-5 w-5 text-white" />
              <div>
                <h3 className="text-sm font-bold font-display">Bulk Tag Candidates</h3>
                <p className="text-[11px] text-purple-100">
                  Tag {selectedMemberIds.length > 0 ? selectedMemberIds.length : batchMembers.length} candidate(s)
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowBulkTagModal(false)}
              disabled={isBulkTagging}
              className="p-1.5 text-white/70 hover:text-white rounded-lg hover:bg-white/10 transition cursor-pointer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block mb-1">
                Enter Tags (comma-separated) *
              </label>
              <input
                type="text"
                value={bulkTagInput}
                onChange={e => setBulkTagInput(e.target.value)}
                placeholder="e.g. VIP, Priority, Summer2025"
                className="w-full bg-slate-50 border border-slate-200 text-xs font-bold px-3 py-2.5 rounded-xl text-slate-800 outline-none focus:bg-white focus:border-purple-500 transition-all"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-slate-500 block mb-1">
                Tag Action Mode
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setBulkTagMode('append')}
                  className={`py-2 px-3 rounded-xl border text-xs font-bold transition cursor-pointer ${bulkTagMode === 'append' ? 'bg-purple-50 border-purple-500 text-purple-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`}
                >
                  Append (Keep existing)
                </button>
                <button
                  type="button"
                  onClick={() => setBulkTagMode('replace')}
                  className={`py-2 px-3 rounded-xl border text-xs font-bold transition cursor-pointer ${bulkTagMode === 'replace' ? 'bg-purple-50 border-purple-500 text-purple-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`}
                >
                  Replace (Overwrite all)
                </button>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 shrink-0 flex justify-end gap-2">
            <button
              onClick={() => setShowBulkTagModal(false)}
              disabled={isBulkTagging}
              className="px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl font-bold cursor-pointer transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                const rawTags = bulkTagInput.split(',').map(t => t.trim()).filter(Boolean);
                if (rawTags.length === 0 && bulkTagMode === 'append') {
                  notifyToast('warning', 'Please enter at least one tag.');
                  return;
                }
                const activeBatch = batches.find(b => b.batchId === selectedBatchId);
                const currentBatchMembers = activeBatch
                  ? applications.filter(app => activeBatch.applicantIds.includes(app.applicationId))
                  : applications;
                const targetIds = selectedMemberIds.length > 0 ? selectedMemberIds : currentBatchMembers.map(m => m.applicationId || m.id);
                if (targetIds.length === 0) {
                  notifyToast('warning', 'No candidate applications found.');
                  return;
                }
                if (!(await confirmDialog(`Confirm ${bulkTagMode} tags for ${targetIds.length} candidate(s)?`))) return;

                setIsBulkTagging(true);
                try {
                  const token = await auth.currentUser?.getIdToken();
                  const res = await safeFetch('/api/admin/applications/bulk-tags', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                    body: JSON.stringify({
                      applicationIds: targetIds,
                      rawTags,
                      mode: bulkTagMode,
                    }),
                  });
                  notifyToast('success', `Tags updated for ${res.updatedCount || targetIds.length} candidate(s).`);
                  props.appCtxRefresh?.();
                  setShowBulkTagModal(false);
                  setBulkTagInput('');
                } catch (err: any) {
                  notifyToast('error', 'Failed to update tags: ' + (err.message || String(err)));
                } finally {
                  setIsBulkTagging(false);
                }
              }}
              disabled={isBulkTagging}
              className="px-5 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white rounded-xl font-bold flex items-center gap-1.5 transition-all shadow-md cursor-pointer"
            >
              {isBulkTagging ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Tag className="h-3.5 w-3.5" />}
              <span>{isBulkTagging ? 'Tagging...' : 'Apply Tags'}</span>
            </button>
          </div>
        </div>
      </Modal>
    )}

      <BulkFinanceModal
        isOpen={bulkFinanceModalOpen}
        onClose={() => setBulkFinanceModalOpen(false)}
        type={bulkFinanceType}
        applicantIds={selectedMemberIds}
      />

      {/* Bulk Screening Invite Modal */}
      {showScreeningInviteModal && (
        <Modal onClose={() => setShowScreeningInviteModal(false)} title="Batch Screening Invitations">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-md animate-slideUp text-slate-800 text-xs overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4 shrink-0 flex items-center justify-between text-white">
              <div className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-white" />
                <div>
                  <h3 className="text-sm font-bold font-display">Batch Screening Invitations</h3>
                  <p className="text-[11px] text-indigo-100">
                    Send invitations to {selectedMemberIds.length > 0 ? selectedMemberIds.length : batchMembers.length} candidate(s)
                  </p>
                </div>
              </div>
              <button onClick={() => setShowScreeningInviteModal(false)} className="text-white/80 hover:text-white transition cursor-pointer p-1">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-indigo-50/80 border border-indigo-100 rounded-xl p-3.5 text-indigo-900 text-xs leading-relaxed space-y-2">
                <p className="font-bold">Automated Eligibility &amp; Duplicate Protection Rules:</p>
                <ul className="list-disc pl-4 space-y-1 text-[11px] text-indigo-800">
                  <li>Candidates with an active upcoming screening appointment will be <strong>skipped</strong>.</li>
                  <li>Candidates who completed screening or received an invitation within the last 24 hours will be <strong>skipped</strong>.</li>
                  <li>Eligible candidates will receive an email with a secure booking link and their credentials.</li>
                </ul>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setShowScreeningInviteModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl font-bold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    const targetIds = selectedMemberIds.length > 0 ? selectedMemberIds : batchMembers.map(m => m.applicationId || m.id);
                    if (targetIds.length === 0) {
                      notifyToast('warning', 'No candidates selected');
                      return;
                    }

                    try {
                      const token = await auth.currentUser?.getIdToken();
                      const jobItems = targetIds.map(appId => ({
                        itemType: 'application',
                        applicationId: appId,
                        inputSnapshot: { applicationId: appId },
                      }));

                      const res = await safeFetch('/api/admin/bulk-jobs', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({
                          type: 'bulk_screening_invite',
                          items: jobItems,
                          options: { jobName: 'Bulk Screening Invitations' },
                        }),
                      });

                      if (res && res.jobId) {
                        notifyToast('success', `Queued screening invitations for ${targetIds.length} candidate(s). Tracking progress in Task Tracker.`);
                        window.dispatchEvent(new CustomEvent('bulk-job-created'));
                        setShowScreeningInviteModal(false);
                      }
                    } catch (err: any) {
                      notifyToast('error', err.message || 'Failed to start screening invite batch job');
                    }
                  }}
                  className="px-5 py-2 bg-brand-primary hover:bg-brand-primary-dark text-white font-extrabold rounded-xl shadow-md cursor-pointer flex items-center gap-1.5"
                >
                  <Send className="h-4 w-4" /> Send Screening Invitations
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Assign Automation Modal */}
      <AssignAutomationModal
        isOpen={showAssignAutomationModal}
        onClose={() => setShowAssignAutomationModal(false)}
        source="batches"
        applicantIds={selectedMemberIds}
        referenceApplicant={(() => {
          // batchMembers only exists inside the batch-detail render branch;
          // derive the same member set here from props/state in scope at this
          // mount (selected batch's active members, mirroring the detail view).
          const selectedBatch = batches.find(b => b.batchId === selectedBatchId);
          if (!selectedBatch) return null;
          const members = applications.filter(app =>
            selectedBatch.applicantIds.includes(app.applicationId) &&
            isApplicationActive(app)
          );
          const selectedObjs = members.filter((m) => selectedMemberIds.includes(m.applicationId || (m as any).id));
          const ref = selectedObjs[0];
          return ref ? {
            id: ref.applicationId || (ref as any).id,
            applicantName: (ref as any).name || (ref as any).applicantName || (ref as any).userDisplayName,
            customApplicationId: ref.applicationId || (ref as any).id,
            programName: (ref as any).program || (ref as any).programName,
            currentStage: (ref as any).stage || (ref as any).currentStage,
          } : null;
        })()}
      />

      {/* Bulk AI Screening Modal */}
      {showAiScreeningModal && (
        <Modal onClose={() => setShowAiScreeningModal(false)} title="Batch AI Screening Run">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-md animate-slideUp text-slate-800 text-xs overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4 shrink-0 flex items-center justify-between text-white">
              <div className="flex items-center space-x-2">
                <Sparkles className="h-5 w-5 text-white" />
                <div>
                  <h3 className="text-sm font-bold font-display">Batch AI Screening Run</h3>
                  <p className="text-[11px] text-indigo-100">
                    Run AI screening across {selectedMemberIds.length > 0 ? selectedMemberIds.length : batchMembers.length} candidate(s)
                  </p>
                </div>
              </div>
              <button onClick={() => setShowAiScreeningModal(false)} className="p-1.5 text-white/70 hover:text-white rounded-lg hover:bg-white/10 transition cursor-pointer">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <label className="flex flex-col gap-1.5">
                <span className="font-bold text-slate-700">Select Screening Type:</span>
                <select
                  value={aiScreeningType}
                  onChange={(e) => setAiScreeningType(e.target.value as any)}
                  className="border border-slate-300 rounded-xl p-2.5 bg-white font-medium text-xs focus:ring-2 focus:ring-brand-primary"
                >
                  <option value="document_review">Document Review (OCR & Compliance)</option>
                  <option value="program_eligibility">Program Eligibility Review</option>
                  <option value="job_matching">Job Recommendations & Shortlist</option>
                  <option value="partner_suggestions">Sponsor / Partner Suggestion</option>
                </select>
              </label>

              <div className="bg-indigo-50/70 border border-indigo-100 rounded-xl p-3 text-indigo-900 text-[11px] font-medium leading-relaxed">
                All generated suggestions will land in the <strong>AI Suggestions</strong> tab as <code>pending_review</code>. No candidate statuses will be modified without explicit admin approval.
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setShowAiScreeningModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-xl font-bold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    const targetIds = selectedMemberIds.length > 0 ? selectedMemberIds : batchMembers.map(m => m.applicationId || m.id);
                    if (targetIds.length === 0) {
                      notifyToast('warning', 'No candidates selected');
                      return;
                    }

                    try {
                      const token = await auth.currentUser?.getIdToken();
                      const jobItems = targetIds.map(appId => ({
                        itemType: 'application',
                        applicationId: appId,
                        inputSnapshot: { applicationId: appId, screeningType: aiScreeningType },
                      }));

                      const res = await safeFetch('/api/admin/bulk-jobs', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                        body: JSON.stringify({
                          type: 'bulk_ai_screening',
                          items: jobItems,
                          options: { screeningType: aiScreeningType },
                        }),
                      });

                      if (res && res.jobId) {
                        notifyToast('success', `Queued AI screening for ${targetIds.length} candidate(s). Tracking progress in Task Tracker.`);
                        window.dispatchEvent(new CustomEvent('bulk-job-created'));
                        setShowAiScreeningModal(false);
                      }
                    } catch (err: any) {
                      notifyToast('error', err.message || 'Failed to start AI screening run');
                    }
                  }}
                  className="px-5 py-2 bg-brand-primary hover:bg-brand-primary-dark text-white font-extrabold rounded-xl shadow-md cursor-pointer flex items-center gap-1.5"
                >
                  <Sparkles className="h-4 w-4" /> Start AI Screening Run
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
      {/* BULK UPDATE PRICING VARIABLE MODAL */}
      {showBulkVariableModal && (
        <Modal onClose={() => { setShowBulkVariableModal(false); setBulkVariableResult(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 space-y-5 text-xs font-sans">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-600" />
                <h3 className="font-extrabold text-slate-900 text-sm font-display">Bulk Update Program Pricing Variable</h3>
              </div>
              <button onClick={() => { setShowBulkVariableModal(false); setBulkVariableResult(null); }} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            <p className="text-slate-600 text-xs leading-relaxed">
              Select a target Program Pricing Variable to assign to <strong>{selectedMemberIds.length}</strong> selected candidate(s).
              The system will validate compatibility independently for each application and skip incompatible applications.
            </p>

            {/* Target Variable Selector */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase text-slate-500 tracking-wider block">Target Pricing Variable *</label>
              <select
                value={targetBulkVariableId}
                onChange={(e) => setTargetBulkVariableId(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-xs font-bold text-slate-800 outline-none focus:bg-white focus:border-emerald-500"
              >
                <option value="">-- Select Target Pricing Variable --</option>
                {(props.programsList || []).map((prog: any) => {
                  const pVars = Array.isArray(prog.pricingVariables) ? prog.pricingVariables : [];
                  if (pVars.length === 0) return null;
                  return (
                    <optgroup key={prog.id || prog.programId} label={prog.name}>
                      {pVars.filter((v: any) => v.visibility === 'visible').map((v: any) => (
                        <option key={v.id} value={v.id}>
                          {prog.name} — {v.name} ({v.code || 'No code'}) [${Number(v.sellingPrice || 0).toFixed(2)}]
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
            </div>

            {bulkVariableResult && (
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                <div className="flex items-center justify-between font-bold">
                  <span className="text-slate-900 text-xs">Bulk Processing Summary</span>
                  <span className="text-[11px] font-mono text-slate-500">Total: {bulkVariableResult.totalSelected}</span>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="p-2 bg-emerald-100/70 border border-emerald-200 rounded-lg text-emerald-900 font-extrabold">
                    Updated: {bulkVariableResult.updatedCount}
                  </div>
                  <div className="p-2 bg-amber-100/70 border border-amber-200 rounded-lg text-amber-900 font-extrabold">
                    Skipped: {bulkVariableResult.skippedCount}
                  </div>
                  <div className="p-2 bg-rose-100/70 border border-rose-200 rounded-lg text-rose-900 font-extrabold">
                    Failed: {bulkVariableResult.failedCount}
                  </div>
                </div>

                {Array.isArray(bulkVariableResult.results) && bulkVariableResult.results.length > 0 && (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                    {bulkVariableResult.results.map((res: any, idx: number) => (
                      <div key={idx} className="p-2 bg-white border border-slate-200 rounded-lg flex items-start justify-between text-[11px]">
                        <div>
                          <span className="font-bold text-slate-800 block">{res.applicantName || res.applicationId}</span>
                          {res.reason && <span className="text-slate-500 text-[10px] block mt-0.5">{res.reason}</span>}
                          {res.previousVariable && <span className="text-slate-500 text-[10px] block mt-0.5">{res.previousVariable} &rarr; {res.newVariable}</span>}
                        </div>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                          res.status === 'updated' ? 'bg-emerald-100 text-emerald-800' : res.status === 'skipped' ? 'bg-amber-100 text-amber-800' : 'bg-rose-100 text-rose-800'
                        }`}>
                          {res.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => { setShowBulkVariableModal(false); setBulkVariableResult(null); }}
                className="px-4 py-2 border border-slate-200 text-slate-600 hover:bg-slate-100 rounded-xl font-bold transition cursor-pointer"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={!targetBulkVariableId || isBulkUpdatingVariable}
                onClick={async () => {
                  setIsBulkUpdatingVariable(true);
                  setBulkVariableResult(null);
                  try {
                    const res = await safeFetch('/api/admin/workflow/bulk-variable-update', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        targetVariableId: targetBulkVariableId,
                        applicationIds: selectedMemberIds,
                      }),
                    });
                    setBulkVariableResult(res);
                    notifyToast('success', `Bulk update complete: ${res.updatedCount || 0} updated, ${res.skippedCount || 0} skipped.`);
                  } catch (err: any) {
                    notifyToast('error', err.message || 'Failed to execute bulk variable update.');
                  } finally {
                    setIsBulkUpdatingVariable(false);
                  }
                }}
                className="px-5 py-2.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] disabled:opacity-50 text-white font-bold rounded-xl shadow-md transition cursor-pointer flex items-center gap-1.5"
              >
                {isBulkUpdatingVariable ? 'Processing...' : `Execute Bulk Update (${selectedMemberIds.length})`}
              </button>
            </div>
          </div>
        </Modal>
      )}

    </div>
  );
}

/* ====================================================================
   APPLICANT EXPORT SECTION
   ==================================================================== */
function ApplicantExportSection({
  batchMembers,
  selectedBatch,
}: {
  batchMembers: any[];
  selectedBatch: any;
}) {
  const [exportFieldSearch, setExportFieldSearch] = React.useState('');
  // All available export fields grouped by category
  const EXPORT_FIELD_GROUPS = [
    {
      category: 'Personal Information',
      fields: [
        { key: 'personalDetails.firstName', label: 'First Name' },
        { key: 'personalDetails.lastName', label: 'Last Name' },
        { key: 'personalDetails.fullNameLegal', label: 'Full Legal Name' },
        { key: 'personalDetails.dob', label: 'Date of Birth' },
        { key: 'personalDetails.gender', label: 'Gender' },
        { key: 'personalDetails.countryOfBirth', label: 'Country of Birth' },
        { key: 'personalDetails.countryOfCitizenship', label: 'Citizenship' },
        { key: 'personalDetails.maritalStatus', label: 'Marital Status' },
      ],
    },
    {
      category: 'Contact Information',
      fields: [
        { key: 'userEmail', label: 'Account Email' },
        { key: 'personalDetails.email', label: 'Personal Email' },
        { key: 'personalDetails.phone', label: 'Phone' },
        { key: 'personalDetails.address.street', label: 'Street' },
        { key: 'personalDetails.address.city', label: 'City' },
        { key: 'personalDetails.address.state', label: 'State' },
        { key: 'personalDetails.address.zip', label: 'ZIP Code' },
        { key: 'personalDetails.address.country', label: 'Country' },
      ],
    },
    {
      category: 'Program & Application',
      fields: [
        { key: 'programType', label: 'Program Type' },
        { key: 'status', label: 'Application Status' },
        { key: 'applicationId', label: 'Application ID' },
        { key: 'createdAt', label: 'Created Date' },
        { key: 'updatedAt', label: 'Updated Date' },
      ],
    },
    {
      category: 'Placement',
      fields: [
        { key: 'assignment.employerName', label: 'Employer Name' },
        { key: 'assignment.roleTitle', label: 'Role Title' },
        { key: 'assignment.city', label: 'City' },
        { key: 'assignment.state', label: 'State' },
        { key: 'serviceProvider.name', label: 'Service Provider' },
      ],
    },
    {
      category: 'Emergency Contacts',
      fields: [
        { key: 'emergencyContacts.0.fullName', label: 'Contact Name' },
        { key: 'emergencyContacts.0.phone', label: 'Contact Phone' },
        { key: 'emergencyContacts.0.email', label: 'Contact Email' },
        { key: 'emergencyContacts.0.relationship', label: 'Relationship' },
      ],
    },
  ];

  const allFieldKeys = EXPORT_FIELD_GROUPS.flatMap(g => g.fields.map(f => f.key));

  const [selectedFields, setSelectedFields] = React.useState<Set<string>>(new Set(allFieldKeys));
  const [showExportModal, setShowExportModal] = React.useState(false);
  const [savedTemplates, setSavedTemplates] = React.useState<{ name: string; fields: string[] }[]>(() => {
    try { return JSON.parse(localStorage.getItem('exportTemplates') || '[]'); } catch { return []; }
  });
  const [templateName, setTemplateName] = React.useState('');

  const toggleField = (key: string) => {
    setSelectedFields(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleGroup = (groupFields: { key: string }[]) => {
    const allSelected = groupFields.every(f => selectedFields.has(f.key));
    setSelectedFields(prev => {
      const next = new Set(prev);
      for (const f of groupFields) {
        if (allSelected) next.delete(f.key); else next.add(f.key);
      }
      return next;
    });
  };

  const selectAll = () => setSelectedFields(new Set(allFieldKeys));
  const deselectAll = () => setSelectedFields(new Set<string>());

  const resolveExportField = (app: any, key: string): string => {
    const parts = key.split('.');
    let val: any = app;
    for (const p of parts) {
      if (val == null) return '';
      if (/^\d+$/.test(p) && Array.isArray(val)) {
        val = val[parseInt(p)] || {};
        continue;
      }
      val = val[p];
    }
    return val != null ? String(val) : '';
  };

  const handleExport = () => {
    if (selectedFields.size === 0) {
      notifyToast('warning', 'Please select at least one field to export.');
      return;
    }
    if (batchMembers.length === 0) {
      notifyToast('warning', 'No applicants to export.');
      return;
    }

    // Build CSV
    const headers = EXPORT_FIELD_GROUPS.flatMap(g =>
      g.fields.filter(f => selectedFields.has(f.key)).map(f => f.label)
    );

    const rows = batchMembers.map(app => {
      return EXPORT_FIELD_GROUPS.flatMap(g =>
        g.fields.filter(f => selectedFields.has(f.key)).map(f => {
          const val = resolveExportField(app, f.key);
          // Wave 5 (M-05): shared csvCell — quoting + formula-injection guard.
          return csvCell(val);
        })
      );
    });

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `export_${selectedBatch?.name?.replace(/\s+/g, '_') || 'applicants'}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    setShowExportModal(false);
  };

  const saveTemplate = () => {
    if (!templateName.trim()) return;
    const newTemplate = { name: templateName.trim(), fields: Array.from(selectedFields) };
    const updated = [...savedTemplates.filter(t => t.name !== templateName.trim()), newTemplate];
    setSavedTemplates(updated);
    localStorage.setItem('exportTemplates', JSON.stringify(updated));
    setTemplateName('');
  };

  const loadTemplate = (tmpl: { name: string; fields: string[] }) => {
    setSelectedFields(new Set(tmpl.fields));
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
      <div className="flex items-center space-x-2">
        <Download className="text-brand h-4 w-4" />
        <h4 className="text-xs font-bold tracking-wider text-slate-900 font-display">Export Applicants</h4>
      </div>
      <p className="text-caption text-slate-400 leading-normal">
        Export applicant data as CSV. Select the fields you want to include.
      </p>

      <button
        type="button"
        onClick={() => setShowExportModal(true)}
        className="w-full py-2 btn-primary  text-white rounded-xl text-xs font-bold transition-all cursor-pointer shadow-sm flex items-center justify-center space-x-1.5"
      >
        <Download className="h-3.5 w-3.5" />
        <span>Export {batchMembers.length} Applicants</span>
      </button>

      {showExportModal && (
        <Modal onClose={() => setShowExportModal(false)} title="Export Applicant Data">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
              <div>
                <h3 className="text-sm font-extrabold text-slate-900">Export Applicant Data</h3>
                <p className="text-caption text-slate-400 mt-0.5">Choose fields to include in the CSV export</p>
              </div>
              <button onClick={() => setShowExportModal(false)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Saved templates */}
            {savedTemplates.length > 0 && (
              <div className="px-6 py-3 border-b border-slate-100 bg-slate-50/50">
                <p className="text-caption font-bold text-slate-500 mb-2">Saved Templates</p>
                <div className="flex flex-wrap gap-2">
                  {savedTemplates.map(tmpl => (
                    <button
                      key={tmpl.name}
                      onClick={() => loadTemplate(tmpl)}
                      className="px-3 py-1 bg-white border border-slate-200 rounded-lg text-caption font-semibold text-slate-600 hover:border-blue-300 hover:text-brand transition-all cursor-pointer"
                    >
                      {tmpl.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Searchable Field Selection */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              <div className="relative mb-2">
                <input
                  type="text"
                  placeholder="Search export fields..."
                  value={exportFieldSearch}
                  onChange={e => setExportFieldSearch(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold outline-none focus:bg-white focus:border-brand"
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-caption font-bold text-slate-500">{selectedFields.size} of {allFieldKeys.length} fields selected</span>
                <div className="flex space-x-2">
                  <button onClick={selectAll} className="text-caption font-semibold text-brand hover:text-blue-800 cursor-pointer">Select All</button>
                  <button onClick={deselectAll} className="text-caption font-semibold text-slate-500 hover:text-slate-700 cursor-pointer">Clear</button>
                </div>
              </div>

              {EXPORT_FIELD_GROUPS.map(group => {
                const filteredFields = exportFieldSearch.trim()
                  ? group.fields.filter(f => f.label.toLowerCase().includes(exportFieldSearch.toLowerCase()) || f.key.toLowerCase().includes(exportFieldSearch.toLowerCase()))
                  : group.fields;
                if (filteredFields.length === 0) return null;
                return (
                  <div key={group.category}>
                    <div
                      className="flex items-center justify-between py-2 cursor-pointer"
                      onClick={() => toggleGroup(filteredFields)}
                    >
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">{group.category}</span>
                      <span className="text-caption text-slate-400">{filteredFields.filter(f => selectedFields.has(f.key)).length}/{filteredFields.length}</span>
                    </div>
                    <div className="space-y-1 pl-2">
                      {filteredFields.map(f => (
                        <label key={f.key} className="flex items-center space-x-2 py-1 px-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedFields.has(f.key)}
                            onChange={() => toggleField(f.key)}
                            className="h-3.5 w-3.5 rounded border-slate-300 text-brand focus:ring-brand-primary"
                          />
                          <span className="text-xs font-medium text-slate-700">{f.label}</span>
                          <span className="text-caption text-slate-400 ml-auto font-mono">{f.key}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50/50 flex items-center justify-between shrink-0">
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={templateName}
                  onChange={e => setTemplateName(e.target.value)}
                  placeholder="Save as template..."
                  className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg outline-none  bg-white w-40"
                />
                <button
                  onClick={saveTemplate}
                  disabled={!templateName.trim() || selectedFields.size === 0}
                  className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-caption font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
                >
                  Save
                </button>
              </div>
              <div className="flex space-x-2">
                <button onClick={() => setShowExportModal(false)} className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 cursor-pointer">Cancel</button>
                <button onClick={handleExport} className="px-4 py-2 btn-primary  text-white rounded-lg text-xs font-bold cursor-pointer flex items-center space-x-1.5">
                  <Download className="h-3.5 w-3.5" />
                  <span>Export CSV ({batchMembers.length} rows)</span>
                </button>
              </div>
            </div>
          </div>
        </Modal>
        )}
    </div>
  );
}

function BulkGroupEventsSection({
  batchMembers,
  adminEmail,
}: {
  batchMembers: any[];
  adminEmail: string;
}) {

  // ── Bulk group event state ──
  const [beTitle, setBeTitle] = React.useState('');
  const [beType, setBeType] = React.useState<string>('Orientation');
  const [beDateTime, setBeDateTime] = React.useState('');
  const [beLocation, setBeLocation] = React.useState('');
  const [beDescription, setBeDescription] = React.useState('');
  const [beStatus, setBeStatus] = React.useState<string>('Scheduled');
  const [beSaving, setBeSaving] = React.useState(false);
  const [beSendEmail, setBeSendEmail] = React.useState(true);

  const handleCreateBulkEvents = async () => {
    if (!beTitle.trim() || !beDateTime || !beLocation.trim()) {
      notifyToast('warning', 'Please fill in title, date/time, and location.');
      return;
    }
    if (batchMembers.length === 0) {
      notifyToast('warning', 'No candidates in this batch to assign events to.');
      return;
    }
    if (!(await confirmDialog(`Create "${beTitle}" as a group event for all ${batchMembers.length} candidates?`))) return;
    setBeSaving(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const eventDateTime = new Date(beDateTime).toISOString();
      const items = batchMembers.map(app => ({
        itemType: 'event_recipient',
        applicationId: app.applicationId,
        inputSnapshot: {
          eventData: {
            title: beTitle.trim(),
            eventType: beType,
            dateTime: eventDateTime,
            location: beLocation.trim(),
            description: beDescription.trim(),
            status: beStatus,
            createdBy: adminEmail,
            applicantId: app.applicationId,
            applicantUserId: app.userId || '',
          }
        }
      }));

      const res = await fetch('/api/admin/bulk-jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          type: 'batch_event',
          items,
          inputSnapshot: { title: beTitle.trim(), dateTime: beDateTime },
        })
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to enqueue bulk event job');
      }

      setBeTitle(''); setBeDateTime(''); setBeLocation(''); setBeDescription('');
      notifyToast('success', `Bulk Event Task Queued for ${batchMembers.length} candidates! Tracking in Task Tracker.`);
      window.dispatchEvent(new CustomEvent('bulk-job-created'));
    } catch (err: any) {
      console.error('Failed to create bulk events:', err);
      notifyToast('error', 'An error occurred while creating events: ' + (err.message || 'Please try again.'));
    } finally { setBeSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-5">
      <div className="flex items-center space-x-2">
        <CalendarDays className="text-purple-500 h-5 w-5" />
        <h3 className="text-sm font-extrabold font-display tracking-tight text-slate-900">Bulk Group Events</h3>
      </div>
      <p className="text-caption text-slate-400 leading-normal">
        Create an event for all {batchMembers.length} enrolled candidates at once. Each applicant will see this event in their Events section.
      </p>

      <div className="space-y-3 text-xs">
        <div>
          <label className="text-caption uppercase font-bold text-slate-500 block mb-1">Event Title *</label>
          <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-purple-600 focus:bg-white text-slate-800"
            placeholder="e.g. Mandatory Cohort Orientation" value={beTitle} onChange={(e) => setBeTitle(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-caption uppercase font-bold text-slate-500 block mb-1">Type</label>
            <select value={beType} onChange={(e) => setBeType(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-purple-600 focus:bg-white cursor-pointer">
              <option value="Embassy">Embassy</option>
              <option value="Interview">Interview</option>
              <option value="Orientation">Orientation</option>
              <option value="Training">Training</option>
              <option value="Flight">Flight</option>
              <option value="Medical">Medical</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div>
            <label className="text-caption uppercase font-bold text-slate-500 block mb-1">Date & Time *</label>
            <input type="datetime-local" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-purple-600 focus:bg-white"
              value={beDateTime} onChange={(e) => setBeDateTime(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="text-caption uppercase font-bold text-slate-500 block mb-1">Location *</label>
          <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-purple-600 focus:bg-white text-slate-800"
            placeholder="e.g. Virtual via Zoom / Embassy Hall A" value={beLocation} onChange={(e) => setBeLocation(e.target.value)} />
        </div>
        <div>
          <label className="text-caption uppercase font-bold text-slate-500 block mb-1">Description</label>
          <textarea className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-purple-600 focus:bg-white min-h-[70px]"
            placeholder="Optional details about this event..." value={beDescription} onChange={(e) => setBeDescription(e.target.value)} />
        </div>
        <button type="button" onClick={handleCreateBulkEvents} disabled={beSaving}
          className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 font-bold text-white rounded-xl text-xs transition-all cursor-pointer shadow-md flex items-center justify-center space-x-1.5">
          <CalendarDays className="h-3.5 w-3.5" />
          <span>{beSaving ? 'Creating Events...' : `Create Group Event (${batchMembers.length} recipients)`}</span>
        </button>
      </div>
    </div>
  );
}

      /* ====================================================================
   BATCH BULK REQUESTS TAB — manages cohort-wide custom requests
   ==================================================================== */
export function BatchBulkRequestsSection({
  batchMembers,
  batchId,
  adminEmail,
  onViewApplicant,
}: {
  batchMembers: any[];
  batchId: string;
  adminEmail: string;
  onViewApplicant?: (app: any) => void;
}) {
  const [bulkRequests, setBulkRequests] = React.useState<any[]>([]);
  const [bulkReqTotalCount, setBulkReqTotalCount] = React.useState(0);
  const [bulkReqHasMore, setBulkReqHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [listError, setListError] = React.useState('');
  const [selectedBulkReq, setSelectedBulkReq] = React.useState<any>(null);
  const [recipientsData, setRecipientsData] = React.useState<any[]>([]);
  const [recipTotalCount, setRecipTotalCount] = React.useState(0);
  const [recipHasMore, setRecipHasMore] = React.useState(false);
  const [metrics, setMetrics] = React.useState<any>(null);
  const [loadingRecipients, setLoadingRecipients] = React.useState(false);

  // Form state
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [instructions, setInstructions] = React.useState('');
  const [responseType, setResponseType] = React.useState<'short_text' | 'long_text' | 'date' | 'dropdown' | 'file_upload' | 'form'>('short_text');
  const [bulkFormTextType, setBulkFormTextType] = React.useState<'short_text' | 'long_text' | 'date' | 'dropdown'>('short_text');
  const [bulkFormFields, setBulkFormFields] = React.useState<FormBuilderField[]>([]);
  const [priority, setPriority] = React.useState<'Low' | 'Medium' | 'High'>('Medium');
  const [dueDate, setDueDate] = React.useState('');
  const [dropdownOptionsInput, setDropdownOptionsInput] = React.useState('');
  const [requiredDocsInput, setRequiredDocsInput] = React.useState('');
  const [bulkNotifyAdmin, setBulkNotifyAdmin] = React.useState<boolean>(false);
  const [submitting, setSubmitting] = React.useState(false);

  // Filters for recipient table
  const [statusFilter, setStatusFilter] = React.useState<'All' | 'Pending' | 'Completed'>('All');
  const [recipSearch, setRecipSearch] = React.useState('');
  const [recipPage, setRecipPage] = React.useState(1);
  const RECIP_PER_PAGE = 25;
  const [recipSortBy, setRecipSortBy] = React.useState<'sent_desc' | 'sent_asc' | 'status_asc' | 'status_desc'>('sent_desc');

  // Search, filter, date range, and pagination state for bulk requests list
  const [bulkReqSearch, setBulkReqSearch] = React.useState('');
  const [bulkReqStatusFilter, setBulkReqStatusFilter] = React.useState<'All' | 'Active' | 'Cancelled'>('All');
  const [bulkReqDateFrom, setBulkReqDateFrom] = React.useState('');
  const [bulkReqDateTo, setBulkReqDateTo] = React.useState('');
  const [bulkReqPage, setBulkReqPage] = React.useState(1);
  const BULK_REQ_PER_PAGE = 25;

  // FIX-1311 (D1/D6): server pagination is cursor-based. Each stack
  // holds, per page number, the opaque cursor that FETCHES that page
  // (index 0 = page 1 = no cursor). Any filter, search or sort change
  // resets the stack and returns to page 1.
  const listCursorStackRef = React.useRef<Array<string | null>>([null]);
  const recipCursorStackRef = React.useRef<Array<string | null>>([null]);

  // Search stability: fetches are sequenced so only the LATEST call may
  // commit state — rapid typing can no longer land a stale result set
  // over a newer one. hasLoadedOnceRef separates the first load (full
  // section spinner) from refetches, which must never blank the section.
  const listFetchSeqRef = React.useRef(0);
  const recipFetchSeqRef = React.useRef(0);
  const hasLoadedOnceRef = React.useRef(false);

  // Active Job Progress Polling State
  const [jobProgressMap, setJobProgressMap] = React.useState<Record<string, { status: string; progressPercent: number; completedItems: number; failedItems: number; totalItems: number; errorSummary?: string }>>({});
  const watchedJobsRef = React.useRef<Set<string>>(new Set());

  // Cancel & update deadline states
  const [isCancelling, setIsCancelling] = React.useState(false);
  const [editDueDate, setEditDueDate] = React.useState('');
  const [isUpdatingDeadline, setIsUpdatingDeadline] = React.useState(false);

  // Response detail modal
  const [viewResponseRecipient, setViewResponseRecipient] = React.useState<any>(null);

  const fetchBulkRequests = React.useCallback(async (pageToLoad = bulkReqPage) => {
    const fetchSeq = ++listFetchSeqRef.current;
    try {
      setLoading(true);
      setListError('');
      const token = await auth.currentUser?.getIdToken();
      const qs = new URLSearchParams({
        batchId,
        page: String(pageToLoad),
        limit: String(BULK_REQ_PER_PAGE),
        status: bulkReqStatusFilter,
        search: bulkReqSearch,
        startDate: bulkReqDateFrom,
        endDate: bulkReqDateTo
      });
      const cursorForPage = listCursorStackRef.current[pageToLoad - 1] || null;
      if (cursorForPage) qs.set('cursor', cursorForPage);
      const res = await fetch(`/api/admin/bulk-requests?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() }
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.error || `Failed to load bulk requests (HTTP ${res.status})`);
      }
      const data = await res.json();
      // A newer fetch started while this one was in flight: drop the
      // stale response without touching state or the loading flag.
      if (fetchSeq !== listFetchSeqRef.current) return;
      // Remember the cursor that fetches the NEXT page.
      listCursorStackRef.current[pageToLoad] = data.nextCursor || null;
      setBulkRequests(data.bulkRequests || []);
      setBulkReqTotalCount(data.totalCount || (data.bulkRequests || []).length);
      setBulkReqHasMore(data.hasMore || false);
    } catch (err) {
      if (fetchSeq !== listFetchSeqRef.current) return;
      console.error('Failed to load bulk requests:', err);
      setListError(err instanceof Error ? err.message : 'Failed to load bulk requests');
    } finally {
      if (fetchSeq === listFetchSeqRef.current) {
        hasLoadedOnceRef.current = true;
        setLoading(false);
      }
    }
  }, [batchId, bulkReqPage, bulkReqStatusFilter, bulkReqSearch, bulkReqDateFrom, bulkReqDateTo]);

  const bulkReqBatchRef = React.useRef(batchId);
  React.useEffect(() => {
    if (bulkReqBatchRef.current !== batchId) {
      bulkReqBatchRef.current = batchId;
      listCursorStackRef.current = [null];
    }
    fetchBulkRequests(bulkReqPage);
  }, [batchId, bulkReqPage, bulkReqStatusFilter, bulkReqSearch, bulkReqDateFrom, bulkReqDateTo]);

  // FIX-1311 (D1/D6): recipients are server-paged by cursor. The view
  // (status/search/sort) is passed explicitly so a caller that has just
  // reset the view state fetches with the NEW values, not the stale
  // closure ones.
  const loadRecipients = async (req: any, pageToLoad: number, view: { status: string; search: string; sort: string }) => {
    const fetchSeq = ++recipFetchSeqRef.current;
    setLoadingRecipients(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const qs = new URLSearchParams({
        page: String(pageToLoad),
        limit: String(RECIP_PER_PAGE),
        status: view.status,
        search: view.search,
        sort: view.sort
      });
      const cursorForPage = recipCursorStackRef.current[pageToLoad - 1] || null;
      if (cursorForPage) qs.set('cursor', cursorForPage);
      const res = await fetch(`/api/admin/bulk-requests/${req.bulkRequestId}/recipients?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() }
      });
      if (res.ok) {
        const data = await res.json();
        // Stale response (a newer load started meanwhile): drop it.
        if (fetchSeq !== recipFetchSeqRef.current) return;
        recipCursorStackRef.current[pageToLoad] = data.nextCursor || null;
        setRecipientsData(data.recipients || []);
        setMetrics(data.metrics || null);
        setRecipTotalCount(data.totalCount || (data.recipients || []).length);
        setRecipHasMore(data.hasMore || false);
      }
    } catch (err) {
      console.error('Failed to load recipients:', err);
    } finally {
      if (fetchSeq === recipFetchSeqRef.current) {
        setLoadingRecipients(false);
      }
    }
  };

  // Refresh the open request keeping the current recipient view state.
  const handleSelectRequest = async (req: any, pageToLoad = recipPage) => {
    setSelectedBulkReq(req);
    setViewResponseRecipient(null);
    setEditDueDate('');
    await loadRecipients(req, pageToLoad, { status: statusFilter, search: recipSearch, sort: recipSortBy });
  };

  // Open a request from the list (FIX-1311 D6): the recipient search,
  // status filter, sort and page all reset, so the detail view never
  // inherits the previous request's view state.
  const selectBulkRequest = (req: any) => {
    setRecipSearch('');
    setStatusFilter('All');
    setRecipSortBy('sent_desc');
    setRecipPage(1);
    recipCursorStackRef.current = [null];
    setSelectedBulkReq(req);
    setViewResponseRecipient(null);
    setEditDueDate('');
    void loadRecipients(req, 1, { status: 'All', search: '', sort: 'sent_desc' });
  };

  React.useEffect(() => {
    if (selectedBulkReq) {
      void loadRecipients(selectedBulkReq, recipPage, { status: statusFilter, search: recipSearch, sort: recipSortBy });
    }
  }, [selectedBulkReq?.bulkRequestId, recipPage, statusFilter, recipSearch, recipSortBy]);

  // Poll background job status for requests that carry a jobId
  React.useEffect(() => {
    const activeJobs = bulkRequests.filter(br => br.jobId && !['completed', 'completed_with_errors', 'failed', 'cancelled'].includes(jobProgressMap[br.jobId]?.status));
    if (activeJobs.length === 0) return;

    const interval = setInterval(async () => {
      for (const br of activeJobs) {
        try {
          const res = await safeFetch(`/api/admin/bulk-jobs/${br.jobId}`);
          // FIX-1311 (D3): the endpoint answers { success, job } — the
          // status and progress fields live on the nested job object.
          const progress = extractBulkJobProgress(res);
          if (progress) {
            setJobProgressMap(prev => ({
              ...prev,
              [br.jobId]: progress
            }));

            // Terminal status toast emission (once per job)
            if (['completed', 'completed_with_errors', 'failed'].includes(progress.status) && !watchedJobsRef.current.has(br.jobId)) {
              watchedJobsRef.current.add(br.jobId);
              const toast = buildBulkJobTerminalToast(progress, br.title);
              if (toast) notifyToast(toast.type, toast.message);
              fetchBulkRequests(bulkReqPage);
            }
          }
        } catch (_) {}
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [bulkRequests, jobProgressMap, bulkReqPage, fetchBulkRequests]);

  const handleCancelBulkRequest = async () => {
    if (!selectedBulkReq) return;
    if (!(await confirmDialog(`Are you sure you want to cancel the bulk request "${selectedBulkReq.title}"? This will cancel all pending individual requests.`))) return;

    setIsCancelling(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`/api/admin/bulk-requests/${selectedBulkReq.bulkRequestId}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() }
      });
      if (res.ok) {
        notifyToast('success', 'Bulk request successfully cancelled!');
        setSelectedBulkReq(prev => ({ ...prev, status: 'Cancelled' }));
        handleSelectRequest({ ...selectedBulkReq, status: 'Cancelled' }, recipPage);
        fetchBulkRequests(bulkReqPage);
      } else {
        const err = await res.json().catch(() => ({}));
        notifyToast('error', 'Failed to cancel request: ' + (err.error || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      notifyToast('error', 'Failed to cancel bulk request.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleUpdateDeadline = async () => {
    if (!selectedBulkReq || !editDueDate) return;
    if (!(await confirmDialog('Are you sure you want to update the deadline? This will apply to both parent request and all uncompleted child requests.'))) return;

    setIsUpdatingDeadline(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`/api/admin/bulk-requests/${selectedBulkReq.bulkRequestId}/update-deadline`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...workspaceHeader()
        },
        body: JSON.stringify({ dueDate: new Date(editDueDate).toISOString() })
      });
      if (res.ok) {
        notifyToast('success', 'Deadline successfully updated!');
        setSelectedBulkReq(prev => ({ ...prev, dueDate: new Date(editDueDate).toISOString() }));
        handleSelectRequest({ ...selectedBulkReq, dueDate: new Date(editDueDate).toISOString() }, recipPage);
        setEditDueDate('');
      } else {
        const err = await res.json().catch(() => ({}));
        notifyToast('error', 'Failed to update deadline: ' + (err.error || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      notifyToast('error', 'Failed to update deadline.');
    } finally {
      setIsUpdatingDeadline(false);
    }
  };

  const handleExportCSV = async () => {
    if (!selectedBulkReq) return;
    try {
      const res = await safeFetch(`/api/admin/bulk-requests/${selectedBulkReq.bulkRequestId}/recipients?all=true`);
      const allRecipients = res.recipients || [];
      if (allRecipients.length === 0) {
        notifyToast('error', 'No recipient data available to export.');
        return;
      }

      const { csvHeaders, csvRows } = buildDynamicFormCsvRows(selectedBulkReq, allRecipients);
      const csvContent = [csvHeaders.join(','), ...csvRows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `export_bulk_request_${selectedBulkReq.title.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      notifyToast('success', `Exported ${allRecipients.length} recipients to CSV`);
    } catch (err) {
      console.error('CSV export failed:', err);
      notifyToast('error', 'Failed to export CSV');
    }
  };

  const handleCreateBulkRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim() || !dueDate) {
      notifyToast('error', 'Please fill in title, description, and due date.');
      return;
    }

    const dropdownOptions = (responseType === 'dropdown' || (responseType === 'form' && bulkFormTextType === 'dropdown'))
      ? dropdownOptionsInput.split('\n').map(o => o.trim()).filter(Boolean)
      : [];

    const requiredDocs = (responseType === 'file_upload' || responseType === 'form')
      ? requiredDocsInput.split('\n').map(d => d.trim()).filter(Boolean)
      : [];

    const formFields = responseType === 'form'
      ? (Array.isArray(bulkFormFields) ? bulkFormFields : [])
      : [
          {
            id: `field_${Date.now()}`,
            label: title.trim(),
            type: responseType,
            required: true,
            instructions: instructions.trim() || undefined,
            options: dropdownOptions.length > 0 ? dropdownOptions : undefined
          }
        ];

    if (responseType === 'dropdown' && dropdownOptions.length === 0) {
      notifyToast('error', 'Please specify at least one dropdown option.');
      return;
    }

    setSubmitting(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/bulk-requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...workspaceHeader()
        },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          instructions: instructions.trim() || undefined,
          responseType,
          priority,
          dueDate: new Date(dueDate).toISOString(),
          batchId,
          dropdownOptions,
          requiredDocs,
          textResponseType: responseType === 'form' ? bulkFormTextType : null,
          formFields,
          notifyAdminOnCompletion: bulkNotifyAdmin
        })
      });

      if (res.ok) {
        const data = await res.json();
        notifyToast('success', `Bulk request is sending in the background to ${data.recipientsCount || batchMembers.length} applicants.`);
        setShowCreateModal(false);
        // Clear fields
        setTitle('');
        setDescription('');
        setInstructions('');
        setResponseType('short_text');
        setPriority('Medium');
        setDueDate('');
        setDropdownOptionsInput('');
        setRequiredDocsInput('');
        setBulkFormFields([]);
        setBulkNotifyAdmin(false);
        listCursorStackRef.current = [null];
        setBulkReqPage(1);
        fetchBulkRequests(1);
      } else {
        const err = await res.json().catch(() => ({}));
        notifyToast('error', 'Failed to create: ' + (err.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Create error:', err);
      notifyToast('error', 'An unexpected error occurred while creating bulk request.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendBulkReminder = async () => {
    if (!selectedBulkReq) return;
    if (!(await confirmDialog('Are you sure you want to send reminders to all pending recipients? This will skip anyone who has already responded.'))) return;

    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`/api/admin/bulk-requests/${selectedBulkReq.bulkRequestId}/remind-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() }
      });
      if (res.ok) {
        const data = await res.json();
        notifyToast('success', `Bulk reminder job queued for ${data.remindersSentCount || data.recipientsCount || 0} pending recipients!`);
        handleSelectRequest(selectedBulkReq, recipPage);
      } else {
        const err = await res.json().catch(() => ({}));
        notifyToast('error', 'Failed to send bulk reminders: ' + (err.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Bulk reminder error:', err);
      notifyToast('error', 'Failed to send bulk reminders.');
    }
  };

  const handleSendIndividualReminder = async (recipient: any) => {
    if (!(await confirmDialog(`Send reminder to ${recipient.applicantName}?`))) return;

    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch(`/api/admin/special-requests/${recipient.requestId}/reminder`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() }
      });
      if (res.ok) {
        notifyToast('success', 'Reminder sent!');
        handleSelectRequest(selectedBulkReq, recipPage);
      } else {
        const err = await res.json().catch(() => ({}));
        notifyToast('error', 'Failed to send reminder: ' + (err.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Individual reminder error:', err);
      notifyToast('error', 'Failed to send reminder.');
    }
  };

  // FIX-1311 (D6): the old filteredRecipients memo filtered and sorted
  // the already-paged rows client-side, so its count disagreed with the
  // server total and its sorting never matched the rendered order. The
  // rows now render exactly as the server paged them (recipientsData),
  // and the header count is the server total (recipTotalCount).

  const bulkFiltersActive = Boolean(bulkReqSearch || bulkReqStatusFilter !== 'All' || bulkReqDateFrom || bulkReqDateTo);

  // The full-section spinner is for the FIRST load only. Refetches
  // (every search keystroke, filter change, background refresh) keep
  // the mounted UI in place so the controls the user is touching can
  // never disappear mid-interaction.
  if (loading && !hasLoadedOnceRef.current) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400">
        <Loader className="h-6 w-6 animate-spin mr-2" />
        <span className="text-sm font-semibold">Loading bulk requests...</span>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
      {/* Detail view of a selected Bulk Request */}
      {selectedBulkReq ? (
        <div className="space-y-6">
          <div className="flex items-center space-x-3 border-b border-slate-200 pb-4">
            <button
              onClick={() => setSelectedBulkReq(null)}
              className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition cursor-pointer"
              title="Back to Bulk Requests list"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded ${
                  selectedBulkReq.priority === 'High' ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-slate-100 text-slate-600'
                }`}>
                  {selectedBulkReq.priority} Priority
                </span>
                <span className="text-caption font-mono text-slate-400">Created: {new Date(selectedBulkReq.createdAt).toLocaleDateString()}</span>
                {selectedBulkReq.status === 'Cancelled' && (
                  <span className="text-[11px] font-bold uppercase px-2 py-0.5 rounded bg-rose-50 text-rose-600 border border-rose-100">Cancelled</span>
                )}
              </div>
              <h4 className="text-sm font-bold text-slate-900 truncate mt-1">{selectedBulkReq.title}</h4>
            </div>
            <button
              onClick={handleSendBulkReminder}
              disabled={selectedBulkReq.status === 'Cancelled'}
              className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white rounded-lg text-caption font-bold flex items-center gap-1.5 shadow-sm transition cursor-pointer"
            >
              <Clock className="h-3.5 w-3.5" />
              <span>Send Bulk Reminder</span>
            </button>
          </div>

          {/* Admin Actions Panel (Deadline & Cancellation) */}
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4 flex-1">
              {/* Deadline editor */}
              <div className="flex items-center space-x-2">
                <span className="text-caption font-bold text-slate-500 uppercase">Deadline:</span>
                <span className="text-caption font-semibold text-slate-700 font-mono">
                  {new Date(selectedBulkReq.dueDate).toLocaleString()}
                </span>
                {selectedBulkReq.status !== 'Cancelled' && (
                  <div className="flex items-center space-x-1 pl-2">
                    <input
                      type="datetime-local"
                      value={editDueDate}
                      onChange={e => setEditDueDate(e.target.value)}
                      className="bg-white border border-slate-200 rounded px-2 py-1 text-caption font-medium outline-none focus:border-brand"
                    />
                    <button
                      onClick={handleUpdateDeadline}
                      disabled={isUpdatingDeadline || !editDueDate}
                      className="px-2.5 py-1.5 bg-brand text-white text-caption font-bold rounded hover:bg-brand-active transition cursor-pointer disabled:opacity-40"
                    >
                      Update
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Cancellation trigger */}
            {selectedBulkReq.status !== 'Cancelled' && (
              <button
                onClick={handleCancelBulkRequest}
                disabled={isCancelling}
                className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white text-caption font-bold rounded-lg shadow-sm transition flex items-center gap-1 cursor-pointer"
              >
                <XCircle className="h-3.5 w-3.5" />
                <span>Cancel Bulk Request</span>
              </button>
            )}
          </div>

          <div className="space-y-6">
              {/* Metrics cards */}
              {metrics && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                  <div className="bg-slate-50 border border-slate-200 p-3.5 rounded-xl text-center">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Recipients</p>
                    <p className="text-lg font-black text-slate-800 mt-0.5">{metrics.total}</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 p-3.5 rounded-xl text-center">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Pending Response</p>
                    <p className="text-lg font-black text-amber-600 mt-0.5">{metrics.pending}</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 p-3.5 rounded-xl text-center">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Responded</p>
                    <p className="text-lg font-black text-emerald-600 mt-0.5">{metrics.responded}</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 p-3.5 rounded-xl text-center">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Completed</p>
                    <p className="text-lg font-black text-emerald-600 mt-0.5">{metrics.completed}</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 p-3.5 rounded-xl text-center">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Response Rate</p>
                    <p className="text-lg font-black text-brand mt-0.5">{metrics.responseRate}%</p>
                  </div>
                </div>
              )}

              {/* Recipient Filters & Table */}
              <div className="space-y-3">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-slate-100 pb-2">
                  <span className="text-xs font-bold text-slate-700 uppercase">Recipients Listing ({recipTotalCount})</span>

                  <div className="flex flex-wrap items-center gap-2 text-caption">
                    {/* Search Input */}
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-slate-400" />
                      <input
                        type="search"
                        placeholder="Search recipient..."
                        value={recipSearch}
                        onChange={e => { setRecipSearch(e.target.value); recipCursorStackRef.current = [null]; setRecipPage(1); }}
                        className="bg-white border border-slate-200 rounded-lg pl-8 pr-2.5 py-1.5 text-caption font-medium outline-none focus:border-brand w-44"
                      />
                    </div>

                    {/* Sorting */}
                    <select
                      value={recipSortBy}
                      onChange={e => { setRecipSortBy(e.target.value as any); recipCursorStackRef.current = [null]; setRecipPage(1); }}
                      className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-caption font-bold outline-none text-slate-700 cursor-pointer"
                    >
                      <option value="sent_desc">Sent (Newest)</option>
                      <option value="sent_asc">Sent (Oldest)</option>
                      <option value="status_asc">Status (A-Z)</option>
                      <option value="status_desc">Status (Z-A)</option>
                    </select>

                    {/* Status filtering */}
                    <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                      <button onClick={() => { setStatusFilter('All'); recipCursorStackRef.current = [null]; setRecipPage(1); }} className={`px-2 py-1 text-[10px] font-bold rounded-md ${statusFilter === 'All' ? 'bg-white text-slate-800 shadow-3xs' : 'text-slate-500 hover:text-slate-800'}`}>All</button>
                      <button onClick={() => { setStatusFilter('Pending'); recipCursorStackRef.current = [null]; setRecipPage(1); }} className={`px-2 py-1 text-[10px] font-bold rounded-md ${statusFilter === 'Pending' ? 'bg-white text-slate-900 shadow-3xs' : 'text-slate-500 hover:text-slate-800'}`}>Pending</button>
                      <button onClick={() => { setStatusFilter('Completed'); recipCursorStackRef.current = [null]; setRecipPage(1); }} className={`px-2 py-1 text-[10px] font-bold rounded-md ${statusFilter === 'Completed' ? 'bg-white text-slate-900 shadow-3xs' : 'text-slate-500 hover:text-slate-800'}`}>Completed</button>
                    </div>

                    {/* Export to CSV */}
                    <button
                      onClick={handleExportCSV}
                      className="px-3 py-1.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white rounded-lg text-caption font-bold flex items-center gap-1 shadow-sm transition cursor-pointer"
                      title="Export recipients and responses as CSV"
                    >
                      <Download className="h-3.5 w-3.5" />
                      <span>Export CSV</span>
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white shadow-3xs">
                  {(() => {
                    return (
                      <>
                        <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200 text-caption font-semibold text-slate-600">
                          <span>Showing {((recipPage - 1) * RECIP_PER_PAGE) + 1}–{Math.min(recipPage * RECIP_PER_PAGE, recipTotalCount)} of {recipTotalCount} recipients</span>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              disabled={recipPage <= 1}
                              onClick={() => setRecipPage(p => Math.max(1, p - 1))}
                              className="px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                            >
                              <ChevronLeft className="h-3 w-3 inline" /> Prev
                            </button>
                            <span>Page {recipPage} of {Math.ceil(recipTotalCount / RECIP_PER_PAGE) || 1}</span>
                            <button
                              type="button"
                              disabled={!recipHasMore}
                              onClick={() => setRecipPage(p => p + 1)}
                              className="px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                            >
                              Next <ChevronRight className="h-3 w-3 inline" />
                            </button>
                          </div>
                        </div>
                        <table className="w-full text-xs text-left border-collapse">
                          <thead>
                            <tr className="bg-slate-50 text-[10px] uppercase font-bold text-slate-500 border-b border-slate-200">
                              <th className="py-2.5 px-4">Applicant</th>
                              <th className="py-2.5 px-4">Status</th>
                              <th className="py-2.5 px-4">Request Sent</th>
                              <th className="py-2.5 px-4">Response Date</th>
                              <th className="py-2.5 px-4">Reminders</th>
                              <th className="py-2.5 px-4 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {loadingRecipients && recipientsData.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="py-8 text-center text-slate-400">
                                  <Loader className="h-4 w-4 animate-spin inline mr-2" />
                                  <span className="font-semibold">Loading recipients...</span>
                                </td>
                              </tr>
                            ) : recipientsData.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="py-8 text-center text-slate-500 italic">No recipients found under this filter.</td>
                              </tr>
                            ) : (
                              recipientsData.map(rec => {
                          const isCompleted = rec.status === 'Submitted' || rec.status === 'Completed';
                          const app = batchMembers.find(m =>
                            m.applicationId === rec.applicationId ||
                            m.applicationId === rec.applicantId ||
                            m.id === rec.applicantId ||
                            m.applicantId === rec.applicantId ||
                            m.userEmail === rec.applicantEmail
                          ) || {
                            id: rec.applicantId,
                            applicationId: rec.applicationId || rec.applicantId,
                            userEmail: rec.applicantEmail,
                            personalDetails: {
                              firstName: rec.applicantName?.split(' ')[0] || '',
                              lastName: rec.applicantName?.split(' ').slice(1).join(' ') || '',
                            },
                          };
                          const displayAppId = rec.applicationId || app.applicationId || rec.applicantId;

                          return (
                            <tr key={rec.requestId} className="hover:bg-slate-50">
                              <td className="py-3 px-4">
                                {onViewApplicant ? (
                                  <div className="space-y-0.5">
                                    <div>
                                      <button
                                        type="button"
                                        onClick={() => onViewApplicant(app)}
                                        className="font-extrabold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left block"
                                        title="Click to view application details"
                                      >
                                        {rec.applicantName}
                                      </button>
                                    </div>
                                    <div>
                                      <button
                                        type="button"
                                        onClick={() => onViewApplicant(app)}
                                        className="text-[10px] text-blue-500 hover:text-blue-700 hover:underline font-semibold cursor-pointer text-left block"
                                        title="Click to view application details"
                                      >
                                        {rec.applicantEmail}
                                      </button>
                                    </div>
                                    <div className="text-[11px] text-slate-500 font-mono">
                                      App ID: <span className="font-semibold text-slate-600">{displayAppId}</span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-0.5">
                                    <div className="font-extrabold text-slate-800">{rec.applicantName}</div>
                                    <div className="text-[11px] text-slate-500 font-semibold">{rec.applicantEmail}</div>
                                    <div className="text-[11px] text-slate-500 font-mono">
                                      App ID: <span className="font-semibold text-slate-600">{displayAppId}</span>
                                    </div>
                                  </div>
                                )}
                              </td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-0.5 text-[10px] font-bold rounded-lg ${
                                  isCompleted ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-amber-50 text-amber-700 border border-amber-100'
                                }`}>
                                  {isCompleted ? 'Responded' : 'Pending Response'}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-slate-500 font-mono text-caption">{new Date(rec.createdAt).toLocaleString()}</td>
                              <td className="py-3 px-4 text-slate-500 font-mono text-caption">{rec.uploadedAt ? new Date(rec.uploadedAt).toLocaleString() : '—'}</td>
                              <td className="py-3 px-4 text-slate-500 text-caption font-semibold">
                                {rec.reminderCount} sent
                                {rec.lastReminderSentAt && (
                                  <span className="text-[11px] text-slate-500 block font-normal">Last: {new Date(rec.lastReminderSentAt).toLocaleDateString()}</span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-right whitespace-nowrap space-x-1.5">
                                {isCompleted ? (
                                  <button
                                    onClick={() => setViewResponseRecipient(rec)}
                                    className="px-2 py-1 bg-brand text-white rounded text-[10px] font-bold hover:bg-brand-active transition"
                                  >
                                    View Response
                                  </button>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleSendIndividualReminder(rec)}
                                      className="px-2 py-1 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded text-[10px] font-bold transition"
                                    >
                                      Remind
                                    </button>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })
                            )}
                          </tbody>
                        </table>
                      </>
                    );
                  })()}
                </div>
              </div>
          </div>
        </div>
      ) : (
        /* List view of existing Bulk Requests for the batch */
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center">
              <FileText className="h-4 w-4 mr-1.5 text-brand" />
              Batch Bulk Requests Listing
            </h4>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-3 py-1.5 btn-primary text-white rounded-lg text-caption font-bold flex items-center gap-1 cursor-pointer"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Create Bulk Request</span>
            </button>
          </div>

          {listError && bulkRequests.length === 0 && !bulkFiltersActive ? (
            <div className="py-12 bg-rose-50/60 rounded-xl border border-rose-200 text-center text-rose-500 px-4">
              <AlertTriangle className="h-8 w-8 mx-auto text-rose-300 mb-2" />
              <p className="text-xs font-bold text-rose-600">Could not load bulk requests</p>
              <p className="text-[11px] text-rose-400 mt-1 break-words">{listError}</p>
              <button
                type="button"
                onClick={() => { listCursorStackRef.current = [null]; setBulkReqPage(1); void fetchBulkRequests(1); }}
                className="mt-3 px-3 py-1.5 bg-white border border-rose-200 hover:bg-rose-50 text-rose-600 rounded-lg text-caption font-bold cursor-pointer"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm space-y-0">
              {/* A refetch error never replaces the card: the search and
                  filter controls stay mounted and the last rows stay
                  visible under an inline banner. */}
              {listError && (
                <div className="px-4 py-2.5 bg-rose-50 border-b border-rose-200 flex items-center justify-between gap-3">
                  <span className="text-[11px] font-semibold text-rose-600 break-words">{listError}</span>
                  <button
                    type="button"
                    onClick={() => { listCursorStackRef.current = [null]; setBulkReqPage(1); void fetchBulkRequests(1); }}
                    className="px-3 py-1 bg-white border border-rose-200 hover:bg-rose-100 text-rose-600 rounded-lg text-caption font-bold cursor-pointer shrink-0"
                  >
                    Retry
                  </button>
                </div>
              )}
              {/* Search & Filter Bar — always rendered, so searching can
                  never unmount the controls being typed into. */}
                  <div className="p-4 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[200px]">
                      <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
                      <input
                        type="search"
                        value={bulkReqSearch}
                        onChange={e => { setBulkReqSearch(e.target.value); listCursorStackRef.current = [null]; setBulkReqPage(1); }}
                        placeholder="Search requests by title or description..."
                        className="w-full pl-9 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-xl outline-none focus:border-brand font-medium text-slate-800"
                      />
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-bold text-slate-500">Status:</span>
                      <select
                        value={bulkReqStatusFilter}
                        onChange={e => { setBulkReqStatusFilter(e.target.value as any); listCursorStackRef.current = [null]; setBulkReqPage(1); }}
                        className="bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 font-bold text-slate-700 outline-none"
                      >
                        <option value="All">All Statuses</option>
                        <option value="Active">Active</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-bold text-slate-500">Sent From:</span>
                      <input
                        type="date"
                        value={bulkReqDateFrom}
                        onChange={e => { setBulkReqDateFrom(e.target.value); listCursorStackRef.current = [null]; setBulkReqPage(1); }}
                        className="bg-white border border-slate-200 rounded-lg px-2 py-1 font-semibold text-slate-700 outline-none"
                      />
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-bold text-slate-500">To:</span>
                      <input
                        type="date"
                        value={bulkReqDateTo}
                        onChange={e => { setBulkReqDateTo(e.target.value); listCursorStackRef.current = [null]; setBulkReqPage(1); }}
                        className="bg-white border border-slate-200 rounded-lg px-2 py-1 font-semibold text-slate-700 outline-none"
                      />
                    </div>
                    {(bulkReqSearch || bulkReqStatusFilter !== 'All' || bulkReqDateFrom || bulkReqDateTo) && (
                      <button
                        onClick={() => { setBulkReqSearch(''); setBulkReqStatusFilter('All'); setBulkReqDateFrom(''); setBulkReqDateTo(''); listCursorStackRef.current = [null]; setBulkReqPage(1); }}
                        className="text-xs font-bold text-rose-600 hover:text-rose-800 cursor-pointer"
                      >
                        Clear Filters
                      </button>
                    )}
                  </div>

                  {bulkRequests.length === 0 ? (
                    loading ? (
                      // A refetch is still in flight with no rows held
                      // (e.g. the fetch right after Clear Filters): show a
                      // neutral loading row. The created-yet copy below is
                      // only true once the list has SETTLED unfiltered and
                      // empty — flashing it during the refetch wrongly
                      // claims nothing was ever created.
                      <div className="p-8 text-center text-slate-400 font-semibold text-xs">
                        <Loader className="h-4 w-4 animate-spin inline mr-2" />
                        Loading bulk requests...
                      </div>
                    ) : bulkFiltersActive ? (
                      <div className="p-8 text-center text-slate-400 font-semibold text-xs">
                        No bulk requests match your search and filter criteria.
                      </div>
                    ) : (
                      <div className="py-12 text-center text-slate-400">
                        <FileText className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                        <p className="text-xs font-bold">No Bulk Requests have been created for this cohort yet.</p>
                        <p className="text-[11px] text-slate-500 mt-1">Configure one global request for all members in this batch.</p>
                      </div>
                    )
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs text-left border-collapse">
                        <thead>
                          <tr className="bg-slate-50 border-b border-slate-200 text-slate-400 uppercase font-bold text-[10px] tracking-wider">
                            <th className="py-3 px-4">Title / Request Name</th>
                            <th className="py-3 px-4">Format</th>
                            <th className="py-3 px-4">Priority</th>
                            <th className="py-3 px-4">Date Sent</th>
                            <th className="py-3 px-4">Due Date</th>
                            <th className="py-3 px-4">Status / Progress</th>
                            <th className="py-3 px-4 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {bulkRequests.map(br => {
                            const jobProg = br.jobId ? jobProgressMap[br.jobId] : null;
                            return (
                              <tr key={br.bulkRequestId} className="hover:bg-slate-50 transition cursor-pointer" onClick={() => selectBulkRequest(br)}>
                                <td className="py-3.5 px-4 font-bold text-slate-900 max-w-[220px] truncate" title={br.title}>
                                  {br.title}
                                </td>
                                <td className="py-3.5 px-4 font-semibold text-slate-600">
                                  <span className="px-2 py-0.5 rounded bg-slate-100 font-mono text-[10px]">{br.responseType}</span>
                                </td>
                                <td className="py-3.5 px-4">
                                  <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                                    br.priority === 'High' ? 'bg-red-50 text-red-600 border border-red-100' : 'bg-slate-100 text-slate-600'
                                  }`}>
                                    {br.priority || 'Medium'}
                                  </span>
                                </td>
                                <td className="py-3.5 px-4 text-slate-500 font-mono">{br.createdAt ? new Date(br.createdAt).toLocaleDateString() : '—'}</td>
                                <td className="py-3.5 px-4 text-slate-500 font-mono">{br.dueDate ? new Date(br.dueDate).toLocaleDateString() : '—'}</td>
                                <td className="py-3.5 px-4">
                                  <div className="flex flex-col gap-1">
                                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded inline-block w-fit ${
                                      br.status === 'Cancelled' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'
                                    }`}>
                                      {br.status || 'Active'}
                                    </span>
                                    {jobProg && jobProg.status !== 'completed' && (
                                      <div className="flex flex-col gap-0.5 text-[10px]">
                                        <span className="font-semibold text-indigo-700">Sending: {jobProg.progressPercent}% ({jobProg.completedItems}/{jobProg.totalItems})</span>
                                        <div className="w-24 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                                          <div className="bg-brand-primary h-1.5 rounded-full transition-all duration-300" style={{ width: `${jobProg.progressPercent}%` }} />
                                        </div>
                                        <a
                                          href={`/admin?tab=bulk-jobs&jobId=${br.jobId}`}
                                          onClick={(e) => e.stopPropagation()}
                                          className="text-[10px] font-bold text-indigo-600 hover:underline"
                                        >
                                          View Job Details
                                        </a>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="py-3.5 px-4 text-right">
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); selectBulkRequest(br); }}
                                    className="px-3 py-1 btn-primary text-white text-caption font-bold rounded-lg transition cursor-pointer"
                                  >
                                    Manage Recipients
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Pagination footer */}
                  {bulkReqTotalCount > 0 && (
                  <div className="p-4 border-t border-slate-100 flex items-center justify-between text-caption text-slate-500 font-semibold">
                    <span>Showing {((bulkReqPage - 1) * BULK_REQ_PER_PAGE) + 1}–{Math.min(bulkReqPage * BULK_REQ_PER_PAGE, bulkReqTotalCount)} of {bulkReqTotalCount} requests</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={bulkReqPage <= 1}
                        onClick={() => setBulkReqPage(p => Math.max(1, p - 1))}
                        className="px-3 py-1 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                      >
                        <ChevronLeft className="h-3 w-3 inline" /> Prev
                      </button>
                      <span>Page {bulkReqPage} of {Math.ceil(bulkReqTotalCount / BULK_REQ_PER_PAGE) || 1}</span>
                      <button
                        type="button"
                        disabled={!bulkReqHasMore}
                        onClick={() => setBulkReqPage(p => p + 1)}
                        className="px-3 py-1 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 transition disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                      >
                        Next <ChevronRight className="h-3 w-3 inline" />
                      </button>
                    </div>
                  </div>
                  )}
            </div>
          )}
        </div>
      )}

      {/* Creation Modal */}
      {showCreateModal && (
        <Modal title="Create Cohort Bulk Request">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden text-slate-800 text-xs">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-200 shrink-0 flex items-center justify-between">
              <div>
                <h4 className="text-sm font-black text-slate-900">Create Cohort Bulk Request</h4>
                <p className="text-[11px] text-slate-500 mt-0.5">This will send an individual request to every applicant in the cohort.</p>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition"><X className="h-5 w-5" /></button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleCreateBulkRequest} className="flex-1 overflow-y-auto p-6 space-y-4">
              <FormTemplatePickerBar
                onSelectTemplate={(tmpl) => {
                  if (tmpl.title) setTitle(tmpl.title);
                  if (tmpl.description) setDescription(tmpl.description);
                  if (tmpl.instructions) setInstructions(tmpl.instructions);
                  if (tmpl.responseType) setResponseType(tmpl.responseType as any);
                  if (tmpl.priority) setPriority(tmpl.priority as any);
                  if (tmpl.dropdownOptions) setDropdownOptionsInput(tmpl.dropdownOptions.join('\n'));
                  if (tmpl.requiredDocs) setRequiredDocsInput(tmpl.requiredDocs.join('\n'));
                  if (tmpl.formFields) setBulkFormFields(tmpl.formFields);
                }}
                getCurrentFormData={() => ({
                  title: title || '',
                  description: description || '',
                  instructions: instructions || '',
                  responseType: responseType || 'short_text',
                  priority: priority || 'Medium',
                  dropdownOptions: dropdownOptionsInput ? dropdownOptionsInput.split('\n').map(s => s.trim()).filter(Boolean) : [],
                  requiredDocs: requiredDocsInput ? requiredDocsInput.split('\n').map(s => s.trim()).filter(Boolean) : [],
                  formFields: bulkFormFields || []
                })}
              />

              <div className="space-y-1">
                <label className="text-caption uppercase font-bold text-slate-500 block">Request Title / Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Visa Interview Form DS-160 / Flight Ticket Request"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg p-2.5 text-xs outline-none focus:border-brand font-medium"
                />
              </div>

              <div className="space-y-1">
                <label className="text-caption uppercase font-bold text-slate-500 block">Request Description *</label>
                <textarea
                  required
                  rows={3}
                  placeholder="Clear instructions explaining exactly what is being requested..."
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg p-2.5 text-xs outline-none focus:border-brand font-medium leading-relaxed"
                />
              </div>

              <div className="space-y-1">
                <label className="text-caption uppercase font-bold text-slate-500 block">Additional Administrative Notes</label>
                <input
                  type="text"
                  placeholder="Optional internal workflow notes or guidelines..."
                  value={instructions}
                  onChange={e => setInstructions(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg p-2.5 text-xs outline-none focus:border-brand font-medium"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-caption uppercase font-bold text-slate-500 block">Response Type *</label>
                  <select
                    value={responseType}
                    onChange={e => {
                      setResponseType(e.target.value as any);
                      setDropdownOptionsInput('');
                      setRequiredDocsInput('');
                    }}
                    className="w-full border border-slate-200 rounded-lg p-2.5 font-bold bg-slate-50 text-slate-700 text-xs"
                  >
                    <option value="short_text">Single-Line Text</option>
                    <option value="long_text">Multi-Line Text</option>
                    <option value="date">Date Input</option>
                    <option value="dropdown">Dropdown Selection</option>
                    <option value="file_upload">File Upload</option>
                    <option value="form">Form (Text + File Upload)</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-caption uppercase font-bold text-slate-500 block">Priority Level *</label>
                  <select
                    value={priority}
                    onChange={e => setPriority(e.target.value as any)}
                    className="w-full border border-slate-200 rounded-lg p-2.5 font-bold bg-slate-50 text-slate-700 text-xs"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>
              </div>

              {responseType === 'dropdown' && (
                <div className="space-y-1 animate-slideDown">
                  <label className="text-caption uppercase font-bold text-slate-500 block">Dropdown Options * <span className="text-slate-400 font-normal normal-case">(One per line)</span></label>
                  <textarea
                    required
                    rows={3}
                    placeholder="e.g.&#10;Option 1&#10;Option 2&#10;Option 3"
                    value={dropdownOptionsInput}
                    onChange={e => setDropdownOptionsInput(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg p-2.5 text-xs font-mono outline-none focus:border-brand"
                  />
                </div>
              )}

              {responseType === 'form' && (
                <div className="space-y-3 p-3 bg-slate-50 border border-slate-200 rounded-xl animate-slideDown">
                  <p className="text-caption text-slate-900 font-bold">Custom Form Builder Configuration</p>
                  <FormBuilder
                    fields={bulkFormFields}
                    onChange={(updated) => setBulkFormFields(updated)}
                  />
                </div>
              )}

              {responseType === 'file_upload' && (
                <div className="space-y-1 animate-slideDown">
                  <label className="text-caption uppercase font-bold text-slate-500 block">Required File Slot Labels <span className="text-slate-400 font-normal normal-case">(Optional, One per line)</span></label>
                  <textarea
                    rows={3}
                    placeholder="e.g.&#10;Signed Agreement&#10;Copy of Passport"
                    value={requiredDocsInput}
                    onChange={e => setRequiredDocsInput(e.target.value)}
                    className="w-full border border-slate-200 rounded-lg p-2.5 text-xs font-mono outline-none focus:border-brand"
                  />
                  <p className="text-[11px] text-slate-500">If left blank, applicants can upload any number of arbitrary files.</p>
                </div>
              )}

              <div className="space-y-1">
                <label className="text-caption uppercase font-bold text-slate-500 block">Due Date & Time *</label>
                <input
                  type="datetime-local"
                  required
                  value={dueDate}
                  onChange={e => setDueDate(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg p-2.5 text-xs outline-none focus:border-brand font-medium"
                />
              </div>

              {/* Email completion notification toggle */}
              <div className="pt-2 border-t border-slate-200 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-800">Email Notification on Completion</p>
                  <p className="text-[11px] text-slate-500">Receive an instant email confirmation when each applicant completes the request.</p>
                </div>
                <input
                  type="checkbox"
                  checked={bulkNotifyAdmin}
                  onChange={e => setBulkNotifyAdmin(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer"
                />
              </div>

              {/* Modal Footer Controls */}
              <div className="pt-4 border-t border-slate-200 flex justify-end gap-2.5 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  disabled={submitting}
                  className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 font-bold transition text-slate-500 cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 btn-primary text-white rounded-xl font-bold transition flex items-center gap-1 cursor-pointer disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <Loader className="h-3.5 w-3.5 animate-spin" />
                      <span>Sending requests ({batchMembers.length})...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="h-3.5 w-3.5" />
                      <span>Issue Bulk Request ({batchMembers.length} emails)</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
      </Modal>
      )}

      {/* View Response Modal */}
      {viewResponseRecipient && (
        <Modal title="Applicant Submission Response">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden text-slate-800 text-xs">
            <div className="px-6 py-4 border-b border-slate-200 shrink-0 flex items-center justify-between">
              <div>
                <h4 className="text-sm font-black text-slate-900">Applicant Submission Response</h4>
                <p className="text-[11px] text-slate-500 mt-0.5">Submitted by {viewResponseRecipient.applicantName}</p>
              </div>
              <button onClick={() => setViewResponseRecipient(null)} className="p-1 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition"><X className="h-5 w-5" /></button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4">
              <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200 space-y-1.5">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Requested Information</p>
                <p className="text-xs font-bold text-slate-800">{selectedBulkReq.title}</p>
                <p className="text-xs text-slate-500 leading-relaxed font-medium">{selectedBulkReq.description}</p>
              </div>

              <div className="space-y-1">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Submitted Response Content</p>
                {viewResponseRecipient.responseValue ? (
                  <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-700 leading-relaxed">
                    {renderFormattedResponseValue(viewResponseRecipient.responseValue, selectedBulkReq?.formFields)}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic">No text response submitted.</p>
                )}
              </div>

              {viewResponseRecipient.responseFiles && Array.isArray(viewResponseRecipient.responseFiles) && viewResponseRecipient.responseFiles.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Uploaded Documents</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {viewResponseRecipient.responseFiles.map((file: any, fIdx: number) => (
                      <div key={fIdx} className="bg-white border border-slate-200 p-2.5 rounded-xl flex items-center justify-between text-xs shadow-3xs">
                        <div className="flex items-center space-x-2 min-w-0 flex-1 pr-2">
                          <Paperclip className="h-3.5 w-3.5 text-brand shrink-0" />
                          <span className="font-semibold text-slate-700 truncate" title={file.name}>{file.name}</span>
                          {file.docName && <span className="px-1.5 py-0.5 bg-blue-50 text-brand text-[11px] font-bold rounded-md shrink-0">{file.docName}</span>}
                        </div>
                        <a
                          href={file.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-2.5 py-1 bg-brand text-white rounded-lg text-caption font-bold hover:bg-brand-active transition flex items-center gap-1 shrink-0"
                        >
                          <Eye className="h-3 w-3" />
                          <span>View</span>
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 text-caption font-semibold text-slate-500 border-t border-slate-100 pt-3">
                <div>
                  <p className="text-[11px] font-bold text-slate-400 uppercase">Received Date</p>
                  <p className="mt-0.5 font-mono">{viewResponseRecipient.uploadedAt ? new Date(viewResponseRecipient.uploadedAt).toLocaleDateString() : '—'}</p>
                </div>
                <div>
                  <p className="text-[11px] font-bold text-slate-400 uppercase">Received Time</p>
                  <p className="mt-0.5 font-mono">{viewResponseRecipient.uploadedAt ? new Date(viewResponseRecipient.uploadedAt).toLocaleTimeString() : '—'}</p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 shrink-0 flex justify-end">
              <button
                onClick={() => setViewResponseRecipient(null)}
                className="px-4 py-2 bg-brand hover:bg-brand-active text-white rounded-xl font-bold transition cursor-pointer text-caption"
              >
                Close Response
              </button>
            </div>
          </div>
      </Modal>
      )}
    </div>
  );
}

/* ====================================================================
   BATCH-RELATED EVENTS TAB — shows events linked to batch applicants
   ==================================================================== */
function BatchRelatedEventsSection({
  batchMembers,
  adminEmail,
  onViewApplicant,
}: {
  batchMembers: any[];
  adminEmail: string;
  onViewApplicant?: (app: any) => void;
}) {
  const [relatedEvents, setRelatedEvents] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [showCreateForm, setShowCreateForm] = React.useState(false);

  // Search & pagination state for related events
  const [eventSearch, setEventSearch] = React.useState('');
  const [eventPage, setEventPage] = React.useState(0);
  const EVENTS_PER_PAGE = 50;

  const filteredEvents = React.useMemo(() => {
    return relatedEvents.filter(ev => {
      if (!eventSearch.trim()) return true;
      const q = eventSearch.toLowerCase().trim();
      return (ev.title || '').toLowerCase().includes(q) ||
             (ev.eventType || '').toLowerCase().includes(q) ||
             (ev.status || '').toLowerCase().includes(q);
    });
  }, [relatedEvents, eventSearch]);

  // Create event form state
  const [ceTitle, setCeTitle] = React.useState('');
  const [ceType, setCeType] = React.useState<string>('Orientation');
  const [ceDateTime, setCeDateTime] = React.useState('');
  const [ceLocation, setCeLocation] = React.useState('');
  const [ceDescription, setCeDescription] = React.useState('');
  const [ceStatus, setCeStatus] = React.useState<string>('Scheduled');
  const [ceSaving, setCeSaving] = React.useState(false);
  const [ceNotifyApplicants, setCeNotifyApplicants] = React.useState(true);

  const fetchEvents = React.useCallback(async () => {
    if (batchMembers.length === 0) { setLoading(false); return; }
    const appIds = batchMembers.map(m => m.applicationId);
    const activeWorkspaceId = getActiveWorkspaceId();
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'events')));
      const all: any[] = [];
      snap.forEach(d => {
        const ev = d.data();
        if (ev.workspaceId && activeWorkspaceId && ev.workspaceId !== activeWorkspaceId) return;

        // Check if event is directly assigned to any member or if any member is in invitationList
        const isTargeted = appIds.some(id => ev.applicantId === id || (Array.isArray(ev.invitationList) && ev.invitationList.includes(id)));
        if (isTargeted) {
          all.push({ eventId: d.id, ...ev });
        }
      });
      all.sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime());
      setRelatedEvents(all);
    } catch (err) {
      console.error('Failed to load related events:', err);
    } finally {
      setLoading(false);
    }
  }, [batchMembers]);

  React.useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleCreateEvent = async () => {
    if (!ceTitle.trim() || !ceDateTime || !ceLocation.trim()) {
      notifyToast('warning', 'Please fill in title, date/time, and location.');
      return;
    }
    if (batchMembers.length === 0) {
      notifyToast('warning', 'No candidates in this batch.');
      return;
    }
    if (!(await confirmDialog(`Create "${ceTitle}" as a single group event for all ${batchMembers.length} candidates?`))) return;
    setCeSaving(true);
    try {
      const eventDateTime = new Date(ceDateTime).toISOString();
      const activeWorkspaceId = getActiveWorkspaceId();
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const appIds = batchMembers.map(m => m.id || m.applicationId);

      const payload = {
        title: ceTitle.trim(),
        eventType: ceType,
        dateTime: eventDateTime,
        timezone: userTimezone,
        location: ceLocation.trim(),
        description: ceDescription.trim(),
        status: ceStatus,
        isGroupEvent: true,
        workspaceId: activeWorkspaceId,
        groupEventConfig: {
          sessionDuration: 60,
          bufferBefore: 0,
          bufferAfter: 0,
          isVirtual: ceLocation.trim().toLowerCase().includes('zoom') || ceLocation.trim().toLowerCase().includes('http') || ceLocation.trim().toLowerCase().includes('virtual'),
          meetingDetails: ceLocation.trim(),
          location: ceLocation.trim(),
        },
        slots: [],
      };

      const groupRes = await safeFetch('/api/admin/group-events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const newEventId = groupRes.eventId;

      if (newEventId && appIds.length > 0) {
        await safeFetch(`/api/admin/group-events/${newEventId}/invitations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ applicationIds: appIds }),
        });
        window.dispatchEvent(new CustomEvent('bulk-job-created'));
      }

      notifyToast('success', `Group event "${ceTitle}" created and invitation task queued for ${batchMembers.length} candidates. Track progress in Task Tracker.`);

      setCeTitle(''); setCeDateTime(''); setCeLocation(''); setCeDescription(''); setCeType('Orientation'); setCeStatus('Scheduled');
      setShowCreateForm(false);
      await fetchEvents();
    } catch (err: any) {
      console.error('Failed to create group event:', err);
      notifyToast('error', 'An error occurred while creating group event: ' + (err.message || 'Please try again.'));
    } finally { setCeSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
        <span className="text-sm font-semibold">Loading related events...</span>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
        <h4 className="text-xs font-bold tracking-wider text-slate-900 flex items-center">
          <Calendar className="h-4 w-4 mr-1.5 text-brand" />
          Batch Related Events ({relatedEvents.length})
        </h4>
        <button
          type="button"
          onClick={() => setShowCreateForm(!showCreateForm)}
          className={`px-3 py-1.5 text-caption font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 ${
            showCreateForm ? 'bg-slate-200 text-slate-700' : 'btn-primary  text-white shadow-sm'
          }`}
        >
          <Plus className="h-3.5 w-3.5" />
          <span>{showCreateForm ? 'Cancel' : 'Create Event'}</span>
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3 animate-slideDown">
          <h5 className="text-xs font-extrabold text-slate-900">Create Group Event for All {batchMembers.length} Candidates</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-caption uppercase font-bold text-slate-500">Title *</label>
              <input type="text" className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-brand font-medium"
                placeholder="e.g. Mandatory Orientation" value={ceTitle} onChange={e => setCeTitle(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-caption uppercase font-bold text-slate-500">Type *</label>
              <select value={ceType} onChange={e => setCeType(e.target.value)}
                className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-brand font-bold text-slate-700">
                <option value="Embassy">Embassy Appointment</option>
                <option value="Interview">Interview</option>
                <option value="Orientation">Orientation</option>
                <option value="Training">Training</option>
                <option value="Flight">Flight</option>
                <option value="Medical">Medical</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-caption uppercase font-bold text-slate-500">Date & Time *</label>
              <input type="datetime-local" className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-brand font-medium"
                value={ceDateTime} onChange={e => setCeDateTime(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-caption uppercase font-bold text-slate-500">Location *</label>
              <input type="text" className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-brand font-medium"
                placeholder="e.g. Conference Room A" value={ceLocation} onChange={e => setCeLocation(e.target.value)} />
            </div>
            <div className="space-y-1 md:col-span-2">
              <label className="text-caption uppercase font-bold text-slate-500">Description</label>
              <textarea className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs outline-none focus:border-brand font-medium min-h-[60px]"
                placeholder="Optional description..." value={ceDescription} onChange={e => setCeDescription(e.target.value)} />
            </div>
          </div>
          <div className="flex items-center justify-between pt-1">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input type="checkbox" checked={ceNotifyApplicants} onChange={e => setCeNotifyApplicants(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand-primary cursor-pointer" />
              <span className="text-xs font-semibold text-slate-600 select-none">Notify applicants of this event</span>
            </label>
            <button type="button" onClick={handleCreateEvent} disabled={ceSaving}
              className="px-4 py-2 btn-primary  disabled:bg-blue-300 text-white font-bold text-xs rounded-xl transition-all cursor-pointer flex items-center space-x-1.5 shadow-sm">
              {ceSaving ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
              <span>{ceSaving ? 'Creating...' : `Create Event (${batchMembers.length} recipients)`}</span>
            </button>
          </div>
        </div>
      )}

      {relatedEvents.length === 0 ? (
        <div className="py-10 text-center text-slate-400">
          <Calendar className="h-8 w-8 mx-auto text-slate-300 mb-2" />
          <p className="text-caption font-bold">No events linked to applicants in this batch.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <input
              type="search"
              value={eventSearch}
              onChange={e => { setEventSearch(e.target.value); setEventPage(0); }}
              placeholder="Search related events..."
              className="w-full pl-9 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:border-brand font-medium text-slate-800"
            />
          </div>

          {(() => {
            const totalPages = Math.ceil(filteredEvents.length / EVENTS_PER_PAGE) || 1;
            const safePage = Math.min(eventPage, totalPages - 1);
            const pagedEvents = filteredEvents.slice(safePage * EVENTS_PER_PAGE, (safePage + 1) * EVENTS_PER_PAGE);

            if (filteredEvents.length === 0) {
              return <p className="text-xs text-slate-500 italic text-center py-6">No events match your search.</p>;
            }

            return (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-100 text-slate-500 font-bold uppercase tracking-wider">
                        <td className="py-2.5 pr-3">Event Name</td>
                        <td className="py-2.5 pr-3">Date</td>
                        <td className="py-2.5 pr-3">Type</td>
                        <td className="py-2.5 pr-3">Status</td>
                        <td className="py-2.5 pr-3">Applicant</td>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedEvents.map(ev => {
                        const app = batchMembers.find(m => m.applicationId === ev.applicantId);
                        const cName = app ? batchDisplayName(app) || app.userEmail : ev.applicantId;
                        return (
                          <tr key={ev.eventId} className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                            <td className="py-2.5 pr-3 font-bold text-slate-800">{ev.title}</td>
                            <td className="py-2.5 pr-3 text-slate-600">{ev.dateTime ? new Date(ev.dateTime).toLocaleDateString() : '—'}</td>
                            <td className="py-2.5 pr-3"><span className="px-2 py-0.5 bg-brand-active rounded-lg font-bold">{ev.eventType}</span></td>
                            <td className="py-2.5 pr-3">
                              <span className={`px-2 py-0.5 rounded-lg font-bold ${
                                ev.status === 'Completed' ? 'bg-emerald-50 text-emerald-700' :
                                ev.status === 'Cancelled' ? 'bg-rose-50 text-rose-600' :
                                'bg-amber-50 text-amber-700'
                              }`}>{ev.status}</span>
                            </td>
                            <td className="py-2.5 pr-3">
                              {app && onViewApplicant ? (
                                <button onClick={(e) => { e.stopPropagation(); onViewApplicant(app); }}
                                  className="inline-flex items-center space-x-1 text-brand hover:text-blue-800 font-medium transition-colors cursor-pointer">
                                  <Eye className="h-3 w-3" />
                                  <span className="underline underline-offset-2 decoration-blue-300 decoration-dotted">{cName}</span>
                                </button>
                              ) : (
                                <span className="text-slate-500 font-medium">{cName}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-3 text-caption text-slate-500 font-semibold">
                    <span>Showing {safePage * EVENTS_PER_PAGE + 1}–{Math.min((safePage + 1) * EVENTS_PER_PAGE, filteredEvents.length)} of {filteredEvents.length} events</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={safePage === 0}
                        onClick={() => setEventPage(safePage - 1)}
                        className="px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                      >
                        <ChevronLeft className="h-3 w-3 inline" /> Prev
                      </button>
                      <span>Page {safePage + 1} of {totalPages}</span>
                      <button
                        type="button"
                        disabled={safePage >= totalPages - 1}
                        onClick={() => setEventPage(safePage + 1)}
                        className="px-2.5 py-1 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
                      >
                        Next <ChevronRight className="h-3 w-3 inline" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

/* ====================================================================
   BATCH-RELATED SIGN REQUESTS TAB — sign requests scoped to batch
   ==================================================================== */
/** A saved template can be sent in bulk only in its native form:
 * native fields AND a stored document. Firma-linked templates carry
 * neither (their document lives in Firma) and are excluded. */
function isNativeBulkSignTemplate(t: any): boolean {
  return (
    Array.isArray(t?.fields) &&
    t.fields.length > 0 &&
    !!(t?.pdfStoragePath || t?.pdfDataUrl)
  );
}

/** Display labels for sign-request statuses in this section's list:
 * native records store lowercase values ('sent', 'completed', ...);
 * legacy records keep their capitalised values via the fallback. */
const SIGN_STATUS_LABELS: Record<string, string> = {
  sent: 'Sent',
  partially_signed: 'Partially Signed',
  completed: 'Completed',
  voided: 'Voided',
  declined: 'Declined',
  expired: 'Expired',
  pending: 'Pending',
};

export function BatchRelatedSignRequestsSection({
  batchMembers,
  adminEmail,
  onViewApplicant,
}: {
  batchMembers: any[];
  adminEmail: string;
  onViewApplicant?: (app: any) => void;
}) {
  const [signRequests, setSignRequests] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [refreshingIds, setRefreshingIds] = React.useState<Set<string>>(new Set());
  const [showSendForm, setShowSendForm] = React.useState(false);
  const [selectedSignGroup, setSelectedSignGroup] = React.useState<any>(null);
  const [showDetail, setShowDetail] = React.useState(false);
  const [signSearch, setSignSearch] = React.useState('');

  // ── Native bulk signing state (Slice 4) ─────────────────────────
  // Saved signing templates from GET /api/signing-templates (native
  // and legacy Firma-linked alike); only native ones can be sent.
  const [bsTemplates, setBsTemplates] = React.useState<any[]>([]);
  const [bsTemplatesLoading, setBsTemplatesLoading] = React.useState(false);
  const [bsSelectedTemplateId, setBsSelectedTemplateId] = React.useState('');
  const [bsSending, setBsSending] = React.useState(false);
  const [bsError, setBsError] = React.useState<string | null>(null);
  const [bsShowConfirm, setBsShowConfirm] = React.useState(false);
  // Slice 6: the same completed-copy option as the individual send —
  // default ON; it rides the job-level input snapshot into every
  // item's create, so each request carries the flag.
  const [bsSendCompletedCopy, setBsSendCompletedCopy] = React.useState(true);

  const fetchSignRequests = React.useCallback(async () => {
    if (batchMembers.length === 0) { setLoading(false); return; }
    const appIds = batchMembers.map(m => m.applicationId);
    const all: any[] = [];
    for (let i = 0; i < appIds.length; i += 30) {
      const chunk = appIds.slice(i, i + 30);
      const q = query(collection(db, 'signRequests'), where('applicationId', 'in', chunk), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      snap.forEach(d => all.push({ signRequestId: d.id, ...d.data() }));
    }
    setSignRequests(all);
  }, [batchMembers]);

  React.useEffect(() => {
    setLoading(true);
    fetchSignRequests().then(() => setLoading(false)).catch(() => setLoading(false));
  }, [fetchSignRequests]);

  React.useEffect(() => {
    // Load saved signing templates (the native send picker's source).
    setBsTemplatesLoading(true);
    auth.currentUser?.getIdToken()
      .then(token => fetch('/api/signing-templates', {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      }))
      .then(r => (r && r.ok ? r.json() : []))
      .then(data => { setBsTemplates(Array.isArray(data) ? data : []); })
      .catch(() => {})
      .finally(() => setBsTemplatesLoading(false));
  }, []);

  const handleMasterRefresh = async () => {
    setRefreshing(true);
    try { await fetchSignRequests(); } catch {}
    setRefreshing(false);
  };

  const handleIndividualRefresh = async (signRequestId: string) => {
    setRefreshingIds(prev => new Set(prev).add(signRequestId));
    try {
      const snap = await getDoc(doc(db, 'signRequests', signRequestId));
      if (snap.exists()) {
        const data = snap.data();
        setSignRequests(prev => prev.map(sr => sr.signRequestId === signRequestId
          ? { signRequestId: sr.signRequestId, ...data }
          : sr
        ));
      }
    } catch {}
    setRefreshingIds(prev => { const next = new Set(prev); next.delete(signRequestId); return next; });
  };

  // ── Native bulk send ─────────────────────────────────────────────
  const bsNativeTemplates = React.useMemo(
    () => bsTemplates.filter(isNativeBulkSignTemplate),
    [bsTemplates],
  );
  const bsLegacyTemplateCount = bsTemplates.length - bsNativeTemplates.length;
  const bsSelectedTemplate =
    bsNativeTemplates.find((t: any) => t.id === bsSelectedTemplateId) || null;

  const handleOpenSignConfirm = () => {
    if (!bsSelectedTemplate) {
      setBsError('Please select a signing template.');
      return;
    }
    if (batchMembers.length === 0) {
      setBsError('This batch has no members to send to.');
      return;
    }
    setBsError(null);
    setBsShowConfirm(true);
  };

  const handleSendSignRequests = async () => {
    if (!bsSelectedTemplate) return;
    setBsSending(true);
    setBsError(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      // ONLY ids go into the job items: the job handler's create core
      // resolves each applicant's signer email, linked values and
      // document from their own record — no email is supplied here.
      const items = batchMembers.map((app: any) => ({
        itemType: 'sign_recipient',
        applicationId: app.applicationId,
        inputSnapshot: { signingTemplateId: bsSelectedTemplate.id },
      }));

      const res = await fetch('/api/admin/bulk-jobs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          type: 'batch_sign_request',
          items,
          inputSnapshot: {
            signingTemplateId: bsSelectedTemplate.id,
            // Slice 6: the confirm modal's completed-copy choice,
            // applied by the job handler to every item's request.
            sendCompletedCopyToSigners: bsSendCompletedCopy,
          },
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to enqueue sign request job');
      }

      notifyToast('success', `Sign Requests Task Queued for ${batchMembers.length} members! Tracking in Task Tracker.`);
      window.dispatchEvent(new CustomEvent('bulk-job-created'));
      setBsShowConfirm(false);
      setShowSendForm(false);
      setBsSelectedTemplateId('');
    } catch (err: any) {
      setBsShowConfirm(false);
      setBsError(err.message || 'Failed to queue sign requests');
    } finally {
      setBsSending(false);
    }
    await fetchSignRequests();
  };

  // ── Group sign requests by templateId ────────────────────────────────
  const signGroups = React.useMemo(() => {
    const groups: Record<string, any[]> = {};
    for (const sr of signRequests) {
      const key = sr.templateId || sr.templateName || 'Other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(sr);
    }
    return Object.entries(groups)
      .map(([templateId, entries]) => ({
        templateId,
        templateName: entries[0]?.templateName || templateId,
        entries,
        count: entries.length,
        sentAt: [...entries].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())[0]?.createdAt || entries[0]?.createdAt,
      }))
      .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
  }, [signRequests]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
        <span className="text-sm font-semibold">Loading sign requests...</span>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
        <h4 className="text-xs font-bold tracking-wider text-slate-900 flex items-center">
          <FileSignature className="h-4 w-4 mr-1.5 text-brand" />
          Batch Sign Requests ({signGroups.length})
        </h4>
        <div className="flex items-center space-x-2">
          <button type="button" onClick={handleMasterRefresh} disabled={refreshing}
            className="p-1.5 text-slate-500 hover:text-brand bg-brand-active rounded-lg transition-all cursor-pointer disabled:opacity-40"
            title="Refresh all sign requests">
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button type="button" onClick={() => setShowSendForm(!showSendForm)}
            className={`px-3 py-1.5 text-caption font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 ${
              showSendForm ? 'bg-slate-200 text-slate-700' : 'btn-primary  text-white shadow-sm'
            }`}>
            <Upload className="h-3.5 w-3.5" />
            <span>{showSendForm ? 'Cancel' : 'Send Sign Request'}</span>
          </button>
        </div>
      </div>

      {showSendForm && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3 animate-slideDown">
          <h5 className="text-xs font-extrabold text-slate-900">Send Sign Request to All {batchMembers.length} Candidates</h5>
          <p className="text-caption text-slate-400">Each recipient gets their own sign request from the selected template, with linked fields populated from their own applicant record. Applicants whose linked data is missing are reported as failed items in the Task Tracker — no request is sent to them.</p>

          <div className="space-y-1">
            <label className="text-caption uppercase font-bold text-slate-500">Signing Template *</label>
            {bsTemplatesLoading ? (
              <div className="flex items-center space-x-2 text-caption text-slate-500 py-2"><Loader className="h-3.5 w-3.5 animate-spin" /><span>Loading templates...</span></div>
            ) : (
              <SearchableSelect
                value={bsSelectedTemplateId}
                onChange={(v) => setBsSelectedTemplateId(v)}
                options={bsNativeTemplates.map((t: any) => ({ value: t.id, label: t.name }))}
                placeholder="Search signing templates…"
                clearLabel="-- Select a template --"
                emptyLabel="No native signing templates found"
              />
            )}
            {bsLegacyTemplateCount > 0 && (
              <p className="text-[11px] text-slate-500">{bsLegacyTemplateCount} saved template{bsLegacyTemplateCount !== 1 ? 's are' : ' is'} legacy (Firma) with no native document and cannot be sent in bulk.</p>
            )}
          </div>

          {bsError && (
            <div className="flex items-start space-x-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
              <AlertCircle className="h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5" />
              <p className="text-caption text-red-700">{bsError}</p>
            </div>
          )}

          <button type="button" onClick={handleOpenSignConfirm}
            disabled={bsSending || !bsSelectedTemplateId}
            className="w-full py-2.5 bg-brand-primary hover:bg-brand-primary-dark disabled:bg-indigo-300 font-bold text-white rounded-xl text-xs transition-all cursor-pointer disabled:cursor-not-allowed shadow-sm flex items-center justify-center space-x-1.5">
            <FileSignature className="h-3.5 w-3.5" /><span>Review &amp; Send ({batchMembers.length} recipients)</span>
          </button>
        </div>
      )}

      {bsShowConfirm && bsSelectedTemplate && (
        <Modal
          onClose={() => { if (!bsSending) setBsShowConfirm(false); }}
          ariaLabel="Confirm bulk sign request"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2">
                <FileSignature className="h-5 w-5 text-brand" />
                <h3 className="text-sm font-bold text-slate-800">Send sign requests?</h3>
              </div>
              <button type="button" onClick={() => setBsShowConfirm(false)} disabled={bsSending}
                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg cursor-pointer disabled:opacity-50">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-caption text-slate-600">
                You are about to queue sign requests for <strong>{batchMembers.length} recipient{batchMembers.length !== 1 ? 's' : ''}</strong> using
                the template <strong>{bsSelectedTemplate.name}</strong>. Each applicant receives their own request, with linked
                fields populated from their own record. Applicants whose linked data is missing will be reported as failed
                items in the Task Tracker — no request is sent to them.
              </p>
              <label className="flex items-start gap-2.5 cursor-pointer bg-slate-50 border border-slate-200 rounded-xl px-4 py-3">
                <input
                  type="checkbox"
                  checked={bsSendCompletedCopy}
                  onChange={(e) => setBsSendCompletedCopy(e.target.checked)}
                  className="h-4 w-4 mt-0.5 rounded border-slate-300 bg-white text-indigo-600 focus:ring-brand-primary cursor-pointer"
                />
                <span>
                  <span className="block text-caption font-bold text-slate-700">
                    Send the finished document to all signing parties when complete
                  </span>
                  <span className="block text-[11px] text-slate-500 mt-0.5">
                    When an applicant has signed, they are emailed the finished signed document and
                    can download a copy from their completion screen.
                  </span>
                </span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50">
              <button type="button" onClick={() => setBsShowConfirm(false)} disabled={bsSending}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-800 cursor-pointer disabled:opacity-50">
                Return
              </button>
              <button type="button" onClick={handleSendSignRequests} disabled={bsSending}
                className="px-4 py-2 bg-brand-primary hover:bg-brand-primary-dark disabled:bg-indigo-300 text-white text-xs font-bold rounded-xl cursor-pointer disabled:cursor-not-allowed flex items-center space-x-1.5">
                {bsSending ? (
                  <><Loader className="h-3.5 w-3.5 animate-spin" /><span>Queueing...</span></>
                ) : (
                  <span>Send Sign Requests ({batchMembers.length})</span>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {signGroups.length === 0 ? (
        <div className="py-10 text-center text-slate-400">
          <FileSignature className="h-8 w-8 mx-auto text-slate-300 mb-2" />
          <p className="text-caption font-bold">No sign requests for applicants in this batch.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-100 text-slate-500 font-bold uppercase tracking-wider">
                <td className="py-2.5 pr-3">Document Name</td>
                <td className="py-2.5 pr-3">Sent</td>
                <td className="py-2.5 pr-3 text-right">Recipients</td>
                <td className="py-2.5 pr-3">Signed / Total</td>
                <td className="py-2.5 pr-3"></td>
              </tr>
            </thead>
            <tbody>
              {signGroups.map((grp, gi) => {
                const signedCount = grp.entries.filter((sr: any) => sr.status === 'Signed' || sr.status === 'Completed' || sr.status === 'completed').length;
                return (
                  <tr key={grp.templateId || gi} className="border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => { setSelectedSignGroup(grp); setShowDetail(true); setSignSearch(''); }}>
                    <td className="py-2.5 pr-3 font-bold text-slate-800">{grp.templateName}</td>
                    <td className="py-2.5 pr-3 text-slate-600">{grp.sentAt ? new Date(grp.sentAt).toLocaleDateString() : '—'}</td>
                    <td className="py-2.5 pr-3 text-right text-slate-700 font-bold">{grp.count}</td>
                    <td className="py-2.5 pr-3 text-slate-600">{signedCount} / {grp.count}</td>
                    <td className="py-2.5 pr-3 text-right">
                      <button onClick={(e) => { e.stopPropagation(); setSelectedSignGroup(grp); setShowDetail(true); setSignSearch(''); }}
                        className="px-2.5 py-1 btn-primary  text-white rounded-lg text-caption font-bold transition-all cursor-pointer">View</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Sign Request Group Detail Modal */}
      {showDetail && selectedSignGroup && (
        <Modal onClose={() => setShowDetail(false)} title={selectedSignGroup.templateName}>
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-200 shrink-0">
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-extrabold text-slate-900 truncate">{selectedSignGroup.templateName}</h3>
                <p className="text-caption text-slate-400 mt-0.5">{selectedSignGroup.count} sign request{selectedSignGroup.count !== 1 ? 's' : ''}</p>
              </div>
              <button onClick={() => setShowDetail(false)} className="text-slate-400 hover:text-slate-700 cursor-pointer p-1 ml-3 shrink-0"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-caption uppercase font-bold text-slate-500">Applicants</span>
                </div>
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-2 h-3 w-3 text-slate-400" />
                  <input type="search" placeholder="Search applicants..." className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-7 pr-2.5 py-1.5 text-xs outline-none  text-slate-700"
                    value={signSearch} onChange={e => setSignSearch(e.target.value)} />
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl max-h-60 overflow-y-auto">
                  {(() => {
                    const q = signSearch.toLowerCase();
                    const filtered = q
                      ? selectedSignGroup.entries.filter((sr: any) => {
                          const app = batchMembers.find(m => m.applicationId === sr.applicationId);
                          const name = app ? batchDisplayName(app) || app.userEmail : sr.applicationId;
                          return (name || '').toLowerCase().includes(q);
                        })
                      : selectedSignGroup.entries;
                    if (filtered.length === 0) {
                      return <p className="text-xs text-slate-500 italic text-center py-4">No applicants match your search.</p>;
                    }
                    return (
                      <table className="w-full text-xs">
                        <thead><tr className="border-b border-slate-200 text-slate-500 font-bold"><td className="px-3 py-2">Applicant</td><td className="px-3 py-2">Status</td><td className="pr-3 text-right"></td></tr></thead>
                        <tbody>
                          {filtered.map((sr: any, i: number) => {
                            const app = batchMembers.find(m => m.applicationId === sr.applicationId);
                            const cName = app ? batchDisplayName(app) || app.userEmail : sr.applicationId;
                            const isSigned = sr.status === 'Signed' || sr.status === 'Completed' || sr.status === 'completed';
                            const isRefreshing = refreshingIds.has(sr.signRequestId);
                            return (
                              <tr key={sr.signRequestId || i} className="border-b border-slate-100 last:border-0 hover:bg-white transition-colors">
                                <td className="px-3 py-2 text-xs text-slate-700">
                                  {app && onViewApplicant ? (
                                    <button onClick={(e) => { e.stopPropagation(); onViewApplicant(app); }}
                                      className="flex items-center space-x-1 text-brand hover:text-blue-800 font-medium transition-colors cursor-pointer">
                                      <Eye className="h-3 w-3" />
                                      <span className="underline underline-offset-2 decoration-blue-300 decoration-dotted">{cName}</span>
                                    </button>
                                  ) : <span className="text-slate-500 font-medium">{cName}</span>}
                                </td>
                                <td className="px-3 py-2">
                                  <span className={`px-1.5 py-0.5 rounded-lg font-bold text-[10px] ${
                                    isSigned ? 'bg-emerald-50 text-emerald-700' :
                                    (sr.status === 'Declined' || sr.status === 'declined') ? 'bg-rose-50 text-rose-600' :
                                    'bg-amber-50 text-amber-700'
                                  }`}>{SIGN_STATUS_LABELS[sr.status] || sr.status || 'Pending'}</span>
                                </td>
                                <td className="pr-3 text-right py-2">
                                  <button type="button" onClick={(e) => { e.stopPropagation(); handleIndividualRefresh(sr.signRequestId); }} disabled={isRefreshing}
                                    className="p-1.5 text-slate-400 hover:text-brand bg-brand-active rounded-lg transition-all cursor-pointer disabled:opacity-40"
                                    title="Refresh this sign request status">
                                    <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </Modal>
        )}
    </div>
  );
}

/* ====================================================================
   BATCH-RELATED EMAIL CAMPAIGNS TAB — batch-sent emails only
   ==================================================================== */
function BatchRelatedEmailCampaignsSection({
  batchMembers,
  batchId,
  adminEmail,
  onViewApplicant,
}: {
  batchMembers: any[];
  batchId: string;
  adminEmail: string;
  onViewApplicant?: (app: any) => void;
}) {
  const [batchEmails, setBatchEmails] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedCampaign, setSelectedCampaign] = React.useState<any>(null);
  const [showDetail, setShowDetail] = React.useState(false);

  // Campaign list search & pagination
  const [campaignSearch, setCampaignSearch] = React.useState('');
  const [campaignPage, setCampaignPage] = React.useState(0);
  const CAMPAIGNS_PER_PAGE = 20;

  // Send campaign state & 4-Step Wizard
  const [showSendForm, setShowSendForm] = React.useState(false);
  const [wizardStep, setWizardStep] = React.useState<1 | 2 | 3 | 4>(1);
  const [audienceProgramFilter, setAudienceProgramFilter] = React.useState('');
  const [audienceStatusFilter, setAudienceStatusFilter] = React.useState('');
  const [audienceStageFilter, setAudienceStageFilter] = React.useState('');
  const [audienceCountryFilter, setAudienceCountryFilter] = React.useState('');
  const [audienceSearch, setAudienceSearch] = React.useState('');
  const [showExcluderDrawer, setShowExcluderDrawer] = React.useState(false);

  const [previewDeviceFrame, setPreviewDeviceFrame] = React.useState<'desktop' | 'mobile'>('desktop');
  const [testEmailAddress, setTestEmailAddress] = React.useState(adminEmail);
  const [testEmailSending, setTestEmailSending] = React.useState(false);
  const [testEmailStatus, setTestEmailStatus] = React.useState('');

  const [campSubject, setCampSubject] = React.useState('');
  // Document templates to generate per recipient + attach at send time,
  // pre-selected from the inserted email template (composer hook below).
  // The batch send wiring binds to this same state (Slice 4).
  const [campDocumentTemplateIds, setCampDocumentTemplateIds] = React.useState<string[]>([]);
  // Campaign attachments — adopted from the inserted email template's
  // STATIC attachments (Slice 4: previously dropped by the batch template
  // picker). Sent with the broadcast/schedule payloads and attached to
  // every recipient's email alongside the generated documents.
  // Intentionally NOT persisted in campaign draft snapshots (the direct
  // composer's rule for attachments).
  const [campAttachments, setCampAttachments] = React.useState<AttachmentPayload[]>([]);
  const [campSenderAddress, setCampSenderAddress] = React.useState('');
  const [campSending, setCampSending] = React.useState(false);
  // Inline send-path validation (PR-11 §F): replaces blocking alert()s —
  // rendered next to the Step-4 pre-flight checklist.
  const [campaignFormError, setCampaignFormError] = React.useState('');
  const [excludedIds, setExcludedIds] = React.useState<string[]>([]);
  const [excludeSearch, setExcludeSearch] = React.useState('');

  // Shared composer — the single source of truth for the campaign body
  // (template insertion, WYSIWYG/HTML modes, font size/colour, sanitization).
  const composer = useComposerBody({
    onTemplateSubject: (s) => setCampSubject(s),
    onTemplateDocumentTemplates: (ids) => setCampDocumentTemplateIds(ids),
  });
  // One idempotency key per compose session; reused across retries of the
  // same send/schedule action, rotated after a successful dispatch.
  const [idempotencyKey, setIdempotencyKey] = React.useState(() => newIdempotencyKey());
  const [showScheduleModal, setShowScheduleModal] = React.useState(false);
  const [broadcastJob, setBroadcastJob] = React.useState<{
    jobId: string; status: string; recipientCount: number; deduplicated?: boolean;
  } | null>(null);

  // Template picker & preview state
  const [showTemplatePicker, setShowTemplatePicker] = React.useState(false);
  const [previewAppIndex, setPreviewAppIndex] = React.useState(0);

  /* ── Server-side draft autosave (per admin, per batch) ──
   * The whole wizard state — step, subject, body, sender, chosen
   * template, audience filters and exclusions — is snapshotted and
   * autosaved so the admin can close the page, log out, or switch
   * batches and resume exactly where they stopped. A pristine form
   * (nothing typed, step 1, no filters) yields a null snapshot, which
   * deletes the draft — that is also how send/reset flows clean up. */
  const campaignIsPristine =
    wizardStep === 1 &&
    !campSubject.trim() &&
    composer.isEmpty &&
    !campSenderAddress &&
    campDocumentTemplateIds.length === 0 &&
    excludedIds.length === 0 &&
    !audienceProgramFilter &&
    !audienceStatusFilter &&
    !audienceStageFilter &&
    !audienceCountryFilter &&
    !audienceSearch;
  const campaignDraftSnapshot = React.useMemo(
    () =>
      campaignIsPristine
        ? null
        : {
            step: wizardStep,
            subject: campSubject,
            bodyHtml: composer.bodyHtml,
            senderAddress: campSenderAddress,
            documentTemplateIds: campDocumentTemplateIds,
            templateName: composer.templateInserted?.name || null,
            audience: {
              program: audienceProgramFilter,
              status: audienceStatusFilter,
              stage: audienceStageFilter,
              country: audienceCountryFilter,
              search: audienceSearch,
            },
            excludedIds,
          },
    [
      campaignIsPristine,
      wizardStep,
      campSubject,
      composer.bodyHtml,
      campSenderAddress,
      campDocumentTemplateIds,
      composer.templateInserted,
      audienceProgramFilter,
      audienceStatusFilter,
      audienceStageFilter,
      audienceCountryFilter,
      audienceSearch,
      excludedIds,
    ],
  );
  // Finding 4: whether the in-wizard "you resumed a draft" notice has
  // been acknowledged for the currently-loaded draft.
  const [resumeNoticeDismissed, setResumeNoticeDismissed] = React.useState(false);
  const campaignDraft = useEmailDraft({
    kind: 'campaign',
    contextId: batchId || null,
    snapshot: campaignDraftSnapshot,
    onRestore: (data) => {
      if (typeof data.subject === 'string') setCampSubject(data.subject);
      if (typeof data.bodyHtml === 'string' && data.bodyHtml) composer.reset(data.bodyHtml);
      if (typeof data.senderAddress === 'string') setCampSenderAddress(data.senderAddress);
      if (Array.isArray(data.documentTemplateIds)) {
        setCampDocumentTemplateIds(data.documentTemplateIds.filter((x: any) => typeof x === 'string'));
      }
      if (data.audience && typeof data.audience === 'object') {
        setAudienceProgramFilter(String(data.audience.program || ''));
        setAudienceStatusFilter(String(data.audience.status || ''));
        setAudienceStageFilter(String(data.audience.stage || ''));
        setAudienceCountryFilter(String(data.audience.country || ''));
        setAudienceSearch(String(data.audience.search || ''));
      }
      if (Array.isArray(data.excludedIds)) setExcludedIds(data.excludedIds.map(String));
      // Live-walk Finding 4: the wizard ALWAYS starts at Step 1 — landing
      // mid-wizard at the draft's saved step made "Send Campaign" feel
      // like it silently opened someone else's half-finished work. The
      // resumed-draft notice in the wizard header makes the state
      // explicit instead. (The saved step remains in the draft data.)
      setResumeNoticeDismissed(false);
    },
  });

  /* ── Team Drafts (PR-11 §B): campaign drafts are shared across the
   * workspace — every admin with campaign permission sees the drafts for
   * this batch, who owns them and who edited them last. Resuming one
   * retargets the wizard's autosave at that shared draft. */
  const [teamDrafts, setTeamDrafts] = React.useState<EmailDraftRecord[]>([]);
  const [teamDraftsLoading, setTeamDraftsLoading] = React.useState(false);
  const refreshTeamDrafts = React.useCallback(async () => {
    if (!batchId) return;
    setTeamDraftsLoading(true);
    try {
      setTeamDrafts(await listCampaignDrafts(batchId));
    } catch {
      /* drafts are best-effort — a list failure must never block the page */
    } finally {
      setTeamDraftsLoading(false);
    }
  }, [batchId]);
  React.useEffect(() => {
    void refreshTeamDrafts();
  }, [refreshTeamDrafts]);

  const handleResumeTeamDraft = (draft: EmailDraftRecord) => {
    campaignDraft.resumeDraft(draft);
    setResumeNoticeDismissed(false);
    setShowSendForm(true);
  };

  const handleDiscardTeamDraft = async (draft: EmailDraftRecord) => {
    const author = draftAuthorLabel(draft);
    const subject = (typeof draft.data?.subject === 'string' && draft.data.subject.trim()) || '(no subject yet)';
    const confirmed = draft.mine
      ? await confirmDialog(`Discard your campaign draft "${subject}"? This cannot be undone.`)
      : await confirmDialog(
          `Discard the campaign draft "${subject}" created by ${author}?\n\nThis deletes a TEAMMATE'S draft for everyone in the workspace. This cannot be undone.`,
        );
    if (!confirmed) return;
    try {
      if (campaignDraft.attribution?.id && draft.id && campaignDraft.attribution.id === draft.id) {
        // The discarded draft is the one loaded in the wizard — reset
        // tracking too so autosave doesn't immediately recreate it.
        await campaignDraft.discard();
        resetCampaignForm();
      } else if (draft.id) {
        await deleteEmailDraft('campaign', batchId, draft.id);
      }
    } catch {
      /* best-effort */
    }
    void refreshTeamDrafts();
  };

  // Filtered audience calculation
  const filteredAudience = React.useMemo(() => {
    return batchMembers.filter((app) => {
      if (excludedIds.includes(app.applicationId)) return false;
      if (audienceProgramFilter && app.programType !== audienceProgramFilter && app.programName !== audienceProgramFilter) return false;
      if (audienceStatusFilter && app.status !== audienceStatusFilter) return false;
      if (audienceStageFilter && (app.masterStage || app.pipelineStage) !== audienceStageFilter) return false;
      if (audienceCountryFilter && app.personalDetails?.countryOfCitizenship !== audienceCountryFilter) return false;
      if (audienceSearch) {
        const q = audienceSearch.toLowerCase();
        const name = batchDisplayName(app).toLowerCase();
        const email = (app.userEmail || '').toLowerCase();
        if (!name.includes(q) && !email.includes(q) && !app.applicationId.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [batchMembers, excludedIds, audienceProgramFilter, audienceStatusFilter, audienceStageFilter, audienceCountryFilter, audienceSearch]);

  const availablePrograms = React.useMemo(() => Array.from(new Set(batchMembers.map(a => a.programType || a.programName).filter(Boolean))), [batchMembers]);
  const availableStatuses = React.useMemo(() => Array.from(new Set(batchMembers.map(a => a.status).filter(Boolean))), [batchMembers]);
  const availableStages = React.useMemo(() => Array.from(new Set(batchMembers.map(a => a.masterStage || a.pipelineStage).filter(Boolean))), [batchMembers]);
  const availableCountries = React.useMemo(() => Array.from(new Set(batchMembers.map(a => a.personalDetails?.countryOfCitizenship).filter(Boolean))), [batchMembers]);

  // Recipient list search in detail modal
  const [recipientSearch, setRecipientSearch] = React.useState('');

  // Delivery status tracking
  const [deliveryCache, setDeliveryCache] = React.useState<Record<string, { status: DeliveryStatus; bounceReason?: string; opens?: number; clicks?: number; lastChecked?: string }>>({});
  const [refreshingDelivery, setRefreshingDelivery] = React.useState(false);
  const [lastRefreshed, setLastRefreshed] = React.useState<string | null>(null);

  // ── Group batchEmailLogs by campaign (campaignId, with fallback to subject) ──
  const campaigns = React.useMemo(() => {
    const groups: Record<string, any[]> = {};
    for (const em of batchEmails) {
      const key = em.campaignId || em.subject || '(No Subject)';
      if (!groups[key]) groups[key] = [];
      groups[key].push(em);
    }
    return Object.entries(groups)
      .map(([key, entries]) => {
        const templateSubject = entries[0]?.templateSubject || entries[0]?.rawSubject || entries[0]?.subject || key;
        const templateBody = entries[0]?.originalTemplateHtml || entries[0]?.templateBody || entries[0]?.unresolvedHtml || entries[0]?.htmlBody || entries[0]?.textBody || '';
        return {
          campaignId: entries[0]?.campaignId || key,
          title: templateSubject,
          templateSubject,
          entries,
          count: entries.length,
          sentBy: entries[0]?.sentBy || '—',
          sentAt: [...entries].sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime())[0]?.sentAt || entries[0]?.sentAt,
          fromName: entries[0]?.fromName || entries[0]?.from || 'Admin',
          htmlBody: entries[0]?.htmlBody || '',
          templateBody,
          textBody: entries[0]?.textBody || entries[0]?.content || '',
          messageIds: entries.map(e => e.messageId).filter(Boolean),
        };
      })
      .sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
  }, [batchEmails]);

  const handleCopyCampaignContent = (camp: any) => {
    const rawBody = camp.templateBody || camp.originalTemplateHtml || camp.htmlBody || camp.textBody || '';
    navigator.clipboard.writeText(rawBody);
    notifyToast('success', 'Dynamic template content copied to clipboard (with dynamic fields preserved).');
  };

  const handleReuseCampaign = (camp: any) => {
    const rawSubject = camp.templateSubject || camp.rawSubject || camp.title || '';
    const rawBody = camp.templateBody || camp.originalTemplateHtml || camp.htmlBody || camp.textBody || '';
    setCampSubject(rawSubject);
    composer.setBodyHtml(rawBody);
    setBroadcastJob(null);
    setShowSendForm(true);
    setWizardStep(2);
    setShowDetail(false);
  };

  // Aggregate delivery status per campaign from deliveryCache
  const campaignDeliverySummary = React.useMemo(() => {
    const summary: Record<string, { delivered: number; bounced: number; pending: number }> = {};
    for (const camp of campaigns) {
      let delivered = 0, bounced = 0, pending = 0;
      for (const mid of camp.messageIds) {
        const cached = deliveryCache[mid];
        if (!cached) { pending++; continue; }
        if (cached.status === 'delivered' || cached.status === 'Sent') delivered++;
        else if (cached.status === 'hard_bounced' || cached.status === 'soft_bounced') bounced++;
        else pending++;
      }
      summary[camp.campaignId] = { delivered, bounced, pending };
    }
    return summary;
  }, [campaigns, deliveryCache]);

  const refreshDeliveryStatuses = React.useCallback(async () => {
    // Collect all unique messageIds across all campaigns
    const allMessageIds = new Set<string>();
    for (const camp of campaigns) {
      for (const mid of camp.messageIds) {
        allMessageIds.add(mid);
      }
    }
    if (allMessageIds.size === 0) return;
    setRefreshingDelivery(true);
    const token = await auth.currentUser?.getIdToken();
    const results: Record<string, { status: DeliveryStatus; bounceReason?: string; opens?: number; clicks?: number; lastChecked?: string }> = {};
    await Promise.allSettled(
      [...allMessageIds].map(async (messageId) => {
        try {
          const res = await fetch(`/api/email/delivery-status/${encodeURIComponent(messageId)}`, {
            headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          });
          if (!res.ok) return;
          const data = await res.json();
          results[messageId] = { status: data.status, bounceReason: data.bounceReason, opens: data.opens, clicks: data.clicks, lastChecked: new Date().toISOString() };
        } catch { /* skip individual failures */ }
      })
    );
    setDeliveryCache(prev => ({ ...prev, ...results }));
    setLastRefreshed(new Date().toISOString());
    setRefreshingDelivery(false);
  }, [campaigns]);

  React.useEffect(() => {
    if (!batchId) { setLoading(false); return; }
    setLoading(true);
    const q = query(
      collection(db, 'batchEmailLogs'),
      where('batchId', '==', batchId),
      orderBy('sentAt', 'desc')
    );
    getDocs(q)
      .then(snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setBatchEmails(all);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [batchId]);

  const [showBroadcastConfirmModal, setShowBroadcastConfirmModal] = React.useState(false);

  /** Recipients for the broadcast contract — filtered audience with valid emails. */
  const broadcastRecipients: BroadcastRecipient[] = React.useMemo(
    () =>
      filteredAudience
        .map((app: any) => ({
          email: String(app.email || app.userEmail || '').trim(),
          name: batchDisplayName(app) || app.userEmail || '',
          applicationId: app.applicationId || null,
          applicantData: app,
        }))
        .filter((r) => r.email && r.email.includes('@')),
    [filteredAudience],
  );

  const resetCampaignForm = () => {
    setShowSendForm(false);
    setWizardStep(1);
    setCampSubject('');
    composer.reset('');
    setCampSenderAddress('');
    // The document selection + attachments are send-effective state —
    // a fresh compose must never inherit the previous campaign's.
    setCampDocumentTemplateIds([]);
    setCampAttachments([]);
    setExcludedIds([]);
    setBroadcastJob(null);
    setCampaignFormError('');
  };

  const executeSendCampaign = async () => {
    setShowBroadcastConfirmModal(false);
    const recipients = broadcastRecipients;
    if (recipients.length === 0) return;

    setCampaignFormError('');
    setCampSending(true);
    try {
      // POST /api/email/broadcast — one idempotency key per compose session,
      // reused across retries of this send action.
      const result = await broadcastEmail({
        recipients,
        subject: campSubject.trim(),
        htmlBody: composer.getSendHtml(),
        idempotencyKey,
        batchId,
        sentBy: adminEmail,
        senderAddress: campSenderAddress || undefined,
        // Slice 4: campaign attachments (incl. the email template's
        // static attachments) ride to every recipient, and the document
        // selection is generated per recipient from their own
        // application by the bulk job. Omitted entirely when empty so
        // document-free campaigns keep the historical payload.
        ...(campAttachments.length > 0 ? { attachments: campAttachments } : {}),
        ...(campDocumentTemplateIds.length > 0 ? { documentTemplateIds: campDocumentTemplateIds } : {}),
      });
      // Success → a NEW compose gets a NEW key. Failures keep this key for retry.
      setIdempotencyKey(newIdempotencyKey());
      // The campaign is dispatched — its draft must not resurrect.
      void campaignDraft.discard();
      void refreshTeamDrafts();
      setBroadcastJob({
        jobId: result.jobId,
        status: result.status,
        recipientCount: result.recipientCount,
        deduplicated: result.deduplicated,
      });
      notifyToast(
        'success',
        result.deduplicated
          ? 'Duplicate send suppressed — this campaign was already queued.'
          : `Email Campaign Queued for ${result.recipientCount} recipient${result.recipientCount === 1 ? '' : 's'}!`,
      );
      window.dispatchEvent(new CustomEvent('bulk-job-created'));
    } catch (err) {
      setCampaignFormError(
        'Broadcast send failed: ' + (err instanceof Error ? err.message : String(err)),
      );
    } finally {
      setCampSending(false);
    }
  };

  const handleSendCampaign = () => {
    if (!campSubject.trim() || composer.isEmpty) {
      setCampaignFormError('Please fill in both subject and email content.');
      return;
    }
    if (broadcastRecipients.length === 0) {
      setCampaignFormError('No recipients available matching the audience filter criteria.');
      return;
    }
    setCampaignFormError('');
    setShowBroadcastConfirmModal(true);
  };

  const handleScheduleCampaign = () => {
    if (!campSubject.trim() || composer.isEmpty) {
      setCampaignFormError('Please fill in both subject and email content.');
      return;
    }
    if (broadcastRecipients.length === 0) {
      setCampaignFormError('No recipients available matching the audience filter criteria.');
      return;
    }
    setCampaignFormError('');
    setShowScheduleModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
        <span className="text-sm font-semibold">Loading batch email campaigns...</span>
      </div>
    );
  }

  const filteredExcludable = batchMembers.filter(app => {
    const q = excludeSearch.toLowerCase();
    const name = batchDisplayName(app).toLowerCase();
    return name.includes(q) || (app.userEmail || '').toLowerCase().includes(q) || String(app.applicationId || '').toLowerCase().includes(q);
  });

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
        <h4 className="text-xs font-bold tracking-wider text-slate-900 flex items-center">
          <Mail className="h-4 w-4 mr-1.5 text-brand" />
          Batch Email Campaigns ({campaigns.length})
        </h4>
        <div className="flex items-center space-x-2">
          <span className="text-caption text-slate-400 font-medium hidden sm:inline">Batch-sent emails only</span>
          <button
            type="button"
            onClick={refreshDeliveryStatuses}
            disabled={refreshingDelivery}
            className="px-2.5 py-1.5 text-caption font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 bg-slate-100 hover:bg-slate-200 text-slate-600 disabled:opacity-40"
            title="Refresh delivery status for all visible entries"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshingDelivery ? 'animate-spin' : ''}`} />
            <span>Refresh Status</span>
          </button>
          <button
            type="button"
            onClick={() => setShowSendForm(!showSendForm)}
            className={`px-3 py-1.5 text-caption font-bold rounded-lg transition-all cursor-pointer flex items-center space-x-1 ${
              showSendForm ? 'bg-slate-200 text-slate-700' : 'btn-primary  text-white shadow-sm'
            }`}
          >
            <Send className="h-3.5 w-3.5" />
            <span>{showSendForm ? 'Cancel' : 'Send Campaign'}</span>
          </button>
        </div>
      </div>
      {lastRefreshed && (
        <p className="text-caption text-slate-400 text-right -mt-2">Last refreshed: {new Date(lastRefreshed).toLocaleString()}</p>
      )}

      {/* Team Drafts — shared workspace campaign drafts for this batch (PR-11 §B) */}
      {teamDrafts.length > 0 && (
        <div className="bg-indigo-50/60 border border-indigo-100 rounded-xl px-4 py-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-bold text-indigo-900">
              Team Drafts ({teamDrafts.length}) — shared with everyone in this workspace
            </p>
            <button
              type="button"
              onClick={() => void refreshTeamDrafts()}
              disabled={teamDraftsLoading}
              className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 disabled:opacity-40 cursor-pointer flex items-center gap-1"
            >
              <RefreshCw className={`h-3 w-3 ${teamDraftsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          {teamDrafts.map((draft) => {
            const subject =
              (typeof draft.data?.subject === 'string' && draft.data.subject.trim()) || '(No subject yet)';
            const editorLabel = draftLastEditorLabel(draft);
            return (
              <div
                key={draft.id || `${draft.ownerUid}-${draft.updatedAt}`}
                className="flex flex-wrap items-center justify-between gap-2 bg-white border border-indigo-100 rounded-lg px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-slate-800 truncate max-w-md" title={subject}>
                    {subject}
                  </p>
                  <p className="text-[11px] text-slate-500">
                    {draft.mine ? 'Your draft' : `Draft by ${draftAuthorLabel(draft)}`}
                    {editorLabel !== draftAuthorLabel(draft) || !draft.mine
                      ? ` · last edited by ${editorLabel}`
                      : ''}
                    {draft.updatedAt ? ` · ${new Date(draft.updatedAt).toLocaleString()}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleResumeTeamDraft(draft)}
                    className="px-3 py-1.5 bg-brand-primary hover:bg-brand-primary-dark text-white text-xs font-bold rounded-lg transition-all cursor-pointer"
                  >
                    Resume
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDiscardTeamDraft(draft)}
                    className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 text-xs font-bold rounded-lg transition-all cursor-pointer"
                  >
                    Discard
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Saved-draft entry point — resume or discard an unfinished campaign */}
      {campaignDraft.restored && !showSendForm && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <p className="text-xs font-semibold text-amber-800">
            {campaignDraft.attribution && !campaignDraft.attribution.mine ? (
              <>
                {draftAuthorLabel(campaignDraft.attribution) === 'a teammate'
                  ? 'A teammate'
                  : draftAuthorLabel(campaignDraft.attribution)}{' '}
                has an unfinished campaign draft for this batch
                {campaignDraft.savedAt ? ` — last edited ${formatDraftTime(campaignDraft.savedAt)}` : ''}.
                Resume to continue it — your edits update the shared draft.
              </>
            ) : (
              <>
                You have an unfinished campaign draft
                {campaignDraft.savedAt ? ` — saved ${formatDraftTime(campaignDraft.savedAt)}` : ''}.
                Resume exactly where you left off.
              </>
            )}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowSendForm(true)}
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold rounded-lg transition-all cursor-pointer"
            >
              Resume draft
            </button>
            <button
              type="button"
              onClick={async () => {
                await campaignDraft.discard();
                resetCampaignForm();
                void refreshTeamDrafts();
              }}
              className="px-3 py-1.5 bg-white border border-amber-200 text-amber-700 hover:bg-amber-100 text-xs font-bold rounded-lg transition-all cursor-pointer"
            >
              Discard
            </button>
          </div>
        </div>
      )}

      {/* 4-Step Campaign Wizard */}
      {showSendForm && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xl space-y-6 animate-slideDown">
          {/* Step Progress Bar */}
          <div className="flex items-center justify-between border-b border-slate-100 pb-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-black uppercase tracking-wider text-slate-400">Campaign Wizard</span>
              <span className="text-xs font-bold text-slate-800">• Step {wizardStep} of 4</span>
              {campaignDraft.status === 'saving' && (
                <span className="text-[11px] font-semibold text-slate-400">• Saving draft…</span>
              )}
              {campaignDraft.status === 'saved' && campaignDraft.savedAt && (
                <span className="text-[11px] font-semibold text-emerald-600">
                  • Draft saved {formatDraftTime(campaignDraft.savedAt)}
                </span>
              )}
              {campaignDraft.status === 'error' && (
                <span className="text-[11px] font-semibold text-rose-500">• Draft autosave failed</span>
              )}
              {campaignDraft.attribution && (
                <span className="text-[11px] font-semibold text-indigo-500">
                  • Shared draft by {draftAuthorLabel(campaignDraft.attribution)} · last edited by{' '}
                  {draftLastEditorLabel(campaignDraft.attribution)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {[
                { step: 1, label: '1. Audience', icon: Users },
                { step: 2, label: '2. Design', icon: Edit3 },
                { step: 3, label: '3. Preview & Test', icon: Eye },
                { step: 4, label: '4. Schedule & Send', icon: Send },
              ].map((s) => (
                <button
                  key={s.step}
                  type="button"
                  onClick={() => setWizardStep(s.step as any)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-extrabold transition-all cursor-pointer ${
                    wizardStep === s.step
                      ? 'bg-brand text-white shadow-sm'
                      : wizardStep > s.step
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}
                >
                  <s.icon className="h-3.5 w-3.5" />
                  <span>{s.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Resumed-draft notice (live-walk Finding 4): entering the
              wizard with a draft loaded must be unmistakable — the wizard
              always starts at Step 1 and says whose draft this is. */}
          {campaignDraft.restored && !resumeNoticeDismissed && (
            <div className="flex flex-wrap items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <p className="text-xs font-semibold text-amber-800">
                You&apos;re editing a resumed draft
                {campaignDraft.attribution
                  ? ` — started by ${draftAuthorLabel(campaignDraft.attribution)}, last edited by ${draftLastEditorLabel(campaignDraft.attribution)}`
                  : ''}
                {campaignDraft.savedAt ? ` at ${formatDraftTime(campaignDraft.savedAt)}` : ''}.
                Nothing has been sent yet.
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    await campaignDraft.discard();
                    resetCampaignForm();
                    void refreshTeamDrafts();
                    setShowSendForm(true);
                  }}
                  className="px-3 py-1.5 bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 text-xs font-bold rounded-lg transition-all cursor-pointer"
                >
                  Start fresh instead
                </button>
                <button
                  type="button"
                  onClick={() => setResumeNoticeDismissed(true)}
                  className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold rounded-lg transition-all cursor-pointer"
                >
                  Continue draft
                </button>
              </div>
            </div>
          )}

          {/* Step 1: Define Audience & Filters */}
          {wizardStep === 1 && (
            <div className="space-y-4">
              <div className="bg-blue-50/80 border border-blue-100 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <h6 className="text-xs font-bold text-brand uppercase tracking-wider">Target Audience Filter</h6>
                  <p className="text-sm font-extrabold text-slate-900 mt-0.5">
                    {filteredAudience.length} applicant{filteredAudience.length !== 1 ? 's' : ''} matched out of {batchMembers.length} cohort members
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowExcluderDrawer(!showExcluderDrawer)}
                  className="px-3 py-1.5 bg-white border border-blue-200 rounded-xl text-xs font-bold text-brand hover:bg-blue-50 transition cursor-pointer"
                >
                  {showExcluderDrawer ? 'Hide Recipient List' : `Review / Exclude Recipients (${excludedIds.length} excluded)`}
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Program</label>
                  <select
                    value={audienceProgramFilter}
                    onChange={(e) => setAudienceProgramFilter(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none text-slate-800 font-medium"
                  >
                    <option value="">All Programs</option>
                    {availablePrograms.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Status</label>
                  <select
                    value={audienceStatusFilter}
                    onChange={(e) => setAudienceStatusFilter(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none text-slate-800 font-medium"
                  >
                    <option value="">All Statuses</option>
                    {availableStatuses.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Country</label>
                  <select
                    value={audienceCountryFilter}
                    onChange={(e) => setAudienceCountryFilter(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none text-slate-800 font-medium"
                  >
                    <option value="">All Countries</option>
                    {availableCountries.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Search Keyword</label>
                  <input
                    type="search"
                    placeholder="Name, email, app ID..."
                    value={audienceSearch}
                    onChange={(e) => setAudienceSearch(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs outline-none text-slate-800 font-medium"
                  />
                </div>
              </div>

              {showExcluderDrawer && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2 max-h-64 overflow-y-auto">
                  <p className="text-xs font-bold text-slate-700">Toggle individual recipients on/off:</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {batchMembers.map((app) => {
                      const name = batchDisplayName(app) || app.userEmail || app.applicationId;
                      const isExcluded = excludedIds.includes(app.applicationId);
                      return (
                        <label key={app.applicationId} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded-lg cursor-pointer border border-slate-100">
                          <input
                            type="checkbox"
                            checked={!isExcluded}
                            onChange={() => {
                              setExcludedIds((prev) =>
                                isExcluded ? prev.filter((id) => id !== app.applicationId) : [...prev, app.applicationId]
                              );
                            }}
                            className="rounded border-slate-300 text-brand"
                          />
                          <div className="min-w-0 flex-1">
                            <p className={`text-xs font-semibold truncate ${isExcluded ? 'line-through text-slate-400' : 'text-slate-800'}`}>{name}</p>
                            <p className="text-[11px] text-slate-500 truncate">{app.userEmail}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setWizardStep(2)}
                  disabled={filteredAudience.length === 0}
                  className="px-5 py-2 btn-primary text-white text-xs font-bold rounded-xl flex items-center gap-1.5 disabled:opacity-40 cursor-pointer"
                >
                  <span>Next: Compose &amp; Design</span>
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Compose & Choose Template */}
          {wizardStep === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Sender Alias</label>
                  <SenderDropdown value={campSenderAddress} onChange={setCampSenderAddress} />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Template Options</label>
                  <button
                    type="button"
                    onClick={() => setShowTemplatePicker(true)}
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-emerald-700 hover:bg-emerald-50 flex items-center justify-center gap-1.5 transition cursor-pointer"
                  >
                    <FileText className="h-4 w-4" />
                    <span>Load Company Template</span>
                  </button>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-bold text-slate-700 uppercase">Subject Line *</label>
                  <MergeFieldPicker
                    categories={MERGE_FIELD_CATEGORIES}
                    onInsert={(tag) => {
                      const inp = document.getElementById('campSubjectInput') as HTMLInputElement | null;
                      if (inp) {
                        insertTextToTextarea(inp as any, tag, campSubject, setCampSubject);
                      } else {
                        setCampSubject((prev) => prev + tag);
                      }
                    }}
                    buttonLabel="Insert Variable"
                    align="right"
                  />
                </div>
                <input
                  id="campSubjectInput"
                  type="text"
                  value={campSubject}
                  onChange={(e) => setCampSubject(e.target.value)}
                  placeholder="e.g. Important updates regarding your {{program_name}} program"
                  className="w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs text-slate-900 font-medium outline-none focus:border-brand"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-bold text-slate-700 uppercase">Email Content *</label>
                  <span className="text-[11px] text-slate-500 font-medium">
                    {broadcastRecipients.length} recipient{broadcastRecipients.length === 1 ? '' : 's'} in audience
                  </span>
                </div>
                <ComposerBody
                  composer={composer}
                  minHeight={220}
                  placeholder="Write your campaign message here. Use {{first_name}} etc. for per-recipient fields."
                  fieldPickerSlot={
                    <MergeFieldPicker
                      categories={MERGE_FIELD_CATEGORIES}
                      onInsert={(tag) => composer.insertTextAtCursor(tag)}
                      buttonLabel="Insert Variable"
                      align="right"
                    />
                  }
                  toolbarExtraSlot={
                    <ChangeCaseDropdown onSelectFormat={(fmt) => applyCaseToSelection(fmt)} />
                  }
                />
                {composer.placeholders.length > 0 && (
                  <p className="text-caption text-slate-400 mt-1.5">
                    Placeholders in this message:{' '}
                    {composer.placeholders.map((ph) => (
                      <code key={ph} className="text-purple-700 bg-purple-50 px-1 rounded mr-1 font-mono">{ph}</code>
                    ))}
                  </p>
                )}
              </div>

              {/* Compose footer (Slice 4): documents to generate per
                  recipient + the campaign's static attachments. */}
              <div className="space-y-4">
                <DocumentTemplateMultiSelect
                  value={campDocumentTemplateIds}
                  onChange={setCampDocumentTemplateIds}
                  hint="Generated from each recipient's own application when the campaign is sent, saved to their Generated Documents, and attached to their email. If a document fails to generate for a recipient, that recipient's email is not sent."
                />
                {campAttachments.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <Paperclip className="h-4 w-4 text-indigo-600" />
                      <span className="text-caption uppercase font-extrabold text-slate-600">
                        Attachments ({campAttachments.length})
                      </span>
                    </div>
                    <p className="text-caption text-slate-400 mb-2">
                      Attached to every recipient's email, from the selected email template.
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {campAttachments.map((att, i) => (
                        <span
                          key={`${att.name}-${i}`}
                          className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold text-slate-700 max-w-full"
                        >
                          <span className="truncate max-w-[200px]">{att.name}</span>
                          <button
                            type="button"
                            aria-label={`Remove ${att.name}`}
                            onClick={() => setCampAttachments((prev) => prev.filter((_, j) => j !== i))}
                            className="text-slate-400 hover:text-rose-600 cursor-pointer p-0.5 shrink-0"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {showTemplatePicker && (
                <TemplatePickerModal
                  onSelect={(tmpl) => {
                    // Subject and documentTemplateIds are forwarded by the
                    // composer's onTemplateSubject / onTemplateDocumentTemplates hooks.
                    composer.insertTemplate({ name: tmpl.name, subject: tmpl.subject, htmlBody: tmpl.htmlBody, documentTemplateIds: tmpl.documentTemplateIds });
                    // Slice 4: the template's STATIC attachments ride along
                    // with the campaign (previously silently dropped by
                    // this picker) — they are sent to every recipient.
                    if (tmpl.attachments?.length) setCampAttachments(tmpl.attachments);
                    setShowTemplatePicker(false);
                  }}
                  onClose={() => setShowTemplatePicker(false)}
                />
              )}

              <div className="flex justify-between pt-2">
                <button
                  type="button"
                  onClick={() => setWizardStep(1)}
                  className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 cursor-pointer"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => setWizardStep(3)}
                  disabled={!campSubject.trim() || composer.isEmpty}
                  className="px-5 py-2 btn-primary text-white text-xs font-bold rounded-xl flex items-center gap-1.5 disabled:opacity-40 cursor-pointer"
                >
                  <span>Next: Preview &amp; Test</span>
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Live Interactive Preview & Test Dispatch */}
          {wizardStep === 3 && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50 p-3 rounded-xl border border-slate-200">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-700">Preview Recipient:</span>
                  <div className="w-64 max-w-full">
                    <SearchableSelect
                      value={String(previewAppIndex)}
                      onChange={(v) => setPreviewAppIndex(Number(v))}
                      options={filteredAudience.map((app, idx) => {
                        const name = batchDisplayName(app) || app.userEmail;
                        return {
                          value: String(idx),
                          label: app.applicationId ? `${name} (${app.applicationId})` : name,
                        };
                      })}
                      placeholder="Search recipients…"
                      allowClear={false}
                      emptyLabel="No recipients found"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1">
                  <button
                    type="button"
                    onClick={() => setPreviewDeviceFrame('desktop')}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition ${previewDeviceFrame === 'desktop' ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    Desktop
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewDeviceFrame('mobile')}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition ${previewDeviceFrame === 'mobile' ? 'bg-brand text-white' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    Mobile
                  </button>
                </div>
              </div>

              {/* Rendered Preview Container */}
              <div className="flex justify-center bg-slate-100 p-6 rounded-2xl border border-slate-200 min-h-[300px]">
                <div
                  className={`bg-white shadow-xl border border-slate-200 rounded-xl p-6 transition-all ${
                    previewDeviceFrame === 'mobile' ? 'w-[375px]' : 'w-full max-w-2xl'
                  }`}
                >
                  <div className="border-b border-slate-100 pb-3 mb-4 space-y-1">
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Subject</p>
                    <p className="text-sm font-extrabold text-slate-900">
                      {filteredAudience.length > 0
                        ? resolveMergeFields(campSubject, filteredAudience[previewAppIndex % filteredAudience.length])
                        : campSubject}
                    </p>
                  </div>
                  <div
                    className="prose prose-sm max-w-none text-xs text-slate-800 leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeEmailHtml(
                        filteredAudience.length > 0
                          ? resolveMergeFields(composer.getSendHtml(), filteredAudience[previewAppIndex % filteredAudience.length])
                          : composer.bodyHtml,
                      ),
                    }}
                  />
                </div>
              </div>

              {/* Instant Test Dispatch */}
              <div className="bg-purple-50/70 border border-purple-100 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h6 className="text-xs font-bold text-purple-900">Send Test Email First</h6>
                  <p className="text-[11px] text-purple-700">Dispatch a live copy to your inbox to check styling and formatting before broadcast.</p>
                  {campDocumentTemplateIds.length > 0 && (
                    <p className="text-[11px] text-purple-700 mt-0.5">
                      The {campDocumentTemplateIds.length} selected document{campDocumentTemplateIds.length === 1 ? '' : 's'} will be generated for the preview recipient and attached — preview only, not saved to their Generated Documents.
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="email"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    placeholder="Your admin email..."
                    className="bg-white border border-purple-200 rounded-xl px-3 py-1.5 text-xs text-slate-900 outline-none w-56 font-medium"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      if (!testEmailAddress) return;
                      setTestEmailSending(true);
                      setTestEmailStatus('');
                      try {
                        const app = filteredAudience[previewAppIndex % filteredAudience.length] || {};
                        const testSubject = `[TEST] ${resolveMergeFields(campSubject, app)}`;
                        const testBody = resolveMergeFields(composer.getSendHtml(), app);
                        // POST /api/email/send — the single-recipient contract.
                        // Slice 4 / fork F-2: the test send carries the
                        // campaign's attachments and document selection,
                        // generated for the PREVIEW applicant — but with
                        // previewDocumentGeneration the documents are NOT
                        // saved to that applicant's Generated Documents.
                        await sendIndividualEmail({
                          to: testEmailAddress,
                          subject: testSubject,
                          htmlBody: testBody,
                          applicationId: app?.applicationId || undefined,
                          applicantData: app || undefined,
                          sentBy: adminEmail,
                          senderAddress: campSenderAddress || undefined,
                          idempotencyKey: newIdempotencyKey(),
                          ...(campAttachments.length > 0 ? { attachments: campAttachments } : {}),
                          ...(campDocumentTemplateIds.length > 0
                            ? { documentTemplateIds: campDocumentTemplateIds, previewDocumentGeneration: true }
                            : {}),
                        });
                        setTestEmailStatus(
                          campDocumentTemplateIds.length > 0
                            ? `✅ Test sent to ${testEmailAddress} — documents generated for preview only (not saved to the applicant's Generated Documents)`
                            : `✅ Test sent to ${testEmailAddress}`,
                        );
                      } catch (err: any) {
                        setTestEmailStatus(`❌ Test failed: ${err.message}`);
                      } finally {
                        setTestEmailSending(false);
                      }
                    }}
                    disabled={testEmailSending}
                    className="px-4 py-1.5 bg-purple-700 hover:bg-purple-800 text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer disabled:opacity-40"
                  >
                    {testEmailSending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    <span>Send Test</span>
                  </button>
                </div>
              </div>
              {testEmailStatus && (
                <p className="text-xs font-semibold text-center text-purple-800">{testEmailStatus}</p>
              )}

              <div className="flex justify-between pt-2">
                <button
                  type="button"
                  onClick={() => setWizardStep(2)}
                  className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 cursor-pointer"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => setWizardStep(4)}
                  className="px-5 py-2 btn-primary text-white text-xs font-bold rounded-xl flex items-center gap-1.5 cursor-pointer"
                >
                  <span>Next: Review &amp; Dispatch</span>
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Review — Send Now vs Schedule */}
          {wizardStep === 4 && (
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-3">
                <h6 className="text-xs font-extrabold text-slate-900 uppercase tracking-wider">Pre-Flight Validation Checklist</h6>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <CheckCircle className={`h-4 w-4 ${filteredAudience.length > 0 ? 'text-emerald-600' : 'text-rose-500'}`} />
                    <span className="font-semibold text-slate-800">Target Audience:</span>
                    <span className="text-slate-600">{broadcastRecipients.length} recipient{broadcastRecipients.length !== 1 ? 's' : ''} ready</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className={`h-4 w-4 ${campSubject.trim() ? 'text-emerald-600' : 'text-rose-500'}`} />
                    <span className="font-semibold text-slate-800">Subject Line:</span>
                    <span className="text-slate-600 truncate max-w-md">{campSubject || '—'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className={`h-4 w-4 ${!composer.isEmpty ? 'text-emerald-600' : 'text-rose-500'}`} />
                    <span className="font-semibold text-slate-800">Email Content:</span>
                    <span className="text-slate-600">Non-empty body ({composer.bodyHtml.length} chars)</span>
                  </div>
                  {composer.placeholders.length > 0 && (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-purple-600" />
                      <span className="font-semibold text-slate-800">Placeholders:</span>
                      <span className="text-slate-600">{composer.placeholders.join(', ')}</span>
                    </div>
                  )}
                  {campDocumentTemplateIds.length > 0 && (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-indigo-600" />
                      <span className="font-semibold text-slate-800">Documents:</span>
                      <span className="text-slate-600">
                        {campDocumentTemplateIds.length} document template{campDocumentTemplateIds.length === 1 ? '' : 's'} generated per recipient from their own application
                      </span>
                    </div>
                  )}
                  {campAttachments.length > 0 && (
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-indigo-600" />
                      <span className="font-semibold text-slate-800">Attachments:</span>
                      <span className="text-slate-600">
                        {campAttachments.length} file{campAttachments.length === 1 ? '' : 's'} attached to every recipient's email
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Inline send-path validation errors (PR-11 §F) */}
              {campaignFormError && (
                <div
                  role="alert"
                  className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl px-4 py-3 text-xs font-semibold"
                >
                  <AlertCircle className="h-4 w-4 shrink-0 mt-px" />
                  <span>{campaignFormError}</span>
                </div>
              )}

              {/* Broadcast job progress (send-now result) */}
              {broadcastJob && (
                <BroadcastJobProgress
                  jobId={broadcastJob.jobId}
                  initialStatus={broadcastJob.status}
                  recipientCount={broadcastJob.recipientCount}
                  deduplicated={broadcastJob.deduplicated}
                />
              )}

              {/* Action Buttons — Send Now vs Schedule */}
              <div className="flex items-center justify-between pt-2 flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setWizardStep(3)}
                  className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50 cursor-pointer"
                >
                  Back
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={resetCampaignForm}
                    className="px-4 py-2.5 border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-bold rounded-xl cursor-pointer"
                  >
                    New Campaign
                  </button>
                  <button
                    type="button"
                    onClick={handleScheduleCampaign}
                    disabled={campSending || broadcastRecipients.length === 0 || !campSubject.trim() || composer.isEmpty}
                    className="px-6 py-2.5 border-2 border-blue-200 text-blue-600 hover:bg-blue-50 font-bold rounded-xl text-xs transition-all cursor-pointer disabled:opacity-40 flex items-center gap-1.5"
                  >
                    <CalendarDays className="h-4 w-4" />
                    <span>Schedule Campaign</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSendCampaign}
                    disabled={campSending || broadcastRecipients.length === 0}
                    className="px-6 py-2.5 btn-primary text-white text-xs font-bold rounded-xl shadow-md flex items-center gap-1.5 cursor-pointer disabled:opacity-40"
                  >
                    {campSending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    <span>Send Now ({broadcastRecipients.length} recipient{broadcastRecipients.length === 1 ? '' : 's'})</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Campaign list — search + paginated table */}
      {campaigns.length === 0 ? (
        <div className="py-10 text-center text-slate-400">
          <Mail className="h-8 w-8 mx-auto text-slate-300 mb-2" />
          <p className="text-caption font-bold">No batch email campaigns sent yet.</p>
        </div>
      ) : (
        <div>
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input type="search" value={campaignSearch} onChange={e => { setCampaignSearch(e.target.value); setCampaignPage(0); }}
              placeholder="Search campaigns by subject or sender..."
              className="w-full pl-9 pr-3 py-2 text-xs border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-primary/20  bg-slate-50" />
          </div>

          {(() => {
            // Filter + paginate
            const q = campaignSearch.toLowerCase().trim();
            const filtered = q
              ? campaigns.filter(c => c.title.toLowerCase().includes(q) || (c.sentBy || '').toLowerCase().includes(q))
              : campaigns;
            const totalPages = Math.ceil(filtered.length / CAMPAIGNS_PER_PAGE) || 1;
            const safePage = Math.min(campaignPage, totalPages - 1);
            const pageCampaigns = filtered.slice(safePage * CAMPAIGNS_PER_PAGE, (safePage + 1) * CAMPAIGNS_PER_PAGE);

            if (filtered.length === 0) {
              return <p className="text-xs text-slate-500 italic text-center py-6">No campaigns match your search.</p>;
            }

            return (<>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-100 text-slate-500 font-bold uppercase tracking-wider">
                    <td className="py-2.5 pr-3">Subject</td>
                    <td className="py-2.5 pr-3">Sent</td>
                    <td className="py-2.5 pr-3 text-right">Recipients</td>
                    <td className="py-2.5 pr-3">Delivery</td>
                    <td className="py-2.5 pr-3">Sent By</td>
                    <td className="py-2.5 pr-3"></td>
                  </tr>
                </thead>
                <tbody>
                  {pageCampaigns.map((camp, ci) => {
                    const ds = campaignDeliverySummary[camp.campaignId];
                    return (
                      <tr key={camp.campaignId || ci} className="border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => { setSelectedCampaign(camp); setShowDetail(true); setRecipientSearch(''); }}>
                        <td className="py-2.5 pr-3 font-bold text-slate-800 max-w-[240px] truncate" title={camp.title}>{camp.title}</td>
                        <td className="py-2.5 pr-3 text-slate-600 whitespace-nowrap">{camp.sentAt ? new Date(camp.sentAt).toLocaleString() : '—'}</td>
                        <td className="py-2.5 pr-3 text-right text-slate-700 font-bold">{camp.count}</td>
                        <td className="py-2.5 pr-3">
                          {ds ? (
                            <span className="text-caption font-medium">
                              {ds.delivered > 0 && <span className="text-emerald-600">{ds.delivered} delivered</span>}
                              {ds.bounced > 0 && <span className="text-red-600 ml-1">{ds.bounced} bounced</span>}
                              {ds.pending > 0 && <span className="text-amber-500 ml-1">{ds.pending} pending</span>}
                              {ds.delivered === 0 && ds.bounced === 0 && ds.pending === 0 && <span className="text-slate-500 italic">Not checked</span>}
                            </span>
                          ) : (
                            <span className="text-slate-500 italic">Not checked</span>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 text-slate-500">{camp.sentBy}</td>
                        <td className="py-2.5 pr-3 text-right whitespace-nowrap space-x-1.5">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleReuseCampaign(camp); }}
                            className="px-2.5 py-1 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-lg text-caption font-bold transition-all cursor-pointer"
                            title="Copy dynamic fields & reuse broadcast email"
                          >
                            Reuse
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedCampaign(camp); setShowDetail(true); setRecipientSearch(''); }}
                            className="px-2.5 py-1 btn-primary text-white rounded-lg text-caption font-bold transition-all cursor-pointer"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-3">
                <span className="text-caption text-slate-500 font-medium">
                  Showing {safePage * CAMPAIGNS_PER_PAGE + 1}–{Math.min((safePage + 1) * CAMPAIGNS_PER_PAGE, filtered.length)} of {filtered.length}
                </span>
                <div className="flex items-center space-x-1.5">
                  <button onClick={() => setCampaignPage(safePage - 1)} disabled={safePage === 0}
                    className="px-2.5 py-1 text-caption font-bold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-default transition-all cursor-pointer">
                    <ChevronLeft className="h-3.5 w-3.5 inline" /> Prev
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    const start = Math.max(0, Math.min(safePage - 2, totalPages - 5));
                    const pageNum = start + i;
                    if (pageNum >= totalPages) return null;
                    return (
                      <button key={pageNum} onClick={() => setCampaignPage(pageNum)}
                        className={`px-2.5 py-1 text-caption font-bold rounded-lg border transition-all cursor-pointer ${pageNum === safePage ? 'btn-primary text-white border-brand' : 'border-slate-200 text-slate-600 hover:bg-slate-100'}`}>
                        {pageNum + 1}
                      </button>
                    );
                  })}
                  <button onClick={() => setCampaignPage(safePage + 1)} disabled={safePage >= totalPages - 1}
                    className="px-2.5 py-1 text-caption font-bold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-default transition-all cursor-pointer">
                    Next <ChevronRight className="h-3.5 w-3.5 inline" />
                  </button>
                </div>
              </div>
            )}
            </>);
          })()}
        </div>
      )}

      {/* Campaign Detail Modal */}
      {showDetail && selectedCampaign && (
        <Modal onClose={() => setShowDetail(false)} title={selectedCampaign.title}>
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl mx-4 max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-slate-200 shrink-0">
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-extrabold text-slate-900 truncate" title={selectedCampaign.title}>{selectedCampaign.title}</h3>
                <p className="text-caption text-slate-400 mt-0.5">Sent {selectedCampaign.sentAt ? new Date(selectedCampaign.sentAt).toLocaleString() : '—'}</p>
              </div>
              <button onClick={() => setShowDetail(false)} className="text-slate-400 hover:text-slate-700 cursor-pointer p-1 ml-3 shrink-0"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-caption uppercase font-bold text-slate-500 block">From</span><span className="font-semibold text-slate-700">{selectedCampaign.fromName}</span></div>
                <div><span className="text-caption uppercase font-bold text-slate-500 block">Sent By</span><span className="font-semibold text-slate-700">{selectedCampaign.sentBy}</span></div>
              </div>
              {/* Recipients list */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-caption uppercase font-bold text-slate-500">Recipients ({selectedCampaign.count})</span>
                </div>
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-2 h-3 w-3 text-slate-400" />
                  <input type="search" placeholder="Search recipients..." className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-7 pr-2.5 py-1.5 text-xs outline-none  text-slate-700"
                    value={recipientSearch} onChange={e => setRecipientSearch(e.target.value)} />
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl max-h-40 overflow-y-auto">
                  {(() => {
                    const q = recipientSearch.toLowerCase();
                    const filtered = q
                      ? selectedCampaign.entries.filter((e: any) => (e.to || '').toLowerCase().includes(q))
                      : selectedCampaign.entries;
                    if (filtered.length === 0) {
                      return <p className="text-xs text-slate-500 italic text-center py-4">No recipients match your search.</p>;
                    }
                    return (
                      <table className="w-full text-xs">
                        <thead><tr className="border-b border-slate-200 text-slate-500 font-bold"><td className="px-3 py-2">Email</td><td className="px-3 py-2">Status</td><td className="pr-3 text-right">Delivery</td></tr></thead>
                        <tbody>
                          {filtered.map((entry: any, i: number) => {
                            const ds = entry.messageId ? deliveryCache[entry.messageId] : null;
                            return (
                              <tr key={entry.id || i} className="border-b border-slate-100 last:border-0 hover:bg-white transition-colors">
                                <td className="px-3 py-2 font-mono text-xs text-slate-700">{entry.to || entry.email || entry.applicationId || '—'}</td>
                                <td className="px-3 py-2 text-slate-500">{entry.status || '—'}</td>
                                <td className="pr-3 text-right py-2">
                                  {entry.messageId && ds
                                    ? <DeliveryStatusBadge status={ds.status} reason={ds.bounceReason} />
                                    : entry.messageId
                                      ? <span className="text-xs text-slate-500 italic">Pending</span>
                                      : <span className="text-xs text-slate-300">—</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </div>
              {/* Email body */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-caption uppercase font-bold text-slate-500">Email Content (Dynamic Template)</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleCopyCampaignContent(selectedCampaign)}
                      className="text-[10px] font-bold text-purple-600 hover:text-purple-800 hover:underline transition-all cursor-pointer flex items-center gap-1"
                      title="Copy raw dynamic template with {{tags}}"
                    >
                      <FileText className="h-3 w-3" /> Copy Dynamic Fields
                    </button>
                    <button
                      onClick={() => handleReuseCampaign(selectedCampaign)}
                      className="text-[10px] font-bold text-blue-600 hover:text-blue-800 hover:underline transition-all cursor-pointer flex items-center gap-1"
                      title="Reuse this broadcast email in composer"
                    >
                      <Send className="h-3 w-3" /> Reuse Broadcast
                    </button>
                  </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                  {selectedCampaign.htmlBody ? (
                    <iframe title="Email preview" srcDoc={selectedCampaign.htmlBody ? sanitizeEmailHtml(selectedCampaign.htmlBody) : selectedCampaign.htmlBody}
                      className="w-full" style={{ minHeight: 200, maxHeight: 400, border: 'none' }} sandbox="allow-same-origin" />
                  ) : (
                    <pre className="p-4 text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed max-h-60 overflow-y-auto">{selectedCampaign.textBody || '(No content)'}</pre>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Modal>
        )}

      {/* MASS EMAIL BROADCAST SAFETY CONFIRMATION MODAL */}
      {showBroadcastConfirmModal && (
        <Modal onClose={() => setShowBroadcastConfirmModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5 animate-fade-in text-xs font-sans">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2 text-rose-600">
                <AlertTriangle className="h-5 w-5" />
                <h3 className="font-extrabold text-slate-900 text-sm font-display">Confirm Mass Email Broadcast</h3>
              </div>
              <button type="button" onClick={() => setShowBroadcastConfirmModal(false)} className="p-1 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-900 font-medium leading-relaxed">
                You are about to send a mass email broadcast. Please verify the campaign summary before dispatching.
              </div>

              <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 space-y-2.5">
                <div className="flex justify-between border-b border-slate-200/60 pb-1.5">
                  <span className="text-slate-500 font-semibold">Total Recipients:</span>
                  <span className="font-extrabold font-mono text-blue-600">
                    {broadcastRecipients.length} Candidates Selected
                  </span>
                </div>
                <div className="flex justify-between border-b border-slate-200/60 pb-1.5">
                  <span className="text-slate-500 font-semibold">Exclusions:</span>
                  <span className="font-bold text-slate-700">{excludedIds.length} Excluded</span>
                </div>
                <div className="flex justify-between border-b border-slate-200/60 pb-1.5">
                  <span className="text-slate-500 font-semibold">Subject:</span>
                  <span className="font-bold text-slate-900 truncate max-w-[200px]">{campSubject}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500 font-semibold">Sender:</span>
                  <span className="font-bold text-slate-800">{campSenderAddress || 'Default Workspace Sender'}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={executeSendCampaign}
                disabled={campSending}
                className="flex-1 py-2.5 bg-rose-600 hover:bg-rose-700 disabled:bg-slate-300 text-white font-extrabold rounded-xl transition-all cursor-pointer shadow-md flex items-center justify-center gap-2"
              >
                {campSending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                <span>Confirm & Dispatch Broadcast</span>
              </button>
              <button
                type="button"
                onClick={() => setShowBroadcastConfirmModal(false)}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* SCHEDULE CAMPAIGN MODAL — broadcast mode: date/time + timezone,
          sendAt persisted as UTC ISO via wallClockToUtcISO */}
      {showScheduleModal && (
        <EmailScheduleModal
          emailData={{
            to: `${broadcastRecipients.length} recipient${broadcastRecipients.length === 1 ? '' : 's'}`,
            toName: 'Batch campaign',
            subject: campSubject.trim(),
            htmlBody: composer.getSendHtml(),
            senderAddress: campSenderAddress || undefined,
            // Shown in the modal's preview as "Documents to Generate".
            ...(campDocumentTemplateIds.length > 0 ? { documentTemplateIds: campDocumentTemplateIds } : {}),
          }}
          broadcast={{
            recipients: broadcastRecipients,
            batchId,
            sentBy: adminEmail,
            senderAddress: campSenderAddress || undefined,
            idempotencyKey,
            // Slice 4: attachments + the document selection ride the
            // schedule; documents generate per recipient at execution.
            ...(campAttachments.length > 0 ? { attachments: campAttachments } : {}),
            ...(campDocumentTemplateIds.length > 0 ? { documentTemplateIds: campDocumentTemplateIds } : {}),
          }}
          onClose={() => setShowScheduleModal(false)}
          onScheduled={() => {
            // Scheduled → a NEW compose gets a NEW key.
            setIdempotencyKey(newIdempotencyKey());
            notifyToast('success', 'Campaign scheduled.');
            resetCampaignForm();
          }}
        />
      )}


    </div>
  );
}


/* Bulk Charge / Quote Multi-Step Modal */
function BulkFinanceModal({
  isOpen,
  onClose,
  type,
  applicantIds,
}: {
  isOpen: boolean;
  onClose: () => void;
  type: "invoice" | "quote";
  applicantIds: string[];
}) {
  const [step, setStep] = React.useState<1 | 2 | 3>(1);
  const [templates, setTemplates] = React.useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = React.useState("");
  const [sendEmailPrompt, setSendEmailPrompt] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen) return;
    setStep(1);
    const fetchTemplates = async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await safeFetch("/api/admin/finance/invoice-templates", {
          headers: { Authorization: "Bearer " + token },
        });
        if (res.templates) setTemplates(res.templates);
      } catch (e) {
        console.error("Failed to fetch templates:", e);
      }
    };
    fetchTemplates();
  }, [isOpen]);

  if (!isOpen) return null;

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  const handleExecute = async () => {
    setSubmitting(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await safeFetch("/api/admin/batch/bulk-finance", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        body: JSON.stringify({
          type,
          applicantIds,
          templateId: selectedTemplateId,
          sendEmail: sendEmailPrompt,
        }),
      });

      if (res.success) {
        notifyToast.success(res.message || "Bulk action queued successfully!");
        window.dispatchEvent(new CustomEvent('bulk-job-created'));
        onClose();
      } else {
        notifyToast.error(res.error || "Failed to process bulk request");
      }
    } catch (e: any) {
      notifyToast.error(e.message || "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const isQuote = type === "quote";
  const docTitle = isQuote ? "Quote" : "Invoice";

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl p-6 w-full max-w-2xl space-y-6 text-xs text-slate-800 my-auto">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <div className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-purple-600" />
            <h3 className="text-sm font-black text-slate-900">Bulk {docTitle} Dispatch Wizard</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition cursor-pointer">
            <X className="h-4 w-4" />
          </button>
        </div>
        {/* Wizard Stepper Header */}
        <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-2xl p-3.5 shadow-2xs">
          <div className={`flex items-center gap-2 font-bold px-3 py-1.5 rounded-xl transition-all ${step === 1 ? 'bg-purple-600 text-white shadow-xs' : 'bg-white border border-slate-200 text-slate-600'}`}>
            <span className="w-5 h-5 rounded-full bg-black/20 flex items-center justify-center text-[10px]">1</span>
            <span>Select Template</span>
          </div>
          <div className="h-0.5 flex-1 bg-slate-200 mx-2" />
          <div className={`flex items-center gap-2 font-bold px-3 py-1.5 rounded-xl transition-all ${step === 2 ? 'bg-purple-600 text-white shadow-xs' : 'bg-white border border-slate-200 text-slate-600'}`}>
            <span className="w-5 h-5 rounded-full bg-black/20 flex items-center justify-center text-[10px]">2</span>
            <span>Confirm Cohort ({applicantIds.length})</span>
          </div>
          <div className="h-0.5 flex-1 bg-slate-200 mx-2" />
          <div className={`flex items-center gap-2 font-bold px-3 py-1.5 rounded-xl transition-all ${step === 3 ? 'bg-purple-600 text-white shadow-xs' : 'bg-white border border-slate-200 text-slate-600'}`}>
            <span className="w-5 h-5 rounded-full bg-black/20 flex items-center justify-center text-[10px]">3</span>
            <span>Email & Execute</span>
          </div>
        </div>

        {/* Step 1: Select Template */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="bg-purple-50/50 border border-purple-100 rounded-2xl p-4 flex items-start gap-3">
              <FileText className="h-5 w-5 text-purple-600 shrink-0 mt-0.5" />
              <div>
                <h5 className="font-extrabold text-purple-950 text-xs">Choose a Finance Template</h5>
                <p className="text-slate-500 font-medium text-[11px] mt-0.5">
                  Select an itemized line-item template to apply across all {applicantIds.length} candidate(s).
                </p>
              </div>
            </div>

            <div>
              <label className="text-[10px] font-black uppercase text-slate-500 block mb-1.5 tracking-wider">Available Templates</label>
              <SearchableSelect
                value={selectedTemplateId}
                onChange={(v) => setSelectedTemplateId(v)}
                options={templates.map(t => ({ value: t.id, label: t.name || t.title }))}
                placeholder="Search templates…"
                clearLabel="-- Select Template --"
                emptyLabel="No templates found"
              />
            </div>

            {selectedTemplate && (
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-2">
                <div className="flex items-center justify-between font-black text-slate-800 border-b border-slate-200 pb-2">
                  <span>Template Summary: {selectedTemplate.name || selectedTemplate.title}</span>
                  <span className="text-purple-700 text-sm">${selectedTemplate.totalAmount || selectedTemplate.total || 0}</span>
                </div>
                {Array.isArray(selectedTemplate.lineItems) && selectedTemplate.lineItems.length > 0 && (
                  <div className="space-y-1 pt-1">
                    {selectedTemplate.lineItems.map((item: any, idx: number) => (
                      <div key={idx} className="flex items-center justify-between text-slate-600 text-[11px] font-medium">
                        <span>• {item.description || item.title || 'Item'} (x{item.quantity || 1})</span>
                        <span className="font-bold text-slate-800">${item.amount || item.unitPrice || 0}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end pt-3 border-t border-slate-100">
              <button
                type="button"
                disabled={!selectedTemplateId}
                onClick={() => setStep(2)}
                className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-extrabold rounded-xl shadow-sm disabled:opacity-40 transition cursor-pointer flex items-center gap-1.5"
              >
                <span>Next: Confirm Candidates</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Confirm Cohort */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 p-4 rounded-2xl space-y-2">
              <div className="flex items-center gap-2 text-slate-900 font-black text-xs">
                <Users className="h-4 w-4 text-purple-600" />
                <span>Selected Candidate Recipients ({applicantIds.length})</span>
              </div>
              <p className="text-slate-600 font-medium text-[11px] leading-relaxed">
                You are about to generate customized {docTitle.toLowerCase()}s for {applicantIds.length} candidate(s). Each document will be assigned a unique {docTitle.toLowerCase()} number and recorded under the candidate's account.
              </p>
            </div>

            {selectedTemplate && (
              <div className="border border-slate-200 rounded-2xl p-4 bg-white space-y-1">
                <span className="text-[10px] uppercase font-bold text-slate-400 block tracking-wider">Applied Template</span>
                <span className="font-extrabold text-slate-900 text-xs block">{selectedTemplate.name || selectedTemplate.title}</span>
              </div>
            )}

            <div className="flex justify-between pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 font-bold rounded-xl text-slate-700 transition cursor-pointer"
              >
                &larr; Back
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-extrabold rounded-xl shadow-sm transition cursor-pointer flex items-center gap-1.5"
              >
                <span>Next: Email Options</span>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Dispatch & Notification Settings */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="bg-purple-50/60 border border-purple-200 p-4 rounded-2xl space-y-3">
              <div className="flex items-center gap-2 text-purple-950 font-black text-xs">
                <Mail className="h-4 w-4 text-purple-700" />
                <span>Email Notification & Attachment Settings</span>
              </div>
              <p className="text-slate-600 font-medium text-[11px] leading-relaxed">
                Choose whether to immediately email all {applicantIds.length} candidate(s) with their attached unique {docTitle.toLowerCase()} and interactive portal link.
              </p>

              <div className="space-y-2 pt-1">
                <label
                  onClick={() => setSendEmailPrompt(true)}
                  className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition ${sendEmailPrompt ? 'bg-white border-purple-500 shadow-xs' : 'bg-slate-50/50 border-slate-200 hover:bg-white'}`}
                >
                  <div className="flex items-center gap-2.5">
                    <input type="radio" name="sendEmail" checked={sendEmailPrompt} onChange={() => setSendEmailPrompt(true)} className="h-4 w-4 text-purple-600" />
                    <div>
                      <span className="font-extrabold text-slate-900 block text-xs">Send Email Notifications Immediately</span>
                      <span className="text-[11px] text-slate-500 font-medium block">Dispatches emails with attached {docTitle} details to each applicant.</span>
                    </div>
                  </div>
                </label>

                <label
                  onClick={() => setSendEmailPrompt(false)}
                  className={`flex items-center justify-between p-3 rounded-xl border cursor-pointer transition ${!sendEmailPrompt ? 'bg-white border-purple-500 shadow-xs' : 'bg-slate-50/50 border-slate-200 hover:bg-white'}`}
                >
                  <div className="flex items-center gap-2.5">
                    <input type="radio" name="sendEmail" checked={!sendEmailPrompt} onChange={() => setSendEmailPrompt(false)} className="h-4 w-4 text-purple-600" />
                    <div>
                      <span className="font-extrabold text-slate-900 block text-xs">Generate Records as Drafts Only</span>
                      <span className="text-[11px] text-slate-500 font-medium block">Creates {docTitle.toLowerCase()} records in the system without sending emails.</span>
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div className="flex justify-between pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 font-bold rounded-xl text-slate-700 transition cursor-pointer"
              >
                &larr; Back
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={handleExecute}
                className="px-6 py-2.5 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white font-black rounded-xl shadow-md transition cursor-pointer flex items-center gap-2 disabled:opacity-50"
              >
                {submitting ? <Loader className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                <span>{submitting ? "Processing..." : `Confirm & Execute Bulk ${docTitle} Generation`}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
